import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid
} from 'recharts';
import BacktestButton, { checkBotBacktestable, UnsupportedAlert } from '../../components/backtestButton';

import botApiV2 from '../../services/botapi';
import AIStrategyChat from '../../components/AIStrategyChat';
import StrategyTemplates from '../../components/strategyTemplates';
import StrategyBuilder, { SPOT_TEMPLATES, templateToConditions } from '../../components/conditionBuilder';
import PositionSizeSection from '../../components/PositionSizeSection';
import AITradingAdvisor from '../../components/aiTradingAdvisor';


import {
  StockStatusBadgeRow,
  BuyingPowerChip,
} from '../../components/StockStatusBadges';
import { HedgePositionPanel } from '../../components/HedgePositionPanel';
import { PendingOrderProgress } from '../../components/PendingOrderProgress';
import { useAlpacaAccountStatus, getAccountStatusForBot } from '../../hooks/useAlpacaAccountStatus';

// Phase B
import { AssetClassChip, useAssetClass } from '../../components/AssetClassChip';
import { ShortabilityBadge } from '../../components/ShortabilityBadge';
import { ExtendedHoursToggle } from '../../components/ExtendedHoursToggle';
import { ShortEligibilityWarning } from '../../components/ShortEligibilityWarning';

import { useShortability } from '../../hooks/useShortability';

import {
  buildLogicFromConditions,
  extractIndicatorsFromConditions,
  parseLogicToConditions
} from '../../components/strategyUtils';

import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
  Typography,
  Box,
  Avatar,
  Stack,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  InputAdornment,
  Snackbar, Alert

} from '@mui/material';
import {
  AddCircleOutline,
  Bolt,
  RemoveCircleOutline,
  ExpandMore,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Settings,
  Delete,
  Edit,
  PlayArrow,
  Pause,
  Visibility,
  VisibilityOff,
  Functions,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Schedule,
  AttachMoney,
  ShowChart,
  History,
  BarChart,
  Timeline,
  DateRange,
  Cancel,
  CheckCircle,
  RadioButtonUnchecked,
  WarningAmber,
  Search,
  ContentCopy,
  Email
} from '@mui/icons-material';
import exchangeApiService from '../../services/exchangeApiService';
import tradingBotService from '../../services/tradingBotService';
import backtestService from '../../services/backtestService'; // Add this at top


import Toolbar from '../../components/Toolbar/Toolbar';
import Logout from '../../containers/Login/Logout';

import { format } from 'date-fns';
import { io } from "socket.io-client";
import Autocomplete from '@mui/material/Autocomplete';
import symbolService from '../../services/symbolService';
import binance from '../../logos/binance.svg';




const socket = io(process.env.REACT_APP_SOCKET_URL || "http://localhost:3005", {
  transports: ["websocket"],     // 👈 match the server
});

// ── AutoTrader matte/steel-blue theme (shared design system w/ Portfolio) ──
const AT_SANS = '"IBM Plex Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const AT_MONO = '"IBM Plex Mono", ui-monospace, "SF Mono", Menlo, monospace';
const AT = {
  bg: '#0a0c11', card: '#0f1218', inner: '#0b0e15', tile: '#070a0f',
  bright: '#f2f6ff', text: '#e8edf7', body: '#9aa6bd', muted: '#6f7c96', faint: '#5e6a82',
  accent: '#4d8fc7', accent2: '#3f6fb0', positive: '#46b88a', negative: '#d97b72', amber: '#d9a441',
  hairline: 'rgba(255,255,255,.06)',
};
const AT_THEME = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: AT.accent },
    success: { main: AT.positive },
    error: { main: AT.negative },
    warning: { main: AT.amber },
    info: { main: AT.accent },
    background: { default: AT.bg, paper: AT.card },
    divider: AT.hairline,
  },
  shape: { borderRadius: 12 },
  typography: { fontFamily: AT_SANS },
  components: {
    // Make every MUI field/select in the wizard match the handoff's matte inputs.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#0b0e15',
          borderRadius: 9,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.1)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.2)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4d8fc7', borderWidth: 1 },
        },
        input: { color: '#f2f6ff' },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#7b88a2', '&.Mui-focused': { color: '#4d8fc7' } } } },
    MuiPaper: { styleOverrides: { outlined: { borderColor: 'rgba(255,255,255,.06)' } } },
    MuiSlider: { styleOverrides: { root: { color: '#4d8fc7' } } },
    MuiStepConnector: { styleOverrides: { line: { borderColor: 'rgba(255,255,255,.12)' } } },
  },
});

// Mono numeric/label span used across the restyled AutoTrader surfaces.
const Mono = (props) => <Box component="span" {...props} sx={{ fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', ...(props.sx || {}) }} />;

// Create-bot glass-cockpit right rail (handoff §8a). The single source of truth
// for the wizard — identity, illustrative estimate, readiness gates (clickable),
// logic-at-a-glance and plain-language risk — all derived from wizard state.
function RailRow({ k, v, c }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
      <Typography sx={{ fontSize: '12px', color: AT.muted }}>{k}</Typography>
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: c || AT.text, fontFamily: AT_MONO }}>{v}</Typography>
    </Box>
  );
}

function StrategyRail({ readiness, newBot, buyConditions, sellConditions, profitStop, lossStop, exchangeLabel, onJump }) {
  const amount = Number(newBot.positionValue || newBot.tradeAmount) || 0;
  const tp = Number(profitStop) || 0, sl = Number(lossStop) || 0;
  const riskAmt = amount * sl / 100, rewardAmt = amount * tp / 100;
  const rr = sl > 0 ? tp / sl : 0;
  const band = rr >= 2 ? { label: 'Favorable', c: AT.positive } : rr >= 1 ? { label: 'Balanced', c: AT.accent } : { label: 'Aggressive', c: AT.negative };
  const orderLabel = { market: 'Market', limit: 'Limit', aggressive_limit: 'Smart Limit' }[newBot.orderExecution] || 'Market';
  const signalLabel = newBot.signalSource === 'tradingview' ? 'Webhook' : 'Internal';
  const sizeUnit = newBot.sizeMode === 'base' ? (newBot.symbol || '').split('/')[0] : (newBot.funding_asset || 'USDT');
  const sizeLabel = amount > 0 ? `${amount.toLocaleString()} ${sizeUnit}` : '—';

  const Section = ({ label, children, mb = 2.25 }) => (
    <Box sx={{ mb }}>
      <Mono sx={{ fontSize: '9.5px', letterSpacing: '.12em', color: AT.muted2, display: 'block', mb: '8px' }}>{label}</Mono>
      {children}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: AT.card, border: `1px solid ${AT.hairline}`, borderRadius: '14px', p: '18px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '4px' }}>
        <Mono sx={{ fontSize: '9.5px', letterSpacing: '.14em', color: AT.accentText }}>STRATEGY</Mono>
        <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '9px', fontWeight: 700, letterSpacing: '.08em', color: AT.amber, background: 'rgba(217,164,65,.12)', border: '1px solid rgba(217,164,65,.34)', px: '7px', py: '2px', borderRadius: '5px' }}>PAPER</Box>
      </Box>
      <Typography sx={{ fontSize: '17px', fontWeight: 700, color: AT.bright, mb: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{newBot.name?.trim() || 'Untitled bot'}</Typography>
      <Typography sx={{ fontSize: '12px', color: AT.body, mb: '16px' }}>{newBot.symbol || '—'} · {signalLabel}</Typography>

      <Section label={`READINESS · ${readiness.doneCount}/${readiness.total}`}>
        <Box sx={{ height: 6, borderRadius: '4px', background: 'rgba(255,255,255,.06)', overflow: 'hidden', mb: '10px' }}>
          <Box sx={{ width: `${(readiness.doneCount / readiness.total) * 100}%`, height: '100%', background: `linear-gradient(90deg,${AT.accent2},${AT.accent})`, transition: 'width .3s' }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {readiness.gates.map((g) => (
            <Box key={g.key} onClick={() => onJump && onJump(g.step)} sx={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', borderRadius: '6px', px: '4px', py: '3px', '&:hover': { background: 'rgba(255,255,255,.03)' } }}>
              {g.done ? <CheckCircle sx={{ fontSize: 15, color: AT.positive }} /> : <RadioButtonUnchecked sx={{ fontSize: 15, color: AT.muted }} />}
              <Typography sx={{ fontSize: '12px', color: g.done ? AT.muted : AT.text, textDecoration: g.done ? 'line-through' : 'none' }}>{g.label}</Typography>
            </Box>
          ))}
        </Box>
      </Section>

      <Section label="LOGIC AT A GLANCE">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <RailRow k="Exchange" v={exchangeLabel || '—'} />
          <RailRow k="Order type" v={orderLabel} />
          <RailRow k="Size" v={sizeLabel} />
          <RailRow k="Entry rules" v={String(buyConditions.length)} c={AT.positive} />
          <RailRow k="Exit rules" v={String(sellConditions.length)} c={AT.negative} />
        </Box>
      </Section>

      <Section label="RISK PROFILE" mb={0}>
        <Typography sx={{ fontSize: '12.5px', color: AT.text, mb: '10px' }}>
          {amount > 0
            ? <>Risk <Box component="b" sx={{ color: AT.negative }}>{atShortUsd(riskAmt)}</Box> to make <Box component="b" sx={{ color: AT.positive }}>{atShortUsd(rewardAmt)}</Box> per trade</>
            : 'Set a position size to see per-trade risk'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '11px', color: AT.positive, background: 'rgba(70,184,138,.12)', border: '1px solid rgba(70,184,138,.32)', px: '8px', py: '3px', borderRadius: '6px' }}>TP {tp}%</Box>
          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '11px', color: AT.negative, background: 'rgba(217,123,114,.12)', border: '1px solid rgba(217,123,114,.32)', px: '8px', py: '3px', borderRadius: '6px' }}>SL {sl}%</Box>
          <Box component="span" sx={{ ml: 'auto', fontFamily: AT_MONO, fontSize: '11px', fontWeight: 700, color: band.c }}>{rr ? rr.toFixed(2) : '—'} R:R · {band.label}</Box>
        </Box>
      </Section>
    </Box>
  );
}

// ── Per-bot "Total Value" equity chart (handoff §6b) ──
const atShortUsd = (n) => {
  const a = Math.abs(Number(n) || 0), sgn = n < 0 ? '-' : '';
  return a >= 1000 ? `${sgn}$${(a / 1000).toFixed(a >= 100000 ? 0 : 1)}K` : `${sgn}$${Math.round(a)}`;
};
function atBuildPath(values) {
  const w = 600, h = 150, pad = 10;
  const vals = values.length >= 2 ? values : [values[0] ?? 0, values[0] ?? 0];
  const min = Math.min(...vals), max = Math.max(...vals), rng = (max - min) || 1, n = vals.length;
  const flat = max === min;
  const xs = (i) => (i / (n - 1)) * w;
  const ys = (v) => (flat ? h / 2 : h - ((v - min) / rng) * (h - pad * 2) - pad);
  let d = `M${xs(0).toFixed(1)} ${ys(vals[0]).toFixed(1)}`;
  for (let i = 1; i < n; i++) {
    const x0 = xs(i - 1), y0 = ys(vals[i - 1]), x1 = xs(i), y1 = ys(vals[i]), xc = (x0 + x1) / 2;
    d += ` C${xc.toFixed(1)} ${y0.toFixed(1)} ${xc.toFixed(1)} ${y1.toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`;
  }
  return { line: d, area: `${d} L${w} ${h} L0 ${h} Z`, lastX: xs(n - 1).toFixed(1), lastY: ys(vals[n - 1]).toFixed(1), firstY: ys(vals[0]).toFixed(1), min, max };
}
// Path on a SHARED y-scale (so strategy + benchmark lines align). Backtest §7d.
function atPathScaled(values, min, max, w = 600, h = 150, pad = 8) {
  const vals = values.length >= 2 ? values : [values[0] ?? 0, values[0] ?? 0];
  const rng = (max - min) || 1, n = vals.length;
  const xs = (i) => (i / (n - 1)) * w;
  const ys = (v) => (max === min ? h / 2 : h - ((v - min) / rng) * (h - pad * 2) - pad);
  let d = `M${xs(0).toFixed(1)} ${ys(vals[0]).toFixed(1)}`;
  for (let i = 1; i < n; i++) {
    const x0 = xs(i - 1), y0 = ys(vals[i - 1]), x1 = xs(i), y1 = ys(vals[i]), xc = (x0 + x1) / 2;
    d += ` C${xc.toFixed(1)} ${y0.toFixed(1)} ${xc.toFixed(1)} ${y1.toFixed(1)} ${x1.toFixed(1)} ${y1.toFixed(1)}`;
  }
  return { line: d, area: `${d} L${w} ${h} L0 ${h} Z`, lastX: xs(n - 1).toFixed(1), lastY: ys(vals[n - 1]).toFixed(1) };
}

function BotEquityChart({ initial, equity }) {
  const uid = React.useId();
  const end = Number(equity);
  if (!Number.isFinite(end)) return null;
  const startRaw = Number(initial);
  const haveStart = Number.isFinite(startRaw) && startRaw > 0;
  const start = haveStart ? startRaw : end;
  const pct = haveStart && start !== 0 ? ((end - start) / start) * 100 : 0;
  const up = pct >= 0;
  // Smooth start→current curve (real per-bot time series not yet stored).
  const n = 24, values = [];
  for (let i = 0; i < n; i++) { const t = i / (n - 1); values.push(start + (end - start) * t); }
  const p = atBuildPath(values);
  const color = up ? AT.accent : AT.negative;
  return (
    <Box sx={{ mt: 3, p: 2.5, border: '1px solid', borderColor: AT.hairline, borderRadius: 2, bgcolor: AT.inner }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: AT.tile, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShowChart sx={{ color: AT.accent, fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: AT.bright }}>Total Value</Typography>
            <Typography sx={{ fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: 18, fontWeight: 600, color: AT.bright }}>{atShortUsd(end)}</Typography>
          </Box>
        </Box>
        <Typography sx={{ fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 600, color: up ? AT.positive : AT.negative }}>
          {(pct >= 0 ? '+' : '') + pct.toFixed(2)}% since start
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 150, py: '4px', minWidth: 42, textAlign: 'right', fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: '10px', color: AT.faint }}>
          <span>{atShortUsd(p.max)}</span><span>{atShortUsd((p.max + p.min) / 2)}</span><span>{atShortUsd(p.min)}</span>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, color }}>
          <svg viewBox="0 0 600 150" preserveAspectRatio="none" style={{ width: '100%', height: 150, display: 'block' }}>
            <defs><linearGradient id={`bot-area-${uid}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="currentColor" stopOpacity="0.22" /><stop offset="1" stopColor="currentColor" stopOpacity="0" /></linearGradient></defs>
            <g stroke="rgba(255,255,255,.05)" strokeWidth="1"><line x1="0" y1="10" x2="600" y2="10" /><line x1="0" y1="75" x2="600" y2="75" /><line x1="0" y1="140" x2="600" y2="140" /></g>
            <line x1="0" y1={p.firstY} x2="600" y2={p.firstY} stroke="rgba(255,255,255,.18)" strokeWidth="1" strokeDasharray="4 5" />
            <path d={p.area} fill={`url(#bot-area-${uid})`} />
            <path d={p.line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={p.lastX} cy={p.lastY} r="3.5" fill="currentColor" />
          </svg>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '8px', fontFamily: AT_MONO, fontSize: '10px', color: AT.faint }}>
            <span>24h ago</span><span>18h</span><span>12h</span><span>6h</span><span>now</span>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---- trade normalization helpers (entry/exit rendering) ----
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};
const toMs = (t) => {
  if (t == null) return undefined;
  const n = Number(t);
  if (Number.isFinite(n)) return n < 1e12 ? n * 1000 : n;
  const d = Date.parse(t);
  return Number.isFinite(d) ? d : undefined;
};

const isNum = (v) => Number.isFinite(Number(v));

const fmtPct = (v, d = 2) =>
  isNum(v) ? `${Number(v).toFixed(d)}%` : '—';

const fmtMoney = (v, d = 2) => {
  if (!isNum(v)) return '—';
  const n = Number(v);
  const s = n < 0 ? '-' : '';
  return `${s}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
};

const fmtRatio = (v, d = 2) => {
  if (v === Infinity) return '∞';
  return isNum(v) ? Number(v).toFixed(d) : '—';
};

// Convert raw user_history rows ({ date, buy_sell_history }) into the
// { _ts, timestamp, text } entries the bot history table consumes. `_ts` is a
// sortable epoch so older entries paged in later merge into the right order.
const HISTORY_PAGE_SIZE = 50;

function mapHistoryRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((entry) => {
    let text = entry.buy_sell_history;
    try { text = JSON.parse(text); } catch (_) {}
    const ms = new Date(entry.date).getTime();
    return {
      _ts: Number.isFinite(ms) ? ms : 0,
      timestamp: new Date(entry.date).toLocaleString(),
      text,
    };
  });
}

// Merge new history entries into an existing per-bot array: dedup by the
// composite key and keep oldest-first order (the table reverses for display).
function historyKeyOf(e) {
  const t = (e && e.text) || {};
  return `${e.timestamp}|${t.side}|${t.price}|${t.quantity}|${t.symbol}`;
}
function mergeHistoryEntries(existing = [], incoming = []) {
  const byKey = new Map();
  for (const e of existing) byKey.set(historyKeyOf(e), e);
  for (const e of incoming) { const k = historyKeyOf(e); if (!byKey.has(k)) byKey.set(k, e); }
  return Array.from(byKey.values()).sort((a, b) => (a._ts || 0) - (b._ts || 0));
}

function computeStatsFromHistory(historyEntries = []) {
  if (!Array.isArray(historyEntries) || historyEntries.length === 0) return null;

  const sorted = historyEntries
    .map(e => ({ ts: new Date(e.timestamp).getTime(), t: e.text || {} }))
    .filter(e =>
      Number.isFinite(e.ts) &&
      e.t &&
      e.t.side &&
      e.t.type !== 'order_pending' &&
      e.t.type !== 'order_cancelled' &&
      e.t.type !== 'order_expired' &&
      e.t.type !== 'reconciliation'
    )
    .sort((a, b) => a.ts - b.ts);

  const buyQueue = []; // FIFO: { qty, costPerUnit, feePerUnit }
  let trades = 0, wins = 0, losses = 0;
  let grossProfit = 0, grossLoss = 0;
  let openQty = 0, openCost = 0;

  for (const { t } of sorted) {
    const side = String(t.side).toUpperCase();
    const qty = Number(t.quantity) || 0;
    const cost = Number(t.cost) || 0;
    const proceeds = Number(t.proceeds) || 0;
    const fee = Number(t.fee) || 0;
    if (qty <= 0) continue;

    if (side === 'BUY') {
      buyQueue.push({
        qty,
        costPerUnit: cost / qty,
        feePerUnit: fee / qty,
      });
      openQty += qty;
      openCost += cost;
    } else if (side === 'SELL') {
      let remaining = qty;
      // `proceeds` in buy_sell_history is proceedsAfterFees — ALREADY net of the
      // sell-side fee (see tradeExecutors.computeProceeds / historyPayload). So we
      // must NOT subtract the sell `fee` again here; the original `pnl -= fee`
      // double-counted it. The buy-side `cost`/costPerUnit is gross notional, so
      // the buy-side feePerUnit IS still subtracted to get a true net P&L.
      const proceedsPerUnit = proceeds / qty;
      let pnl = 0;
      let matchedQty = 0;

      while (remaining > 1e-12 && buyQueue.length > 0) {
        const head = buyQueue[0];
        const m = Math.min(remaining, head.qty);
        // Net proceeds per unit minus gross cost minus the prorated buy-side fee.
        pnl += m * (proceedsPerUnit - head.costPerUnit - head.feePerUnit);
        head.qty -= m;
        remaining -= m;
        openQty -= m;
        openCost -= m * head.costPerUnit;
        matchedQty += m;
        if (head.qty <= 1e-12) buyQueue.shift();
      }

      // Only count the trade for the portion that actually closed a position;
      // an unmatched sell (no open long) isn't charged a fee or counted.
      if (matchedQty > 1e-12) {
        trades += 1;
        if (pnl >= 0) { wins += 1; grossProfit += pnl; }
        else { losses += 1; grossLoss += Math.abs(pnl); }
      }
    }
  }

  return {
    Trades: trades,
    Wins: wins,
    Losses: losses,
    WinRatePct: trades > 0 ? (wins / trades) * 100 : 0,
    GrossProfit: grossProfit,
    GrossLoss: grossLoss,
    ProfitFactor: grossLoss > 0
      ? grossProfit / grossLoss
      : (grossProfit > 0 ? Infinity : 0),
    RealizedPnL: grossProfit - grossLoss,
    OpenPositionQty: openQty,
    OpenPositionCost: openCost,
  };
}

const getEmail = () => (localStorage.getItem('email') || '').trim() || null;


const API_BASE_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:3005"; // matches your socket base
const WEBHOOK_BASE_URL = "https://trade.cryptologics-ai.com"; // ✅ Force production webhook domain

const TV_WEBHOOK_PATH = "/api/webhooks/tradingview";

// botId can be "BOT_ID" for template usage in UI
const buildTradingViewWebhookUrl = (botId, secret) => {
  const s = (secret || "").trim();
  const qs = new URLSearchParams();
  qs.set("bot_id", String(botId));
  if (s) qs.set("secret", s);
  return `${WEBHOOK_BASE_URL}${TV_WEBHOOK_PATH}?${qs.toString()}`;
};




function FuturesBadge({ bot }) {
  // Parse trade_info to get futures config
  const ti = React.useMemo(() => {
    try { return JSON.parse(bot.trade_info || '{}'); }
    catch { return {}; }
  }, [bot.trade_info]);

  if (ti.marketType !== 'futures') return null;

  const f = ti.futures || {};
  return (
    <>
      <Chip
        size="small"
        label={`Futures ${f.leverage || 1}x`}
        color="warning"
        variant="outlined"
        sx={{ height: 22, fontWeight: 600 }}
      />
      <Chip
        size="small"
        label={f.marginMode === 'isolated' ? 'Isolated' : 'Cross'}
        variant="outlined"
        sx={{ height: 22 }}
      />
      {f.positionSide === 'hedge' && (
        <Chip
          size="small"
          label="Hedge"
          variant="outlined"
          color="info"
          sx={{ height: 22 }}
        />
      )}
    </>
  );
}

function OrderTypeBadge({ bot }) {
  const ti = React.useMemo(() => {
    try { return JSON.parse(bot.trade_info || '{}'); }
    catch { return {}; }
  }, [bot.trade_info]);

  const oe = ti.orderExecution || {};
  const mode = oe.mode || 'market';

  if (mode === 'market') return null; // Don't show badge for default

  const config = ORDER_EXECUTION_MODES[mode] || {};
  return (
    <Tooltip title={config.description || ''}>
      <Chip
        size="small"
        label={config.label || mode}
        variant="outlined"
        sx={{ height: 22 }}
      />
    </Tooltip>
  );
}


function validateFuturesConfig(bot, userExchanges) {
  const errors = [];
  if (bot.marketType !== 'futures') return errors;

  const exchange = userExchanges.find(ex => ex.id === bot.exchangeId);
  if (!exchangeSupportsFutures(exchange)) {
    errors.push(`${exchange?.exchange || 'This exchange'} does not support futures trading.`);
  }

  const lev = Number(bot.leverage);
  if (!Number.isFinite(lev) || lev < 1 || lev > 125) {
    errors.push('Leverage must be between 1x and 125x.');
  }

  if (!MARGIN_MODES.includes(bot.marginMode)) {
    errors.push('Invalid margin mode. Choose "cross" or "isolated".');
  }

  return errors;
}


function FuturesInfoPanel({ bot, futuresData }) {
  const ti = React.useMemo(() => {
    try { return JSON.parse(bot.trade_info || '{}'); } catch { return {}; }
  }, [bot.trade_info]);

  if (ti.marketType !== 'futures') return null;

  const d = futuresData || {};
  const f = ti.futures || {};

  const markIndexSpread = (d.markPrice && d.indexPrice && d.indexPrice !== 0)
    ? ((d.markPrice - d.indexPrice) / d.indexPrice * 100)
    : null;

  const fundingAnnualized = d.lastFundingRate != null
    ? (d.lastFundingRate * 3 * 365 * 100) // 3 funding periods/day × 365
    : null;

  // Estimated liq distance (simplified)
  const leverage = Number(f.leverage) || 1;
  const liqDistancePct = leverage > 0 ? (100 / leverage) : null;

  const fmtRate = (v, digits = 4) => {
    if (v == null || !Number.isFinite(v)) return '—';
    return `${(v * 100).toFixed(digits)}%`;
  };

  const fmtPrice = (v) => {
    if (v == null || !Number.isFinite(v)) return '—';
    return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtOI = (v) => {
    if (v == null || !Number.isFinite(v)) return '—';
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return v.toLocaleString();
  };

  const fundingColor = d.lastFundingRate == null
    ? '#9aa6bd'
    : d.lastFundingRate > 0 ? '#46b88a' : d.lastFundingRate < 0 ? '#d97b72' : '#9aa6bd';

  const spreadColor = markIndexSpread == null
    ? '#9aa6bd'
    : Math.abs(markIndexSpread) > 0.05 ? '#d9a441' : '#46b88a';

  const items = [
    {
      label: 'Funding Rate',
      value: fmtRate(d.lastFundingRate),
      sub: fundingAnnualized != null ? `${fundingAnnualized.toFixed(1)}% ann.` : null,
      color: fundingColor,
      tooltip: d.lastFundingRate > 0
        ? 'Longs pay shorts'
        : d.lastFundingRate < 0
          ? 'Shorts pay longs'
          : null,
    },
    {
      label: 'Next Funding',
      value: d.nextFundingTime
        ? new Date(d.nextFundingTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '—',
      sub: d.nextFundingTime
        ? `in ${Math.max(0, Math.round((d.nextFundingTime - Date.now()) / 60_000))} min`
        : null,
    },
    {
      label: 'Mark Price',
      value: fmtPrice(d.markPrice),
    },
    {
      label: 'Index Price',
      value: fmtPrice(d.indexPrice),
    },
    {
      label: 'Mark/Index Spread',
      value: markIndexSpread != null ? `${markIndexSpread.toFixed(4)}%` : '—',
      color: spreadColor,
      tooltip: markIndexSpread != null
        ? (markIndexSpread > 0 ? 'Futures premium (contango)' : 'Futures discount (backwardation)')
        : null,
    },
    {
      label: 'Open Interest',
      value: fmtOI(d.openInterest),
    },
    {
      label: 'Leverage',
      value: `${leverage}x`,
      color: leverage > 20 ? '#d97b72' : leverage > 5 ? '#d9a441' : '#46b88a',
    },
    {
      label: 'Est. Liq Distance',
      value: liqDistancePct != null ? `~${liqDistancePct.toFixed(1)}%` : '—',
      color: liqDistancePct != null && liqDistancePct < 5 ? '#d97b72' : '#d9a441',
      tooltip: 'Approximate % move to liquidation (isolated, no extra margin)',
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        p: 2.5,
        border: '1px solid',
        borderColor: 'rgba(255,255,255,0.06)',
        borderRadius: 2,
        bgcolor: '#0f1218',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1.5,
              bgcolor: '#070a0f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TrendingUp sx={{ color: '#d9a441', fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>
            Futures Market Data
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {d.lastFundingRate != null && (
            <Chip
              size="small"
              label={d.lastFundingRate > 0 ? 'Longs Pay' : d.lastFundingRate < 0 ? 'Shorts Pay' : 'Neutral'}
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: d.lastFundingRate > 0 ? '#052e16' : d.lastFundingRate < 0 ? '#2a0a0a' : '#0f1218',
                color: fundingColor,
                border: '1px solid',
                borderColor: fundingColor,
              }}
            />
          )}
          {d.fetchedAt && (
            <Chip
              size="small"
              variant="outlined"
              label={`Updated ${new Date(d.fetchedAt).toLocaleTimeString()}`}
              sx={{ height: 22, fontSize: '0.65rem', color: '#9aa6bd', borderColor: 'rgba(255,255,255,0.1)' }}
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={1.5}>
        {items.map((item, idx) => (
          <Grid item xs={6} sm={3} key={idx}>
            <Tooltip title={item.tooltip || ''} arrow disableHoverListener={!item.tooltip}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#070a0f',
                  border: '1px solid',
                  borderColor: 'rgba(255,255,255,0.06)',
                  minHeight: 72,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease',
                  cursor: item.tooltip ? 'help' : 'default',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#9aa6bd',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.6rem',
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: item.color || '#f2f6ff',
                    fontFamily: '"SF Mono", Monaco, Consolas, monospace',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  {item.value}
                </Typography>
                {item.sub && (
                  <Typography variant="caption" sx={{ color: '#6f7c96', fontSize: '0.6rem' }}>
                    {item.sub}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}


function SymbolFeeDisplay({ symbol, exchangeId, userExchanges, symbolsByExchange, orderExecutionMode }) {
  // Resolve symbol metadata from the cache
  const meta = useMemo(() => {
    if (!symbol || !exchangeId) return null;
    const ex = userExchanges.find(e => e.id === exchangeId);
    const cacheKey = ex?.exchange || ex?.name || exchangeId;
    const list = symbolsByExchange?.[cacheKey] || [];
    return list.find(s => String(s.symbol).toUpperCase() === String(symbol).toUpperCase()) || null;
  }, [symbol, exchangeId, userExchanges, symbolsByExchange]);

  // Derive exchange identity for accurate copy + attribution.
  // Bug fix: the panel previously hardcoded "Binance" everywhere, so Alpaca
  // stocks (which legitimately set is_zero_fee=true for commission-free trading)
  // rendered "Binance has waived fees" — wrong brand, confusing UX.
  const exchangeMeta = useMemo(() => {
    const ex = userExchanges.find(e => e.id === exchangeId);
    const raw = String(ex?.exchange || '').toLowerCase();
    const isAlpaca = raw.startsWith('alpaca');
    const isBinance = raw.startsWith('binance');
    const displayName = ex?.name || ex?.exchange || 'this exchange';
    return {
      isAlpaca,
      isBinance,
      brand: isAlpaca ? 'Alpaca' : (isBinance ? 'Binance' : displayName),
      feeWord: isAlpaca ? 'commissions' : 'fees',
    };
  }, [exchangeId, userExchanges]);

  if (!meta) return null;

  const makerFee = meta.default_maker_fee;
  const takerFee = meta.default_taker_fee;
  const isZero = meta.is_zero_fee;
  const hasFees = makerFee != null && takerFee != null;

  if (!hasFees && !isZero) {
    // No fee data — don't show anything (avoid misleading empty state)
    return null;
  }

  // Predict effective fee based on order type
  // - market / aggressive_limit (fallback) → taker fee
  // - limit (post_only) → maker fee
  // - aggressive_limit → uncertain (likely taker)
  const effectiveFee = (() => {
    if (isZero) return 0;
    if (orderExecutionMode === 'limit') return makerFee;
    return takerFee; // market and aggressive_limit lean taker
  })();

  const pct = (v) => (v == null ? '—' : `${(v * 100).toFixed(3)}%`);

  if (isZero) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mt: 1.5,
          bgcolor: '#052e16',
          borderColor: '#46b88a',
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CheckCircle sx={{ color: '#46b88a', fontSize: 20 }} />
        <Box sx={{ flex: 1 }}>
<Typography variant="body2" fontWeight={600} sx={{ color: '#46b88a' }}>
            {exchangeMeta.isAlpaca ? 'Commission-Free Trading' : 'Zero Fee Trading'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#a7f3d0' }}>
            {exchangeMeta.isAlpaca
              ? `${exchangeMeta.brand} offers commission-free trading on ${symbol}. Your bot pays $0 in commissions per trade. (Regulatory and SEC fees may still apply on sells.)`
              : `${exchangeMeta.brand} has waived maker and taker ${exchangeMeta.feeWord} on ${symbol}. Your bot pays $0 in ${exchangeMeta.feeWord} per trade.`
            }
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mt: 1.5,
        borderRadius: 1.5,
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AttachMoney sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            Trading Fees
          </Typography>
        </Box>
<Tooltip title={exchangeMeta.isAlpaca
          ? 'Source: Alpaca trading account. Stocks are commission-free; regulatory fees may apply.'
          : `Source: ${exchangeMeta.brand} VIP-tier service account. Updated every 5 minutes.`
        }>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            via {exchangeMeta.brand}
          </Typography>
        </Tooltip>
      </Box>

      <Grid container spacing={1.5}>
        <Grid item xs={4}>
          <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Maker
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
              {pct(makerFee)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              Limit orders
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Taker
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
              {pct(takerFee)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              Market orders
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: '#0f1218',
              border: '1px solid',
              borderColor: '#3f6fb0',
            }}
          >
            <Typography variant="caption" sx={{ color: '#4d8fc7', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Your Fee
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', color: '#4d8fc7' }}>
              {pct(effectiveFee)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9aa6bd', fontSize: '0.6rem' }}>
              {orderExecutionMode === 'limit' ? 'Maker rate' : 'Taker rate'}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}

// ============================================================================
// 4. UI COMPONENT — FuturesConfig (add inside Step 1, after Signal Source)
// ============================================================================

// 🔧 CHANGE: Add this as a new <Grid item xs={12}> block in your Step 1 (activeStep === 0),
// AFTER the Signal Source section and BEFORE the "Order Size In" select.

function FuturesConfigSection({ newBot, setNewBot, userExchanges }) {
  const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
  const futuresAvailable = exchangeSupportsFutures(selectedExchange);
  const isFutures = newBot.marketType === 'futures';

  return (
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 2,
          borderColor: isFutures ? 'warning.main' : 'divider',
          transition: 'border-color 0.2s',
        }}
      >
        {/* Market Type Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccountBalance color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Market Type
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: isFutures ? 3 : 0 }}>
          {/* Spot card */}
          <Paper
            variant="outlined"
            onClick={() => setNewBot(prev => ({ ...prev, marketType: 'spot', leverage: 1 }))}
            sx={{
              flex: 1, p: 2, cursor: 'pointer',
              borderColor: !isFutures ? 'primary.main' : 'divider',
              borderWidth: !isFutures ? 2 : 1,
              bgcolor: !isFutures ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.light' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccountBalance color={!isFutures ? 'primary' : 'action'} fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>Spot</Typography>
              {!isFutures && <Chip size="small" label="Selected" color="primary" sx={{ ml: 'auto' }} />}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Buy and sell assets directly. No leverage, no liquidation risk.
            </Typography>
          </Paper>

          {/* Futures card */}
          <Paper
            variant="outlined"
            onClick={() => {
              if (!futuresAvailable && newBot.exchangeId) return; // disabled
              setNewBot(prev => ({ ...prev, marketType: 'futures' }));
            }}
            sx={{
              flex: 1, p: 2,
              cursor: futuresAvailable || !newBot.exchangeId ? 'pointer' : 'not-allowed',
              opacity: !futuresAvailable && newBot.exchangeId ? 0.5 : 1,
              borderColor: isFutures ? 'warning.main' : 'divider',
              borderWidth: isFutures ? 2 : 1,
              bgcolor: isFutures ? 'action.selected' : 'background.paper',
              transition: 'all 0.2s',
              '&:hover': futuresAvailable ? { borderColor: 'warning.light' } : {},
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TrendingUp color={isFutures ? 'warning' : 'action'} fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>Futures</Typography>
              {isFutures && <Chip size="small" label="Selected" color="warning" sx={{ ml: 'auto' }} />}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Trade with leverage. Supports long & short positions.
            </Typography>
            {!futuresAvailable && newBot.exchangeId && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                Not available for this exchange
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Futures Settings (conditional) */}
        {isFutures && (
          <>
            {/* Warning banner */}
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              <Typography variant="body2">
                <strong>Futures trading carries significant risk.</strong> You can lose more
                than your initial margin. Make sure you understand leverage and liquidation
                before proceeding.
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              {/* Futures Type */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Contract Type</InputLabel>
                  <Select
                    value={newBot.futuresType}
                    onChange={e => setNewBot(prev => ({ ...prev, futuresType: e.target.value }))}
                    label="Contract Type"
                  >
                    <MenuItem value="usdt_m">USDT-M (Linear)</MenuItem>
                    <MenuItem value="coin_m">COIN-M (Inverse)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Margin Mode */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Margin Mode</InputLabel>
                  <Select
                    value={newBot.marginMode}
                    onChange={e => setNewBot(prev => ({ ...prev, marginMode: e.target.value }))}
                    label="Margin Mode"
                  >
                    <MenuItem value="cross">
                      <Box>
                        <Typography variant="body2">Cross</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Shares margin across positions
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="isolated">
                      <Box>
                        <Typography variant="body2">Isolated</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Margin locked per position
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Position Mode */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Position Mode</InputLabel>
                  <Select
                    value={newBot.positionSide}
                    onChange={e => setNewBot(prev => ({ ...prev, positionSide: e.target.value }))}
                    label="Position Mode"
                  >
                    <MenuItem value="one_way">One-Way (Net)</MenuItem>
                    <MenuItem value="hedge">Hedge (Long + Short)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Leverage */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Leverage: <strong>{newBot.leverage}x</strong>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Slider
                    value={newBot.leverage}
                    onChange={(_, v) => setNewBot(prev => ({ ...prev, leverage: v }))}
                    min={1}
                    max={125}
                    step={1}
                    marks={LEVERAGE_PRESETS.map(v => ({ value: v, label: `${v}x` }))}
                    valueLabelDisplay="auto"
                    valueLabelFormat={v => `${v}x`}
                    sx={{
                      flex: 1,
                      '& .MuiSlider-markLabel': { fontSize: '0.7rem' },
                      color: newBot.leverage > 20 ? 'error.main'
                        : newBot.leverage > 5 ? 'warning.main'
                          : 'primary.main',
                    }}
                  />
                  <TextField
                    value={newBot.leverage}
                    onChange={e => {
                      const v = Math.max(1, Math.min(125, parseInt(e.target.value) || 1));
                      setNewBot(prev => ({ ...prev, leverage: v }));
                    }}
                    type="number"
                    inputProps={{ min: 1, max: 125, step: 1 }}
                    sx={{ width: 90 }}
                    size="small"
                  />
                </Box>

                {/* Leverage risk indicator */}
                {newBot.leverage > 10 && (
                  <Alert
                    severity={newBot.leverage > 50 ? 'error' : 'warning'}
                    sx={{ mt: 1 }}
                  >
                    <Typography variant="caption">
                      {newBot.leverage > 50
                        ? `${newBot.leverage}x leverage is extremely risky. A ${(100 / newBot.leverage).toFixed(1)}% move against you = liquidation.`
                        : `${newBot.leverage}x leverage. A ${(100 / newBot.leverage).toFixed(1)}% adverse move = liquidation.`
                      }
                    </Typography>
                  </Alert>
                )}
              </Grid>

              {/* Reduce Only toggle (futures-specific) */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
                    border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    cursor: 'pointer',
                  }}
                  onClick={() => setNewBot(prev => ({ ...prev, reduceOnly: !prev.reduceOnly }))}
                >
                  <Chip
                    label={newBot.reduceOnly ? 'ON' : 'OFF'}
                    size="small"
                    color={newBot.reduceOnly ? 'info' : 'default'}
                    variant={newBot.reduceOnly ? 'filled' : 'outlined'}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Reduce Only</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Orders can only reduce existing position, never increase
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
    </Grid>
  );
}


// ============================================================================
// 5. UI COMPONENT — OrderExecutionSection (Option B)
//    Add inside Step 1, AFTER the FuturesConfig and BEFORE "Order Size In"
// ============================================================================

function OrderExecutionSection({ newBot, setNewBot }) {
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const mode = newBot.orderExecution || 'market';

  return (
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Order Execution
            </Typography>
          </Box>
          <Chip
            label={ORDER_EXECUTION_MODES[mode]?.label || 'Market'}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        {/* Default: Market order, shown as the recommended choice */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: showAdvanced ? 2 : 0 }}>
          {Object.entries(ORDER_EXECUTION_MODES).map(([key, config]) => (
            <Paper
              key={key}
              variant="outlined"
              onClick={() => setNewBot(prev => ({ ...prev, orderExecution: key }))}
              sx={{
                flex: 1, p: 1.5, cursor: 'pointer',
                borderColor: mode === key ? 'primary.main' : 'divider',
                borderWidth: mode === key ? 2 : 1,
                bgcolor: mode === key ? 'action.selected' : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'primary.light' },
                position: 'relative',
              }}
            >
              {key === 'market' && (
                <Chip
                  label="Recommended"
                  size="small"
                  color="success"
                  sx={{
                    position: 'absolute', top: -10, right: 8,
                    fontSize: '0.6rem', height: 20,
                  }}
                />
              )}
              <Typography variant="body1" sx={{ mb: 0.5 }}>{config.icon}</Typography>
              <Typography variant="subtitle2" fontWeight={600}>{config.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {config.description}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Advanced toggle — Option B pattern */}
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            startIcon={<Settings fontSize="small" />}
            onClick={() => setShowAdvanced(!showAdvanced)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Order Settings
          </Button>

          <Collapse in={showAdvanced}>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Grid container spacing={2}>
                {/* Limit offset — only relevant for limit/aggressive_limit */}
                {(mode === 'limit' || mode === 'aggressive_limit') && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Limit Offset (%)"
                      type="number"
                      value={newBot.limitOffsetPct}
                      onChange={e => setNewBot(prev => ({
                        ...prev,
                        limitOffsetPct: Math.max(0, Number(e.target.value) || 0)
                      }))}
                      inputProps={{ step: 0.01, min: 0, max: 5 }}
                      fullWidth
                      size="small"
                      helperText="How far above/below market price to place limit. 0 = exact market price."
                    />
                  </Grid>
                )}

                {/* Timeout — only for aggressive_limit */}
                {mode === 'aggressive_limit' && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Fallback Timeout (sec)"
                      type="number"
                      value={newBot.limitTimeoutSec}
                      onChange={e => setNewBot(prev => ({
                        ...prev,
                        limitTimeoutSec: Math.max(1, parseInt(e.target.value) || 10)
                      }))}
                      inputProps={{ step: 1, min: 1, max: 120 }}
                      fullWidth
                      size="small"
                      helperText="Seconds to wait before converting unfilled limit to market order."
                    />
                  </Grid>
                )}

                {/* Post-only — for limit modes */}
                {(mode === 'limit') && (
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
                        border: '1px solid', borderColor: 'divider', borderRadius: 1,
                        cursor: 'pointer', height: '100%',
                      }}
                      onClick={() => setNewBot(prev => ({ ...prev, postOnly: !prev.postOnly }))}
                    >
                      <Chip
                        label={newBot.postOnly ? 'ON' : 'OFF'}
                        size="small"
                        color={newBot.postOnly ? 'info' : 'default'}
                        variant={newBot.postOnly ? 'filled' : 'outlined'}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>Post Only</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Maker fees only. Rejects if would fill immediately.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Explanation of what each mode does on the backend */}
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  {mode === 'market' && 'Market orders execute instantly at the best available price. Fees are typically 0.1% (taker). Best for volatile conditions where fill speed matters most.'}
                  {mode === 'limit' && 'Limit orders are placed at current price ± offset. You pay lower maker fees (0.02-0.04%), but the order may not fill if the price moves away.'}
                  {mode === 'aggressive_limit' && `A limit order is placed at market price ± ${newBot.limitOffsetPct}%. If not filled within ${newBot.limitTimeoutSec}s, it auto-converts to a market order. Best balance of fee savings and fill guarantee.`}
                </Typography>
              </Alert>
            </Box>
          </Collapse>
        </Box>
      </Paper>
    </Grid>
  );
}

// ✅ Module-level — defined OUTSIDE TradingBotDashboard
function ExchangeBadge({ type, label }) {
  const text = String(label || type || '—');
  return (
    <Chip
      size="small"
      variant="outlined"
      color="default"
      label={text}
      sx={{ height: 22 }}
    />
  );
}


/**
 * Accepts:
 *  A) closed-trade rows: { entryPrice, exitPrice, entryDate/Time, exitDate/Time, amount, pnl/pnlPct, ... }
 *  B) leg rows:          { side:'buy'|'sell', time, price, qty, pnl? }
 * Returns an array of display objects with entry+exit when available.
 */
function normalizeAndPairTrades(raw = []) {
  if (!Array.isArray(raw)) return [];

  // If any row already looks like a closed trade, just normalize and return.
  const hasClosed = raw.some(t =>
    t.entryPrice != null || t.exitPrice != null || t.entryDate || t.exitDate || t.exitTime || t.closeTime
  );
  if (hasClosed) {
    return raw.map(t => {
      const entryPrice = toNum(t.entryPrice ?? t.price);
      const exitPrice = toNum(t.exitPrice ?? t.closePrice);
      const pnlPct = toNum(String(t.pnlPct ?? t.profit ?? '').toString().replace('%', ''));
      const pnl = toNum(t.pnl ?? t.profitValue);
      return {
        entryTime: toMs(t.entryDate || t.entryTime || t.time),
        exitTime: toMs(t.exitDate || t.exitTime || t.closeTime),
        entryPrice, exitPrice,
        amount: toNum(t.amount ?? t.qty),
        pnl: pnl ?? (Number.isFinite(entryPrice) && Number.isFinite(exitPrice) && t.amount != null
          ? (exitPrice - entryPrice) * Number(t.amount) : undefined),
        pnlPct: pnlPct ?? (Number.isFinite(entryPrice) && Number.isFinite(exitPrice)
          ? ((exitPrice - entryPrice) / entryPrice) * 100 : undefined),
        side: (t.type || t.side || 'BUY').toUpperCase(),
        raw: t,
      };
    });
  }

  // Otherwise we have BUY/SELL legs. Pair FIFO with partial-lot handling so a
  // sell that closes only part of a lot — or spans several lots — is measured
  // on the quantity actually sold, not on the whole buy lot.
  const buys = []; // FIFO lots: { time, price, qty (remaining), feePerUnit, leg }
  const trips = [];
  for (const leg of raw) {
    const side = String(leg.side || leg.type || '').toUpperCase();
    const time = toMs(leg.time);
    const price = toNum(leg.price);
    const qty = toNum(leg.qty ?? leg.amount);

    if (side === 'BUY') {
      const q = Number.isFinite(qty) ? qty : 0;
      const fee = toNum(leg.fee) || 0;
      buys.push({ time, price, qty: q, feePerUnit: q > 0 ? fee / q : 0, leg });
    } else if (side === 'SELL') {
      let remaining = Number.isFinite(qty) ? qty : 0;
      const sellFeePerUnit = remaining > 0 ? (toNum(leg.fee) || 0) / remaining : 0;

      // No open long to close → surface the sell instead of silently dropping it.
      if (remaining <= 1e-12 || !buys.length) {
        trips.push({
          exitTime: time, exitPrice: price, amount: qty,
          side: 'SELL (unmatched)', raw: { sell: leg },
        });
        continue;
      }

      let matchedQty = 0, costBasis = 0, buyFee = 0, entryTime = null;
      const matchedLots = [];
      while (remaining > 1e-12 && buys.length) {
        const head = buys[0];
        const m = Math.min(remaining, head.qty);
        if (entryTime == null) entryTime = head.time;
        matchedQty += m;
        costBasis += m * head.price;
        buyFee += m * head.feePerUnit;
        matchedLots.push(head.leg);
        head.qty -= m;
        remaining -= m;
        if (head.qty <= 1e-12) buys.shift();
      }

      const avgEntry = matchedQty > 0 ? costBasis / matchedQty : undefined;
      // Prefer an engine-supplied pnl; otherwise compute on the matched qty,
      // net of the prorated buy- and sell-side fees (mirrors the live calc).
      const computedPnl = Number.isFinite(avgEntry)
        ? (price - avgEntry) * matchedQty - buyFee - sellFeePerUnit * matchedQty
        : undefined;
      const pnlPct = Number.isFinite(avgEntry) && avgEntry !== 0
        ? ((price - avgEntry) / avgEntry) * 100
        : undefined;

      trips.push({
        entryTime, exitTime: time,
        entryPrice: avgEntry, exitPrice: price,
        amount: matchedQty,
        pnl: toNum(leg.pnl) ?? computedPnl,
        pnlPct,
        side: 'BUY→SELL',
        raw: { buys: matchedLots, sell: leg },
      });

      // Sell qty beyond the available buys = unmatched remainder.
      if (remaining > 1e-12) {
        trips.push({
          exitTime: time, exitPrice: price, amount: remaining,
          side: 'SELL (unmatched)', raw: { sell: leg },
        });
      }
    }
  }
  // Unpaired buys (still open)
  for (const o of buys) {
    if (o.qty > 1e-12) {
      trips.push({
        entryTime: o.time, entryPrice: o.price, amount: o.qty,
        side: 'BUY (open)', raw: { buy: o.leg },
      });
    }
  }
  return trips;
}


const MS_DAY = 24 * 60 * 60 * 1000;

const endOfTodayMs = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
};
const monthsAgoMs = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const yearsAgoMs = (n) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const isTickBasedBot = (bot) => {

  console.log('bot_check', bot)
  if (!bot) return false;
  if ((bot.interval || '').toLowerCase() === 'tick') return true;
  const s = `${bot.logic?.buy || ''}${bot.logic?.sell || ''}`.toLowerCase();
  return s.includes('tick');
};
const computeBacktestBounds = (bot) => {
  const max = endOfTodayMs();                          // always up to today
  const min = isTickBasedBot(bot) ? monthsAgoMs(2)     // tick: last 2 months
    : yearsAgoMs(2);     // non-tick: last 2 years
  return { min, max };
};
const defaultBacktestRange = () => [monthsAgoMs(1), endOfTodayMs()]; // last 1 month




// Mock data and utilities
const initialBots = [];

// "BTC/USDT" or "BTCUSDT" → ["BTC","USDT"]
const splitPair = (sym) => {
  const S = String(sym || '').toUpperCase();
  if (!S) return ['—', '—'];
  if (S.includes('/')) return S.split('/');
  // Try common crypto quote suffixes first (longest first)
  const quotes = ['USDT', 'USDC', 'BUSD', 'TUSD', 'USD', 'DAI', 'BTC', 'ETH'];
  quotes.sort((a, b) => b.length - a.length);
  for (const q of quotes) {
    if (S.endsWith(q) && S.length > q.length) {
      return [S.slice(0, -q.length), q];
    }
  }
  // Bare ticker (stocks): default quote to USD
  return [S, 'USD'];
};

// Prefer symbol metadata (base_asset/quote_asset) from symbolsByExchange cache.
// Fallback to old heuristic splitPair if not found.
function getPairMeta({ symbol, exchangeId, userExchanges, symbolsByExchange }) {
  const sym = String(symbol || '').toUpperCase();
  if (!sym) return { base: '—', quote: '—', raw: null };

  // Find cacheKey the same way you do elsewhere
  const selectedExchange = (userExchanges || []).find(ex => ex.id === exchangeId);
  const cacheKey = selectedExchange?.exchange || selectedExchange?.name || exchangeId;

  const list = symbolsByExchange?.[cacheKey] || [];
  const found = list.find(s => String(s.symbol || '').toUpperCase() === sym);

  if (found?.base_asset && found?.quote_asset) {
    return {
      base: String(found.base_asset).toUpperCase(),
      quote: String(found.quote_asset).toUpperCase(),
      raw: found,
    };
  }

  // fallback (your old behavior)
  const [BASE, QUOTE] = splitPair(sym);
  return { base: BASE, quote: QUOTE, raw: null };
}

function buildOrderSizing(symbol, sizeMode, positionValue, baseAsset, quoteAsset) {
  const BASE = String(baseAsset || '').toUpperCase();
  const QUOTE = String(quoteAsset || '').toUpperCase();

  // final fallback if caller didn’t pass assets
  const [fbBase, fbQuote] = splitPair(symbol);
  const base = BASE || fbBase;
  const quote = QUOTE || fbQuote;

  const assetSymbol = sizeMode === 'base' ? base : quote;

  return {
    mode: sizeMode,                       // 'quote' | 'base'
    value: Number(positionValue) || 0,
    assetType: sizeMode,
    assetSymbol,
  };
}


const EX_LOGOS = { binance };

function getAccountMeta(ex) {
  // Three account types:
  //   'live'  → real money on a real exchange
  //   'paper' → real exchange API, paper-trading account (Alpaca paper, Binance testnet)
  //   'sim'   → Cryptologics-internal simulator (no upstream API)
  //
  // The DB stores this in `account_type` (preferred) and `simulated` (legacy).
  // `simulated: true` is now exclusively the internal sim — Alpaca paper accounts
  // have `simulated: false` and `account_type: 'paper'`.
  const acctType = String(ex.account_type || ex.accountType || '').toLowerCase();
  const isInternalSim = ex.simulated === true || ex.isSimulated === true || acctType === 'sim';
  const isPaper = !isInternalSim && acctType === 'paper';
  const isLive = !isInternalSim && !isPaper;

  if (isInternalSim) {
    return {
      isSim: true,
      isPaper: false,
      isLive: false,
      chipLabel: 'Simulated',
      chipColor: 'info',
      subtitle: 'Cryptologics test account',
    };
  }
  if (isPaper) {
    // E.g. Alpaca paper trading: real API, paper money
    const brand = String(ex.exchange || '').toLowerCase().startsWith('alpaca')
      ? 'Alpaca Paper'
      : 'Paper';
    return {
      isSim: false,
      isPaper: true,
      isLive: false,
      chipLabel: brand,
      chipColor: 'warning',
      subtitle: 'Paper trading at exchange',
    };
  }
  return {
    isSim: false,
    isPaper: false,
    isLive: true,
    chipLabel: 'Live',
    chipColor: 'success',
    subtitle: 'Real Account',
  };
}

// --- ExchangeSelect (drop-in) ---
function ExchangeSelect({ exchanges, value, onChange, label = 'Exchange', disabled }) {
  return (
    <Autocomplete
      options={exchanges}
      value={value || null}                  // <= object or null
      onChange={(_, v) => onChange(v)}       // <= pass object (or null)
      getOptionLabel={(o) => (o?.name || o?.exchange || '')}
      isOptionEqualToValue={(o, v) => o.id === v?.id}
      disableClearable={false}
      disabled={disabled}
      blurOnSelect
      openOnFocus={false}
      disablePortal
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password',    // <= tame browser autofill
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.25, py: 1 }}>
          <Avatar src={EX_LOGOS[(option.exchangeType || option.exchange || '').toLowerCase()]} sx={{ width: 28, height: 28 }}>
            {(option.exchange || '?')[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap fontWeight={600}>{option.name || 'Unnamed'}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, minWidth: 0 }}>
              {(() => {
                const { isSim, chipLabel, chipColor } = getAccountMeta(option);
                return <Chip size="small" variant="filled" color={chipColor} label={chipLabel} sx={{ height: 22 }} />;
              })()}
              <Typography noWrap variant="caption" color="text.secondary" sx={{ opacity: 0.9 }}>
                {getAccountMeta(option).subtitle}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    />
  );
}



const mockTransactions = [
  { id: 1, botId: 1, type: 'BUY', pair: 'BTC/USDT', amount: 0.05, price: 42000, value: 2100, fee: 2.10, date: '2023-05-15 10:30:22' },
  { id: 2, botId: 1, type: 'SELL', pair: 'BTC/USDT', amount: 0.05, price: 43500, value: 2175, fee: 2.18, date: '2023-05-16 15:45:10' },
  { id: 3, botId: 2, type: 'BUY', pair: 'ETH/USDT', amount: 2.5, price: 2800, value: 7000, fee: 7.00, date: '2023-05-17 09:15:33' },
];

const indicators = [
  { name: 'RSI', params: ['period', 'source'], info: 'RSI(period, source) — Relative Strength Index' },
  { name: 'MACD', params: [], info: 'MACD() — Moving Average Convergence Divergence' },
  { name: 'Volume', params: [], info: 'Volume — Candle volume' },
  { name: 'SMA', params: ['period', 'source'], info: 'SMA(period, source) — Simple Moving Average' },
  { name: 'EMA', params: ['period', 'source'], info: 'EMA(period, source) — Exponential Moving Average' },

  // ✅ Tick-based indicators
  { name: 'tickPrice', params: [], info: 'tickPrice — Latest tick price' },
  { name: 'tickDelta', params: [], info: 'tickDelta — Change from last tick' },
  { name: 'tickVolume', params: [], info: 'tickVolume — Tick count in interval' },
  { name: 'tickTradeVolume', params: [], info: 'tickTradeVolume — Total quantity from recent ticks' },
  { name: 'tickRSI', params: ['period'], info: 'tickRSI(period) — RSI on tick prices' },
  { name: 'tickVWAP', params: [], info: 'tickVWAP — VWAP from tick data' }
];
const operators = ['<', '<=', '==', '>=', '>', '+', '-', '*', '/', '(', ')'];
const combinators = ['AND', 'OR'];

const TICK_INDICATOR_NAMES = new Set([
  'tickPrice', 'tickDelta', 'tickVolume', 'tickTradeVolume', 'tickRSI', 'tickVWAP'
]);


// Mock backtest data generator
const generateBacktestResults = (bot) => {
  const startDate = new Date('2023-01-01');
  const endDate = new Date('2023-06-01');
  const days = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));

  const trades = Math.floor(Math.random() * 50) + 10;
  const profit = (Math.random() * 30 - 5).toFixed(2);
  const winRate = (Math.random() * 40 + 50).toFixed(1);

  const equityCurve = Array.from({ length: days }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      date,
      value: 10000 + (i * (profit * 100 / days)) + (Math.random() * 200 - 100)
    };
  });

  const metrics = {
    totalReturn: `${profit}%`,
    winRate: `${winRate}%`,
    sharpeRatio: (Math.random() * 2 + 0.5).toFixed(2),
    maxDrawdown: `-${(Math.random() * 15 + 5).toFixed(1)}%`,
    trades,
    profitFactor: (Math.random() * 1.5 + 0.5).toFixed(2)
  };

  return { equityCurve, metrics, trades: generateMockTrades(bot, trades) };
};

const pctStr = (v, digits = 2) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `${Number(v).toFixed(digits)}%`;
};
const moneyStr = (v, digits = 2) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const sign = Number(v) >= 0 ? '' : '-';
  return `${sign}$${Math.abs(Number(v)).toFixed(digits)}`;
};
const pfStr = (v) => {
  if (v === Infinity) return '∞';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
};


const generateMockTrades = (bot, count) => {
  return Array.from({ length: count }, (_, i) => {
    const isBuy = Math.random() > 0.5;
    const entryPrice = isBuy ? 40000 + Math.random() * 5000 : 45000 - Math.random() * 5000;
    const exitPrice = isBuy ? entryPrice * (1 + Math.random() * 0.1) : entryPrice * (1 - Math.random() * 0.05);
    const profit = isBuy ? (exitPrice - entryPrice) / entryPrice * 100 : (entryPrice - exitPrice) / entryPrice * 100;

    return {
      id: i + 1,
      type: isBuy ? 'BUY' : 'SELL',
      entryPrice: entryPrice.toFixed(2),
      exitPrice: exitPrice.toFixed(2),
      entryDate: format(new Date(2023, 0, Math.floor(Math.random() * 150) + 1), 'yyyy-MM-dd HH:mm'),
      exitDate: format(new Date(2023, 0, Math.floor(Math.random() * 150) + 2), 'yyyy-MM-dd HH:mm'),
      profit: profit.toFixed(2) + '%',
      amount: (Math.random() * 0.1 + 0.01).toFixed(4),
      profitValue: (Math.random() * 200 - 50).toFixed(2)
    };
  });
};

const downsample = (arr, max = 2000) => {
  if (!Array.isArray(arr) || arr.length <= max) return arr || [];
  const step = Math.ceil(arr.length / max);
  const out = [];
  for (let i = 0; i < arr.length; i += step) out.push(arr[i]);
  if (out[out.length - 1] !== arr[arr.length - 1]) out.push(arr[arr.length - 1]);
  return out;
};

const toChartData = (equityCurve = []) =>
  downsample(equityCurve).map(([t, e]) => ({ time: toMs(t), equity: Number(e) }));

function evaluateLogic(ruleString) {
  const mockValues = {
    RSI: 55,
    SMA: 1800,
    EMA: 1825,
    MACD: 5,
    Volume: 100000,

    // ✅ Tick-based mock values
    tickPrice: 42780,
    tickDelta: -1.25,
    tickVolume: 63,
    tickTradeVolume: 18.9,
    tickRSI: 60,
    tickVWAP: 42750.3
  };

  try {
    const expr = ruleString
      .replace(/\b(tickRSI|tickVWAP|tickPrice|tickDelta|tickVolume|tickTradeVolume|RSI|SMA|EMA|MACD|Volume)\s*\(\s*[^)]*\)/g, (_, name) => {
        return mockValues[name] !== undefined ? mockValues[name] : '0';
      })
      .replace(/\b(tickRSI|tickVWAP|tickPrice|tickDelta|tickVolume|tickTradeVolume|RSI|SMA|EMA|MACD|Volume)\b/g, match => {
        return mockValues[match] !== undefined ? mockValues[match] : '0';
      })
      .replace(/\bAND\b/gi, '&&')
      .replace(/\bOR\b/gi, '||');

    const result = eval(expr);
    return {
      valid: typeof result === 'boolean',
      result: typeof result === 'boolean' ? result : null
    };
  } catch (e) {
    return { valid: false, result: null };
  }
}




const FUTURES_SUPPORTED_EXCHANGES = new Set([
  'binance.com',   // ✅ has USDT-M and COIN-M futures
  'binance',       // fallback if your DB stores it without TLD
  // 'binance.us' intentionally excluded — no futures
]);
// Futures position actions (replaces simple buy/sell for futures)
const FUTURES_ACTIONS = {
  OPEN_LONG: 'OPEN_LONG',
  CLOSE_LONG: 'CLOSE_LONG',
  OPEN_SHORT: 'OPEN_SHORT',
  CLOSE_SHORT: 'CLOSE_SHORT',
};

// Order execution presets for Option B
const ORDER_EXECUTION_MODES = {
  market: {
    label: 'Market Order',
    description: 'Immediate fill at current price. Best for fast-moving strategies.',
    icon: '⚡',
  },
  limit: {
    label: 'Limit Order',
    description: 'Place at specific price. Lower fees, risk of no fill.',
    icon: '🎯',
  },
  aggressive_limit: {
    label: 'Smart Limit',
    description: 'Limit order with small buffer + auto-fallback to market after timeout.',
    icon: '🧠',
  },
};

// Which margin modes Binance supports
const MARGIN_MODES = ['cross', 'isolated'];

// Leverage presets for quick selection
const LEVERAGE_PRESETS = [1, 2, 3, 5, 10, 20, 50];

// ─────────────────────────────────────────────────────────────
// Funding asset — balance source for bot capital, P&L, equity.
// Distinct from the pair's quote asset (the settlement currency).
// When they differ, the bot converts at startup and back at exit.
// ─────────────────────────────────────────────────────────────
const FUNDING_ASSETS_CRYPTO = ['USDT', 'USDC', 'FDUSD', 'BUSD', 'BTC', 'ETH'];
const FUNDING_ASSETS_STOCK = ['USD'];

function getFundingAssetsForClass(assetClass) {
  return assetClass === 'stock_equity' ? FUNDING_ASSETS_STOCK : FUNDING_ASSETS_CRYPTO;
}

function defaultFundingAssetForClass(assetClass) {
  return assetClass === 'stock_equity' ? 'USD' : 'USDT';
}

const FUNDING_STABLES = new Set(['USDT', 'USDC', 'FDUSD', 'BUSD', 'TUSD', 'DAI', 'USD']);

// For a single-pair manual bot, describe whether funding asset matches
// the pair's quote. Returns null when matched (no warning).
function describeFundingMismatchForPair(fundingAsset, quoteAsset) {
  if (!fundingAsset || !quoteAsset) return null;
  const f = String(fundingAsset).toUpperCase();
  const q = String(quoteAsset).toUpperCase();
  if (f === q) return null;

  const bothStable = FUNDING_STABLES.has(f) && FUNDING_STABLES.has(q);
  return {
    severity: bothStable ? 'info' : 'warning',
    title: bothStable
      ? `Stablecoin conversion: ${f} → ${q} at entry`
      : `Cross-asset conversion: ${f} → ${q} at entry`,
    body: bothStable
      ? `This pair quotes in ${q} but your bot is funded from ${f}. The bot will swap ${f} → ${q} at startup (and back at exit). Stablecoin swaps cost less than 0.01%, so impact is minimal — but you'll see two extra trades per bot lifecycle.`
      : `This pair quotes in ${q} but your bot is funded from ${f}. The bot will swap ${f} → ${q} at startup and back on exit, exposing you to spread (~0.05–0.2%) and ${f} price movement during the position. Funding directly in ${q} would be cheaper.`,
  };
}





// Helper: does the selected exchange support futures?
function exchangeSupportsFutures(exchangeObj) {
  if (!exchangeObj) return false;
  const name = (exchangeObj.exchange || exchangeObj.name || '').toLowerCase().trim();
  return FUTURES_SUPPORTED_EXCHANGES.has(name);
}

// Add near the top of your fi

const steps = ['Basic Configuration', 'Trading Logic', 'Risk Management'];

export default function TradingBotDashboard() {
  const [activeStep, setActiveStep] = useState(0);
  const routerHistory = useHistory();
  const rfLocation = useLocation();
  const quickBotRanRef = useRef(false);
  const [exchangesLoaded, setExchangesLoaded] = useState(false);
  const [bots, setBots] = useState(initialBots);

  // Stable list of bot ids (used for websocket room joins/listeners)
  const botIds = useMemo(() => {
    const ids = (bots || []).map(b => Number(b.id)).filter(n => Number.isFinite(n));
    ids.sort((a, b) => a - b);
    return ids;
  }, [bots]);

  const botIdsKey = useMemo(() => botIds.join(','), [botIds]);

  const [newBot, setNewBot] = useState({
    name: '',
    interval: '1H',
    symbol: 'BTC/USDT',
    sizeMode: 'quote',
    positionValue: '',
    totalAmount: '',
    maxLoss: '5',
    maxTrades: '10',
    trailingStop: '2',
    exchangeId: null,

    // Signal source (existing)
    signalSource: 'logic',
    webhookSecret: '',
    webhookActionField: 'action',
    webhookBuyValue: 'BUY',
    webhookSellValue: 'SELL',

    // ✅ NEW: Futures configuration
    marketType: 'spot',            // 'spot' | 'futures'
    futuresType: 'usdt_m',        // 'usdt_m' | 'coin_m' (Binance-specific)
    leverage: 1,                   // 1-125
    marginMode: 'cross',          // 'cross' | 'isolated'
    positionSide: 'one_way',      // 'one_way' | 'hedge'
    // In hedge mode, buy logic = open long, sell logic = close long
    // Separate short logic fields:
    shortOpenLogic: '',            // only used in hedge mode
    shortCloseLogic: '',           // only used in hedge mode

    // ✅ NEW: Order execution (Option B)
    orderExecution: 'market',      // 'market' | 'limit' | 'aggressive_limit'
    // Advanced order settings (revealed on toggle):
    limitOffsetPct: 0.05,          // % above/below current price for limit orders
    limitTimeoutSec: 10,           // seconds before fallback to market
    postOnly: false,               // maker-only flag
    reduceOnly: false,             // futures: only reduce position
    futureSizeMode: 'margin_pct',
    marginPct: '',
    fixedNotional: '',
    maxConcurrentTrades: 1,
    marginAsset: undefined,
    baseAsset: undefined,
    quoteAsset: undefined,
    basePrecision: null,
    quotePrecision: null,
    contractType: null,

    // Phase B — stock-specific
// Phase B — stock-specific
    extendedHours: false,     // allow PRE/POST market on stock_equity
    allowShorts: false,       // enable short_open_logic / short_close_logic on stock_equity

    // Funding asset — balance source for bot capital & P&L
    funding_asset: 'USDT',
  });



  // top of your component
// top of your component
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });
  const closeToast = () => setToast(t => ({ ...t, open: false }));

  // Soft-warning state for the duplicate-bot 409 path.
  // shape: { conflict: { bot_id, bot_name, symbol, exchange_id, created_at }, pendingPayload }
  const [duplicateBotConflict, setDuplicateBotConflict] = useState(null);


  const [buyConditions, setBuyConditions] = useState([]);
  const [sellConditions, setSellConditions] = useState([]);
  const [userExchanges, setUserExchanges] = useState([]);
  const [tempToken, setTempToken] = useState({ side: 'buy', value: '', combinator: 'AND' });
  const [profitStop, setProfitStop] = useState('20');
  const [lossStop, setLossStop] = useState('10');
  const [expandedBot, setExpandedBot] = useState(null);
  const [activeTab, setActiveTab] = useState('bots');
  const [isCreating, setIsCreating] = useState(false);
  const [backtestProgress, setBacktestProgress] = useState(0);
  const [isBacktesting, setIsBacktesting] = useState(false);
  const [backtestResults, setBacktestResults] = useState(null);
  const [backtestDialogOpen, setBacktestDialogOpen] = useState(false);
  const [backtestDateRange, setBacktestDateRange] = useState(defaultBacktestRange());
  const [btRange, setBtRange] = useState('1M'); // active date-range preset chip (§7a)
  const [btAdvOpen, setBtAdvOpen] = useState(false); // collapsible Advanced params in the backtest config bar (§7a)
  const [rangeBounds, setRangeBounds] = useState(() => computeBacktestBounds(null));

  const [activeBacktestTab, setActiveBacktestTab] = useState('summary');
  const [selectedBotForBacktest, setSelectedBotForBacktest] = useState(null);
  const [liveBotHistories, setLiveBotHistories] = useState({});
  // Per-bot history pagination state: { [botId]: { offset, total, loading, done } }
  const [historyPaging, setHistoryPaging] = useState({});
  const listenersRef = useRef(new Map());  // ✅ Top-level hook call
  const [liveBacktest, setLiveBacktest] = useState(null);
  const liveBacktestRef = useRef(null); // ← ADD THIS

  const [recentBacktests, setRecentBacktests] = useState([]);
  const [loadingSavedBacktest, setLoadingSavedBacktest] = useState(false);
  const [viewingSavedBacktestId, setViewingSavedBacktestId] = useState(null);
  const isSavedView = Boolean(viewingSavedBacktestId);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const backtestListenersRef = useRef(new Map()); // jobId -> handler
  const backtestPollRef = useRef(null); // ← ADD THIS
  const [futuresInfo, setFuturesInfo] = useState({}); // { [botId]: { fundingRate, nextFundingTime, markPrice, indexPrice, openInterest, ... } }

  const [isCanceling, setIsCanceling] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [backtestParams, setBacktestParams] = useState({
    initialCapital: 10000,
    commission: 0.1,
    slippage: 0.5,
  });

  const [symbolsByExchange, setSymbolsByExchange] = useState({});   // { [exchangeId]: ["BTCUSDT", ...] }
  const [symbolsLoading, setSymbolsLoading] = useState(false);
  const [symbolsError, setSymbolsError] = useState(null);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [confirmDeleteBotId, setConfirmDeleteBotId] = useState(null);
  const [isDeletingBot, setIsDeletingBot] = useState(false);
  const [futuresSymbolsByExchange, setFuturesSymbolsByExchange] = useState({});
  const [shortOpenConditions, setShortOpenConditions] = useState([]);
  const [shortCloseConditions, setShortCloseConditions] = useState([]);
  const [botListTab, setBotListTab] = useState('manual');  // 'manual' | 'scanner'
  const [expandedScannerProfile, setExpandedScannerProfile] = useState(null);

// Phase A: poll Alpaca account status (PDT, buying power, market hours, halts)
  // every 30s. Returns global market state + per-exchange-id account state.
  // Hook self-disables if no Alpaca exchanges are configured.
   const hasAlpacaExchange = useMemo(
    () => userExchanges.some(ex => String(ex.exchange || '').toLowerCase().startsWith('alpaca')),
    [userExchanges]
  );
  const alpacaStatus = useAlpacaAccountStatus({ enabled: hasAlpacaExchange });

  // Phase B — asset class derived from selected exchange
  const selectedExchangeForAssetClass = useMemo(
    () => userExchanges.find(ex => ex.id === newBot.exchangeId) || null,
    [userExchanges, newBot.exchangeId]
  );
const assetClass = useAssetClass(selectedExchangeForAssetClass);
  const isStockEquity = assetClass === 'stock_equity';

  // When asset class flips between crypto and stock, snap funding_asset to a
  // valid default. Prevents saving an Alpaca bot with funding_asset='USDT'
  // or a Binance bot stuck on 'USD'.
  useEffect(() => {
    const allowed = getFundingAssetsForClass(assetClass);
    if (!allowed.includes(newBot.funding_asset)) {
      setNewBot(prev => ({
        ...prev,
        funding_asset: defaultFundingAssetForClass(assetClass),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetClass]);

  // Phase B — shortability cache (only relevant for stock_equity)
  const shortability = useShortability({ enabled: isStockEquity });

  // Phase B — account status for the currently-selected exchange (for PDT warning)
  const accountStatusForSelected = useMemo(() => {
    if (!isStockEquity || !newBot.exchangeId) return null;
    return alpacaStatus?.accounts?.[newBot.exchangeId] || null;
  }, [isStockEquity, newBot.exchangeId, alpacaStatus]);

  // Create-bot readiness — drives the rail checklist + the readiness-gated footer.
  const botReadiness = useMemo(() => {
    const sizeOk = newBot.marketType === 'futures'
      ? (newBot.futureSizeMode === 'fixed_notional' ? Number(newBot.fixedNotional) > 0 : Number(newBot.marginPct) > 0)
      : Number(newBot.positionValue || newBot.tradeAmount) > 0;
    const logicOk = newBot.signalSource === 'tradingview'
      ? !!newBot.webhookSecret
      : (buyConditions.length > 0 && sellConditions.length > 0);
    const gates = [
      { key: 'name', label: 'Name your bot', done: !!newBot.name?.trim(), step: 0 },
      { key: 'exchange', label: 'Connect an exchange', done: !!newBot.exchangeId, step: 0 },
      { key: 'pair', label: 'Choose a trading pair', done: newBot.signalSource === 'tradingview' ? true : !!newBot.symbol, step: 0 },
      { key: 'logic', label: 'Define entry & exit logic', done: logicOk, step: 1 },
      { key: 'size', label: 'Set position size', done: sizeOk, step: 0 },
      { key: 'risk', label: 'Set risk limits', done: Number(profitStop) > 0 && Number(lossStop) > 0, step: 2 },
    ];
    const doneCount = gates.filter((g) => g.done).length;
    return { gates, doneCount, total: gates.length, ready: doneCount === gates.length, firstMissing: gates.find((g) => !g.done) };
  }, [newBot, buyConditions.length, sellConditions.length, profitStop, lossStop]);

  useEffect(() => {
    liveBacktestRef.current = liveBacktest;
  }, [liveBacktest]);

  useEffect(() => {
    const fetchUserExchanges = async () => {
      try {
        const data = await exchangeApiService.list(); // assuming it returns exchanges owned by the user
        setUserExchanges(data);
      } catch (err) {
        console.error("Failed to fetch exchanges", err);
      } finally {
        setExchangesLoaded(true);
      }
    };

    fetchUserExchanges();
  }, []);


  useEffect(() => {
    if (!newBot.exchangeId) return;
    const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
    const exchangeName = selectedExchange?.exchange || selectedExchange?.name;
    if (!exchangeName) return;

    if (newBot.marketType === 'futures') {
      // Load the relevant futures market
      const marketType = newBot.futuresType === 'coin_m' ? 'coinm_futures' : 'usdm_futures';
      ensureFuturesSymbols(newBot.exchangeId, exchangeName, marketType);
    } else {
      ensureSymbols(newBot.exchangeId, exchangeName);
    }
  }, [newBot.exchangeId, newBot.marketType, newBot.futuresType, userExchanges]);


  const fmtMoneyAdaptive = (v) => {
    if (!isNum(v)) return '—';
    const n = Number(v);
    const digits = Math.abs(n) < 1 ? 4 : 2;
    return fmtMoney(n, digits);
  };



  // right inside your component
  const exchangeValue = React.useMemo(
    () => userExchanges.find(ex => ex.id === newBot.exchangeId) || null,
    [userExchanges, newBot.exchangeId]
  );
  // "BTCUSDT" <-> "BTC/USDT" helpers for display
  const compactToPretty = (s) => (typeof s === 'string' && s.length > 3
    ? s.replace(/(USDT|USDC|BTC|ETH|BUSD|USD|EUR)$/, '/$1')
    : s);

  const prettyToCompact = (s) => (typeof s === 'string' ? s.replace(/\//g, '') : s);

  // fetch & cache symbols for a given exchangeId (from Redis)
  async function ensureSymbols(exchangeId, exchangeName) {
    const cacheKey = exchangeName || exchangeId;
    if (!cacheKey || symbolsByExchange[cacheKey]) return;

    try {
      setSymbolsLoading(true);
      setSymbolsError(null);

      const list = await symbolService.get(exchangeName, 'objects');

      const normalized = Array.isArray(list)
        ? list.map(item => {
          if (typeof item === 'string') {
            const [BASE, QUOTE] = splitPair(item);
            return {
              symbol: item,
              base_asset: BASE,
              base_precision: null,
              quote_asset: QUOTE,
              quote_precision: null,
              categories: [],
              default_maker_fee: null,
              default_taker_fee: null,
              is_zero_fee: false,
            };
          }

          const sym = item.symbol;
          let base = item.base_asset;
          let quote = item.quote_asset;

          if (base && !quote) {
            quote = sym.toUpperCase().replace(new RegExp(`^${base}`, 'i'), '');
            if (!quote || quote === sym.toUpperCase()) {
              const [, fallbackQuote] = splitPair(sym);
              quote = fallbackQuote;
            }
          }
          if (!base || !quote) {
            const [fallbackBase, fallbackQuote] = splitPair(sym);
            base = base || fallbackBase;
            quote = quote || fallbackQuote;
          }

          return {
            symbol: sym,
            base_asset: base,
            base_precision: item.base_precision ?? item.precision ?? null,
            quote_asset: quote,
            quote_precision: item.quote_precision ?? 2,
            // ✅ Carry through metadata
            categories: Array.isArray(item.categories) ? item.categories : [],
            default_maker_fee: item.default_maker_fee != null ? Number(item.default_maker_fee) : null,
            default_taker_fee: item.default_taker_fee != null ? Number(item.default_taker_fee) : null,
            is_zero_fee: !!item.is_zero_fee,
          };
        })
        : [];

      setSymbolsByExchange(m => ({ ...m, [cacheKey]: normalized }));
    } catch (e) {
      console.error('symbols fetch failed', e);
      setSymbolsError('Failed to load symbols');
    } finally {
      setSymbolsLoading(false);
    }
  }

  useEffect(() => {
    const saved = localStorage.getItem('bt.params');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setBacktestParams(prev => ({ ...prev, ...parsed }));
      } catch { }
    }
  }, []);


  // ── Fetch Binance futures info for all futures bots ──
  useEffect(() => {
    if (!bots.length) return;

    const futureBots = bots.filter(b => {
      try {
        const ti = JSON.parse(b.trade_info || '{}');
        return ti.marketType === 'futures';
      } catch { return false; }
    });

    if (!futureBots.length) return;

    const controller = new AbortController();

    const fetchFuturesData = async () => {
      const updates = {};

      for (const bot of futureBots) {
        let ti;
        try { ti = JSON.parse(bot.trade_info || '{}'); } catch { continue; }

        const symbol = (ti.symbol || bot.symbol || '').replace('/', '').toUpperCase();
        if (!symbol) continue;

        const isCoinM = ti.futures?.type === 'coin_m' || ti.futuresType === 'coin_m';
        const base = isCoinM
          ? 'https://dapi.binance.com/dapi/v1'
          : 'https://fapi.binance.com/fapi/v1';

        try {
          const [premiumRes, oiRes, fundingRes] = await Promise.all([
            fetch(`${base}/premiumIndex?symbol=${symbol}`, { signal: controller.signal }),
            fetch(`${base}/openInterest?symbol=${symbol}`, { signal: controller.signal }),
            fetch(`${base}/fundingRate?symbol=${symbol}&limit=1`, { signal: controller.signal }),
          ]);

          const premium = await premiumRes.json();
          const oi = await oiRes.json();
          const fundingArr = await fundingRes.json();

          // premiumIndex response shape (USDT-M):
          //   { symbol, markPrice, indexPrice, lastFundingRate, nextFundingTime, ... }
          const p = Array.isArray(premium) ? premium[0] : premium;
          const f = Array.isArray(fundingArr) ? fundingArr[0] : fundingArr;

          updates[bot.id] = {
            markPrice: Number(p?.markPrice) || null,
            indexPrice: Number(p?.indexPrice) || null,
            lastFundingRate: Number(p?.lastFundingRate ?? f?.fundingRate) || null,
            nextFundingTime: Number(p?.nextFundingTime) || null,
            interestRate: Number(p?.interestRate) || null,
            openInterest: Number(oi?.openInterest) || null,
            fetchedAt: Date.now(),
          };
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.warn(`Futures info fetch failed for ${symbol}:`, err);
          }
        }
      }

      setFuturesInfo(prev => ({ ...prev, ...updates }));
    };

    fetchFuturesData();
    const interval = setInterval(fetchFuturesData, 30_000); // refresh every 30s

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [botIdsKey]); // re-run when bot list changes


  useEffect(() => {
    localStorage.setItem('bt.params', JSON.stringify(backtestParams));
  }, [backtestParams]);

  useEffect(() => {
    const bounds = computeBacktestBounds(selectedBotForBacktest);
    setRangeBounds(bounds);

    setBacktestDateRange(prev => {
      // if no prior range, fall back to your default 1-month window
      let [s, e] = Array.isArray(prev) ? prev : defaultBacktestRange();

      // clamp to new bounds
      s = Math.max(bounds.min, s);
      e = Math.min(bounds.max, e);

      // if inverted, show ~30 days ending at max
      if (s > e) {
        e = bounds.max;
        s = Math.max(bounds.min, e - 30 * MS_DAY);
      }
      return [s, e];
    });
  }, [selectedBotForBacktest]);


  useEffect(() => {
    return () => {
      // Clear poll interval on unmount
      if (backtestPollRef.current) clearInterval(backtestPollRef.current);
      // component unmount cleanup
      for (const { channel, handlers } of backtestListenersRef.current.values()) {
        for (const h of handlers || []) {
          socket.off(channel, h);
        }
      }
      backtestListenersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const fetchBots = async () => {
      try {
        const userId = parseInt(localStorage.getItem('userId'), 10);

        // Fetch regular bots (excludes scanner profiles by default)
        const regularBots = await tradingBotService.list(userId);

        // Fetch scanner profiles separately
        let scannerProfiles = [];
        try {
          scannerProfiles = await tradingBotService.list(userId, { status: 'scanner_profile' });
        } catch (err) {
          console.warn('Failed to fetch scanner profiles:', err);
        }

        // Merge: scanner profiles first, then regular bots
        // Merge with dedup — guards against any API call returning overlapping rows
        const seenIds = new Set();
        const botsFromServer = [];
        for (const bot of [...scannerProfiles, ...regularBots]) {
          if (seenIds.has(bot.id)) continue;
          seenIds.add(bot.id);
          botsFromServer.push(bot);
        }
        // Trade history is NOT bulk-loaded here anymore (that downloaded every
        // bot's entire history up front). The socket `join` seeds the most recent
        // N entries per bot, and expanding a bot lazy-loads older pages via
        // GET /api/userhistory/by_bot/:botId. Existing liveBotHistories is left
        // intact across refreshes.

        // 📦 Process bots
        const formattedBots = botsFromServer.map(bot => {
          let tradeInfo = {};
          try {
            tradeInfo = JSON.parse(bot.trade_info || '{}');
          } catch (_) { }

          const webhookSecret = (tradeInfo.webhook?.secret || '').trim();
          const signalSource = tradeInfo.signalSource || 'logic';

          return {
            id: bot.id,
            name: bot.name,
            symbol: tradeInfo.symbol,
            interval: tradeInfo.timeframe,
            exchangeId: bot.exchange_id ?? bot.exchangeId ?? null,
            exchange: bot.exchange?.exchange || '',
            trade_info: bot.trade_info,
            exchangeName: bot.exchange?.name || bot.exchange?.exchange || "—",
            status: bot.stop_trade_ASSET ? 'paused' : 'active',
            stop_trade_ASSET: bot.stop_trade_ASSET,
            logic: {
              buy: bot.buy_logic,
              sell: bot.sell_logic,
              shortOpen: tradeInfo.futures?.shortOpenLogic || bot.short_open_logic || '',
              shortClose: tradeInfo.futures?.shortCloseLogic || bot.short_close_logic || '',
              profitStop: tradeInfo.profitStop,
              lossStop: tradeInfo.lossStop,
              maxLoss: tradeInfo.maxDailyLoss,
              maxTrades: tradeInfo.maxTradesPerDay,
              trailingStop: tradeInfo.trailingStop,
              investment: tradeInfo.totalInvestment,
            },
            // Normalize server stats at fetch time (the socket path already does)
            // so bot.stats is always the panel's PascalCase shape; otherwise the
            // first paint would read camelCase DB stats and show blanks.
            stats: bot.statistics ? normalizeBotStats(bot.statistics) : undefined,
            statusMessage: bot.status || '',
            errorMessage: bot.error_message || '',

            // ✅ TradingView webhook fields
            signalSource,
            webhookUrl: signalSource === 'tradingview'
              ? buildTradingViewWebhookUrl(bot.id, webhookSecret)
              : '',
            webhookConfig: tradeInfo.webhook || null,
            isScannerProfile: bot.status === 'scanner_profile',
            spawnedFromProfileId: bot.spawned_from_profile_id ?? null,
            scannerConfig: tradeInfo.scanner || null,
            spawnedFromMeta: tradeInfo._spawned_from || null,
          };
        });

        setBots(formattedBots);
      } catch (error) {
        console.error('Error fetching bots or histories:', error);
      }
    };

    fetchBots();
  }, []);


  // Preload symbol caches for every exchange the user's bots live on.
// Without this, spawned bots on a sim exchange (or any exchange the user
// hasn't browsed in the create-bot form yet) fall through to splitPair
// and mis-render fiat-quoted pairs (TRY, BRL, EUR, etc.) as USD.
useEffect(() => {
  if (!bots.length || !userExchanges.length) return;
  const seen = new Set();
  for (const bot of bots) {
    const ex = userExchanges.find(e => e.id === bot.exchangeId);
    const name = ex?.exchange || ex?.name;
    if (!name || seen.has(name)) continue;
    seen.add(name);
    ensureSymbols(bot.exchangeId, name);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [bots.length, userExchanges.length]);


  useEffect(() => {
    // Keep websocket room membership & per-bot listeners in sync with the current bot list.
    // This preserves existing behavior while preventing duplicate listeners on reconnects/rerenders.
    const userId = parseInt(localStorage.getItem("userId"), 10);

    const registerBotListener = (botId) => {
      const channel = `botUpdate:${botId}`;
      if (listenersRef.current.has(channel)) return;

      const listener = (data) => {
        console.log(`[${channel}] update received`, data);

        const actualBotId = parseInt(data.bot_id || data.id || botId, 10);
        if (!actualBotId) return;

        // ✅ Handle stats - could be object, string, or nested in botData
        let statsData = data.stats || data.botData?.stats;
        if (typeof statsData === 'string') {
          try { statsData = JSON.parse(statsData); } catch { statsData = null; }
        }
        const normalizedStats = statsData && Object.keys(statsData).length > 0
          ? normalizeBotStats(statsData)
          : null;

        // Parse history entries
        const historyData = Array.isArray(data.history) ? data.history : [];
        const parsedEntries = historyData.map(entry => {
          let parsed = entry;
          if (typeof entry === 'string') {
            try { parsed = JSON.parse(entry); } catch { return null; }
          }
          let cleanText = parsed.buy_sell_history;
          if (typeof cleanText === 'string') {
            try { cleanText = JSON.parse(cleanText); } catch { }
          }
          const ms = new Date(parsed.date).getTime();
          return { _ts: Number.isFinite(ms) ? ms : 0, timestamp: new Date(parsed.date).toLocaleString(), text: cleanText };
        }).filter(Boolean);

        if (parsedEntries.length > 0) {
          setLiveBotHistories(prev => {
            const updated = { ...prev };
            updated[actualBotId] = mergeHistoryEntries(updated[actualBotId] || [], parsedEntries);
            return updated;
          });
        }

        // Update bot state with ALL fields
        setBots(prev =>
          prev.map(bot => {
            if (bot.id !== actualBotId) return bot;

            const updatedBot = { ...bot };

            // ✅ Update stats if present
            if (normalizedStats) {
              updatedBot.stats = normalizedStats;
              console.log(`[${channel}] stats updated:`, normalizedStats);
            }

            // Status fields
            if (data.error_message !== undefined) updatedBot.errorMessage = data.error_message;
            if (data.error_side !== undefined) updatedBot.errorSide = data.error_side;
            if (data.error_code !== undefined) updatedBot.errorCode = data.error_code;
            if (data.error_at !== undefined) updatedBot.errorAt = data.error_at;
            if (data.status !== undefined) updatedBot.statusMessage = data.status;
            if (data.stop_trade_ASSET !== undefined) {
              updatedBot.stop_trade_ASSET = data.stop_trade_ASSET;
              updatedBot.status = data.stop_trade_ASSET ? 'paused' : 'active';
            }

            // Guard data from decision engine
            if (data.guard_tag !== undefined) updatedBot.guardTag = data.guard_tag || '';
            if (data.guard_reason !== undefined) updatedBot.guardReason = data.guard_reason || '';
            if (data.guard_at !== undefined) updatedBot.guardAt = data.guard_at || '';
            if (data.guard_side !== undefined) updatedBot.guardSide = data.guard_side || '';
            if (data.guard_price !== undefined) updatedBot.guardPrice = data.guard_price || '';

            // Current price
            if (data.ASSET_PRICE) updatedBot.currentPrice = data.ASSET_PRICE;

            // ✅ Last trade info
            if (data.last_trade_side) updatedBot.lastTradeSide = data.last_trade_side;
            if (data.last_trade_price) updatedBot.lastTradePrice = data.last_trade_price;
            if (data.last_trade_qty) updatedBot.lastTradeQty = data.last_trade_qty;
            if (data.last_trade_ts) updatedBot.lastTradeTs = data.last_trade_ts;

            // Pending limit order info
            // Pending limit order info
            if (data.pending_order_id !== undefined) updatedBot.pendingOrderId = data.pending_order_id || '';
            if (data.pending_order_side !== undefined) updatedBot.pendingOrderSide = data.pending_order_side || '';
            if (data.pending_order_price !== undefined) updatedBot.pendingOrderPrice = data.pending_order_price || '';
            // Phase A: partial fill progress
            if (data.pending_order_quantity !== undefined) updatedBot.pendingOrderQuantity = data.pending_order_quantity || '';
            if (data.pending_order_applied_qty !== undefined) updatedBot.pendingOrderAppliedQty = data.pending_order_applied_qty || '';
            // Legacy trading status fields
            if (data.trading_status !== undefined) updatedBot.tradingStatus = data.trading_status;
            if (data.trading_status_reason !== undefined) updatedBot.tradingStatusReason = data.trading_status_reason;

            // ✅ Clear guard state if status is active and no guard_tag
            if (data.status === 'active' && !data.guard_tag && !data.error_message) {
              updatedBot.guardTag = '';
              updatedBot.guardReason = '';
              updatedBot.guardAt = '';
              updatedBot.guardSide = '';
              updatedBot.guardPrice = '';
              updatedBot.errorMessage = '';
            }

            return updatedBot;
          })
        );
      };

      listenersRef.current.set(channel, listener);
      socket.on(channel, listener);
    };

    const unregisterBotListener = (botId) => {
      const channel = `botUpdate:${botId}`;
      const listener = listenersRef.current.get(channel);
      if (listener) {
        socket.off(channel, listener);
        listenersRef.current.delete(channel);
      } else {
        // keep parity with prior behavior (removes any listener if present)
        socket.off(channel);
      }
    };

    const joinAll = () => {
      console.log("✅ WebSocket connected");

      if (userId) {
        socket.emit('join', userId); // 🔁 Request full sync
      }

      // Join rooms + attach listeners (idempotent)
      botIds.forEach(botId => {
        socket.emit("joinBotRoom", botId);
        console.log(`🟢 Joined room: bot:${botId}`);
        registerBotListener(botId);
      });
    };

    const handleConnect = () => {
      joinAll();
    };

    socket.on("connect", handleConnect);

    // If already connected, trigger immediately
    if (socket.connected) {
      handleConnect();
    } else {
      // While disconnected, ensure no stale listeners stick around (matches previous cleanup behavior)
      botIds.forEach(unregisterBotListener);
    }

    return () => {
      // Leave rooms and remove only the listeners we registered.
      botIds.forEach(botId => {
        socket.emit("leaveBotRoom", botId);
        unregisterBotListener(botId);
      });
      socket.off("connect", handleConnect);
    };
  }, [botIdsKey]);


  function safeParseMaybe(objOrStr, fallback = {}) {
    if (objOrStr == null) return fallback;
    if (typeof objOrStr === 'string') {
      try { return JSON.parse(objOrStr); } catch { return fallback; }
    }
    return objOrStr;
  }

  // normalize detail → {metrics, trades, equityCurve}
  function normalizeBacktestDetail(detail = {}) {
    const metrics = safeParseMaybe(detail.metrics, {});
    const trades = Array.isArray(detail.trades)
      ? detail.trades
      : safeParseMaybe(detail.trades, []);
    const equityCurve =
      Array.isArray(detail.equity_curve) ? detail.equity_curve :
        Array.isArray(detail.equity) ? detail.equity :
          safeParseMaybe(detail.equity_curve || detail.equity, []);

    return { metrics, trades, equityCurve };
  }

  // Is the create form currently in tick mode?
  const isTickInterval = useMemo(
    () => String(newBot.interval || '').toLowerCase() === 'tick',
    [newBot.interval]
  );

  // Split indicators by family
  const availableIndicators = useMemo(() => {
    return indicators.filter(ind =>
      isTickInterval ? TICK_INDICATOR_NAMES.has(ind.name) : !TICK_INDICATOR_NAMES.has(ind.name)
    );
  }, [isTickInterval]);

  function normalizeBotStats(raw = {}) {
    if (!raw || typeof raw !== 'object') return {};

    const num = (v) => {
      if (v === null || v === undefined || v === '') return undefined;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };

    return {
      // Core capital
      InitialCapital: num(raw.initialCapital),
      CashQuote: num(raw.cashQuote),
      Equity: num(raw.equity),
      PeakEquity: num(raw.peakEquity),

      // Long position info
      PositionQty: num(raw.positionQty),
      PositionCost: num(raw.positionCost),
      EntryPrice: num(raw.entryPrice),
      MarkPrice: num(raw.markPrice),
      TrailPeakPrice: num(raw.trailPeakPrice),

      // ─── Short position info (Phase 7) ───
      ShortPositionQty: num(raw.shortPositionQty),
      ShortPositionCost: num(raw.shortPositionCost),
      ShortEntryPrice: num(raw.shortEntryPrice),
      ShortTrailLowPrice: num(raw.shortTrailLowPrice),

      // PnL
      RealizedPnL: num(raw.realizedPnL),
      RealizedPnLPct: num(raw.realizedPnLPct),
      GrossProfit: num(raw.grossProfit),
      GrossLoss: num(raw.grossLoss),

      // Drawdown
      MaxDrawdownPct: num(raw.maxDrawdownPct),

      // Trade stats
      Trades: num(raw.trades),
      Wins: num(raw.wins),
      Losses: num(raw.losses),
      WinRatePct: num(raw.winRatePct),
      // The engine emits 9999 as a "wins, no losses" sentinel; show it as ∞
      // (the panel's fmtRatio renders Infinity as '∞').
      ProfitFactor: (() => { const pf = num(raw.profitFactor); return pf === 9999 ? Infinity : pf; })(),

      // Volume & Fees
      VolumeBase: num(raw.volumeBase),
      VolumeQuote: num(raw.volumeQuote),
      FeesPaidQuote: num(raw.feesPaidQuote),

      // Meta
      Initialized: num(raw.initialized),
      UpdatedAt: raw.updatedAt,

      __raw: raw,
    };
  }


  // ✅ Trading status display helper
  // ✅ Trading status display helper
  // ✅ Enhanced Trading status display helper
  // ✅ Enhanced Trading status display helper - BLOCK states are normal flow

  // Augments checkBotBacktestable with a fallback extraction from buy/sell logic
  // strings, in case trade_info.indicators wasn't populated at save time.
  const gateBacktest = (bot, opts = { hasFuturesData: false }) => {
    if (!bot) return { supported: false, message: 'No bot selected', unsupported: [] };

    let ti = {};
    try {
      ti = typeof bot.trade_info === 'string'
        ? JSON.parse(bot.trade_info || '{}')
        : (bot.trade_info || {});
    } catch {
      return { supported: false, message: 'Bot configuration is malformed', unsupported: [] };
    }

    const hasIndicators = Array.isArray(ti.indicators) && ti.indicators.length > 0;

    if (!hasIndicators) {
      const buy = bot.logic?.buy || bot.buy_logic || '';
      const sell = bot.logic?.sell || bot.sell_logic || '';
      const shortOpen = bot.logic?.shortOpen || ti.futures?.shortOpenLogic || '';
      const shortClose = bot.logic?.shortClose || ti.futures?.shortCloseLogic || '';
      const extracted = extractIndicatorsFromLogic(`${buy} ${sell} ${shortOpen} ${shortClose}`);
      ti = { ...ti, indicators: extracted };
    }

    // Pass a patched bot so checkBotBacktestable sees the indicators
    const patched = { ...bot, trade_info: JSON.stringify(ti) };
    return checkBotBacktestable(patched, opts);
  };

  function getTradingStatusDisplay(bot) {
    let status = bot.guardTag || bot.tradingStatus;
    let reason = bot.guardReason || bot.tradingStatusReason;

    // ✅ BLOCK states are normal trading flow - don't show alerts
    if (status === 'BLOCK') {
      return null;
    }

    // Fallback: derive from errorMessage if no explicit status
    if (!status && bot.errorMessage) {
      const errLower = bot.errorMessage.toLowerCase();

      // Skip normal block reasons
      if (errLower === 'noposition' || errLower === 'alreadyinposition') {
        return null;
      }

      if (errLower.includes('maxtrades') || errLower.includes('maxtradesperday')) {
        status = 'PAUSE';
        reason = 'maxTradesPerDay';
      } else if (errLower.includes('profit') && !errLower.includes('factor')) {
        status = 'PAUSE';
        reason = 'profitStop';
      } else if (errLower.includes('dailyloss') || errLower.includes('daily loss')) {
        status = 'PAUSE';
        reason = 'maxDailyLoss';
      } else if (errLower.includes('loss') && !errLower.includes('daily')) {
        status = 'PAUSE';
        reason = 'lossStop';
      } else if (errLower.includes('trailing')) {
        status = 'FORCE_SELL';
        reason = 'trailingStop';
      } else if (errLower.includes('manual')) {
        status = 'PAUSE';
        reason = 'manualStop';
      }


      if (!status) {
        return {
          status: 'error',
          reason: 'tradeRejected',
          color: 'error',
          icon: <WarningAmber fontSize="small" />,
          label: 'Trade Rejected',
          description: bot.errorMessage,    // shows the full reason
          guardAt: bot.errorAt,
          guardSide: bot.errorSide,
        };
      }


      // If none match, don't show alert
      if (!status) return null;
    }

    // No alert needed for normal states
    if (!status || status === 'IDLE' || status === 'ALLOW') {
      return null;
    }

    const statusConfig = {
      error: {
        color: 'error',
        icon: <WarningAmber fontSize="small" />,
        reasons: {},  // dynamic — uses errorMessage directly
      },
      PAUSE: {
        color: 'warning',
        icon: <Schedule fontSize="small" />,
        reasons: {
          profitStop: { label: 'Profit Target Reached', description: 'Take profit threshold hit. Trading paused.' },
          lossStop: { label: 'Stop Loss Triggered', description: 'Maximum loss threshold reached.' },
          maxDailyLoss: { label: 'Daily Loss Limit', description: 'Maximum daily loss reached. Resumes tomorrow.' },
          maxTradesPerDay: { label: 'Daily Trade Limit Reached', description: 'Maximum trades per day reached. Resumes at midnight UTC.' },
          manualStop: { label: 'Manually Paused', description: 'Bot was manually paused by user.' },
        }
      },
      FORCE_SELL: {
        color: 'error',
        icon: <TrendingDown fontSize="small" />,
        reasons: {
          trailingStop: { label: 'Trailing Stop Hit', description: 'Price dropped below trailing stop. Position closed.' },
          lossStop: { label: 'Stop Loss Executed', description: 'Stop loss triggered. Position closed.' },
        }
      },
    };

    const config = statusConfig[status];
    if (!config) return null; // Unknown status - don't show

    const reasonInfo = config.reasons[reason] || {
      label: status === 'error' ? 'Trade Error' : formatReasonLabel(reason || status),
      description: status === 'error'
        ? (bot.errorMessage || `Trading error: ${formatReasonLabel(reason)}`)
        : `Trading ${status.toLowerCase()}: ${formatReasonLabel(reason)}`
    };

    return {
      status,
      reason,
      color: config.color,
      icon: config.icon,
      label: reasonInfo.label,
      description: reasonInfo.description,
      guardAt: bot.guardAt,
      guardPrice: bot.guardPrice,
      guardSide: bot.guardSide,
    };
  }

  // Helper to format camelCase/snake_case to readable text
  function formatReasonLabel(str) {
    if (!str) return '';
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^\s+/, '')
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }




  const filteredSymbolOptions = useMemo(() => {
    const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
    const exchangeName = selectedExchange?.exchange || selectedExchange?.name || newBot.exchangeId;

    let all = [];
    if (newBot.marketType === 'futures') {
      const marketType = newBot.futuresType === 'coin_m' ? 'coinm_futures' : 'usdm_futures';
      const cacheKey = `${exchangeName}:${marketType}`;
      all = futuresSymbolsByExchange[cacheKey] || [];
      // Only show TRADING symbols for futures
      all = all.filter(s => !s.status || s.status === 'TRADING');
    } else {
      const cacheKey = exchangeName;
      all = symbolsByExchange[cacheKey] || [];
    }

    if (!symbolSearch) return all;
    const q = symbolSearch.trim().toUpperCase().replace('/', '');
    return all.filter(s => {
      const sym = (s.symbol || '').toUpperCase();
      return sym.includes(q) || sym.replace('/', '').includes(q);
    });
  }, [symbolsByExchange, futuresSymbolsByExchange, newBot.exchangeId, newBot.marketType, newBot.futuresType, symbolSearch, userExchanges]);

  async function cancelBacktest() {
    const jobId = liveBacktest?.jobId;
    if (!jobId) return;
    try {
      setIsCanceling(true);
      setConfirmCancelOpen(false); // close the confirm dialog immediately

      await backtestService.cancel(jobId);

      // Immediately reflect in UI; the worker/status key will confirm shortly
      setLiveBacktest(prev => prev ? { ...prev, status: 'canceled' } : prev);
      setIsBacktesting(false);

      // Optional: stop listening right away
      socket.emit('leaveBacktest', jobId);
      detachBacktestListeners(jobId);
    } catch (err) {
      console.error('Cancel backtest failed:', err);
      alert('Failed to cancel. Check server logs.');
    } finally {
      setIsCanceling(false);
    }
  }

  async function handleDeleteSavedBacktest() {
    if (!viewingSavedBacktestId) return;
    try {
      setDeleting(true);
      const userId = Number(localStorage.getItem('userId')); // optional ownership
      await backtestService.deleteResult(viewingSavedBacktestId, userId);
      setConfirmDeleteOpen(false);
      closeBacktestDialog();
      await fetchRecentBacktests();
    } catch (err) {
      console.error('Delete backtest failed:', err);
      alert(err?.response?.data?.error || 'Failed to delete this backtest.');
    } finally {
      setDeleting(false);
    }
  }
  async function openSavedBacktest(btRow) {
    try {
      setViewingSavedBacktestId(btRow.id);
      setBacktestDialogOpen(true);
      setLoadingSavedBacktest(true);

      const detail = await backtestService.getDetail(btRow.id);
      const { metrics, trades, equityCurve } = normalizeBacktestDetail(detail || btRow);
      const savedCfg =
        safeParseMaybe(detail?.backtest_config) ||
        safeParseMaybe(detail?.config) ||
        safeParseMaybe(btRow?.backtest_config) ||
        safeParseMaybe(btRow?.config) ||
        null;

      const botId = btRow.bot_id ?? detail.bot_id;
      const found = bots.find(b => b.id === botId);

      // pull strategy from the saved record
      const savedStrategy = (() => {
        try { return typeof detail?.strategy === 'string' ? JSON.parse(detail.strategy) : (detail?.strategy || {}); }
        catch { return {}; }
      })();

      const matchBot = found || {
        id: botId,
        name: btRow.bot_name || `Bot #${botId ?? '—'}`,
        symbol: btRow.symbol ?? detail.symbol,
        interval: (btRow.timeframe ?? detail.timeframe ?? '').toUpperCase(),
        // ✅ ensure logic exists so future runs don’t crash
        logic: {
          buy: savedStrategy.buy || '',
          sell: savedStrategy.sell || '',
        },
      };

      const bounds = computeBacktestBounds(matchBot); // ✅ define bounds before using it


      setSelectedBotForBacktest(matchBot);
      setBacktestResults({ metrics, trades, equityCurve, backtest_config: savedCfg });
      // use saved range if present (works with both *_ms and ISO)
      const startMs =
        btRow.start_time_ms ?? detail.start_time_ms ??
        (btRow.start_time ? Date.parse(btRow.start_time) : undefined) ??
        (detail.start_time ? Date.parse(detail.start_time) : undefined) ??
        backtestDateRange[0];

      const endMs =
        btRow.end_time_ms ?? detail.end_time_ms ??
        (btRow.end_time ? Date.parse(btRow.end_time) : undefined) ??
        (detail.end_time ? Date.parse(detail.end_time) : undefined) ??
        backtestDateRange[1];

      let s = Number.isFinite(startMs) ? startMs : defaultBacktestRange()[0];
      let e = Number.isFinite(endMs) ? endMs : defaultBacktestRange()[1];

      s = Math.max(bounds.min, s);
      e = Math.min(bounds.max, e);
      if (s > e) { e = bounds.max; s = Math.max(bounds.min, e - 30 * MS_DAY); }

      setBacktestDateRange([s, e]);
      setActiveBacktestTab('summary');           // open on Summary
      setIsBacktesting(false);                   // ensure progress UI is off
      setBacktestProgress(100);                  // show as complete
      setBacktestDialogOpen(true);               // open the dialog
    } catch (err) {
      console.error('Failed to open saved backtest:', err);
      alert('Could not load this backtest. Please check server logs.');
    } finally {
      setLoadingSavedBacktest(false);
    }
  }



  // Handle backtest simulation
  const runBacktest = async (botArg) => {
    // choose explicit bot arg or fall back to the currently selected one
    const bot = botArg || selectedBotForBacktest;

    if (!bot) {
      alert('Please select a bot to backtest.');
      return;
    }

    setViewingSavedBacktestId(null);   // live run, not a saved record
    setIsBacktesting(true);
    setBacktestProgress(0);
    setSelectedBotForBacktest(bot);    // ✅ use the right variable
    setLiveBacktest(null);             // reset stream snapshot

    try {
      const userId = Number(localStorage.getItem("userId"));

      // Prefer canonical bot from state (has .logic), else use what we got
      const canonical = bot?.id ? (bots.find(b => b.id === bot.id) || bot) : bot;

      const getStr = (v) => (v == null ? '' : String(v));
      const buyLogic = getStr(canonical?.logic?.buy ?? canonical?.buy_logic ?? '');
      const sellLogic = getStr(canonical?.logic?.sell ?? canonical?.sell_logic ?? '');

      if (!buyLogic && !sellLogic) {
        setIsBacktesting(false);
        alert('This selection has no strategy logic to backtest.');
        return;
      }

      const symbol = canonical?.symbol ?? selectedBotForBacktest?.symbol ?? '';
      const timeframe = getStr(canonical?.interval ?? canonical?.timeframe ?? selectedBotForBacktest?.interval).toLowerCase();

      // 🔒 Clamp the requested range to the computed bounds for this bot
      const bounds = computeBacktestBounds(canonical);
      let s = Math.max(bounds.min, startOfDayMs(backtestDateRange[0]));
      let e = Math.min(bounds.max, endOfDayMs(backtestDateRange[1]));
      if (s > e) { e = bounds.max; s = Math.max(bounds.min, e - 30 * MS_DAY); }


      const ti = (() => {
        try { return JSON.parse(canonical?.trade_info || '{}'); } catch { return {}; }
      })();

      const payload = {
        userId,
        botId: canonical?.id,
        strategy: { buy: buyLogic, sell: sellLogic },
        config: {
          initialCapital: Number(backtestParams.initialCapital) || 0,
          commission: Number(backtestParams.commission) || 0,
          slippage: Number(backtestParams.slippage) || 0,
          startTime: s,
          endTime: e,
        },
        symbol,
        timeframe,
        isTick: timeframe === 'tick' || (buyLogic + sellLogic).toLowerCase().includes('tick'),

        // ✅ ADD THESE — futures context passed to the worker
        marketType: ti.marketType || 'spot',
        leverage: ti.futures?.leverage || 1,
        positionMode: ti.futures?.positionSide || 'one_way',
        futuresType: ti.futures?.type || 'usdt_m',
        marginMode: ti.futures?.marginMode || 'cross',
      };

      const { jobId } = await backtestService.submit(payload);

      // create immediate UI feedback: queued state
      setLiveBacktest({
        jobId,
        status: 'queued',
        progress: 0,
        equityCurve: [],
        trades: [],
        startedAt: Date.now(),
        lastEventTs: Date.now(),
      });

      // Optional: open the dialog instantly if you prefer the dialog UX
      // setBacktestDialogOpen(true);

      if (!jobId) throw new Error('No jobId returned from backtest submit');

      attachBacktestListeners(jobId);

      // Optional polling fallback
      if (backtestPollRef.current) clearInterval(backtestPollRef.current);
      backtestPollRef.current = setInterval(async () => {
        try {
          const { status, detail } = await backtestService.getStatus(jobId);
          if (status === 'queued') {
            setLiveBacktest(prev => prev ? { ...prev, status: 'queued' } : prev);
          } else if (status === 'running') {
            setLiveBacktest(prev => prev ? { ...prev, status: 'running' } : prev);
          }
          if (status === 'completed') {
            clearInterval(backtestPollRef.current);
            if (detail) {
              setBacktestResults({
                metrics: detail.metrics || {},
                trades: detail.trades || [],
                equityCurve: detail.equity || []
              });
              setIsBacktesting(false);
              setBacktestProgress(100);
              setBacktestDialogOpen(true);
            }
            detachBacktestListeners(jobId);
          } else if (status === 'canceled') {
            clearInterval(backtestPollRef.current);
            detachBacktestListeners(jobId);
            setIsBacktesting(false);
            setLiveBacktest(prev => prev ? { ...prev, status: 'canceled' } : prev);
            return;
          }

          else if (status === 'failed') {
            clearInterval(backtestPollRef.current);
            detachBacktestListeners(jobId);
            alert("Backtest failed. Check logs.");
            setIsBacktesting(false);
          }
        } catch {/* ignore transient errors */ }
      }, 5000);

    } catch (err) {
      console.error("Backtest enqueue failed:", err);
      setIsBacktesting(false);
    }
  };



  // unified close (clear flags + close dialog)
  function closeBacktestDialog() {
    setBacktestDialogOpen(false);
    setViewingSavedBacktestId(null);
    // optional: also clear results
    // setBacktestResults(null);
  }

  const botsById = useMemo(
    () => Object.fromEntries(bots.map(b => [b.id, b.name])),
    [bots]
  );

  const groupedBots = useMemo(() => {
    const manual = [];
    const scannerProfiles = [];
    const spawnedByProfile = new Map();   // profileId → [bots...]
    const orphanedSpawned = [];           // spawned bots whose parent was deleted

    for (const bot of bots) {
      if (bot.isScannerProfile) {
        scannerProfiles.push(bot);
      } else if (bot.spawnedFromProfileId) {
        const arr = spawnedByProfile.get(bot.spawnedFromProfileId) || [];
        arr.push(bot);
        spawnedByProfile.set(bot.spawnedFromProfileId, arr);
      } else {
        manual.push(bot);
      }
    }

    // Sweep up spawned bots whose parent doesn't exist (deleted scanner)
    for (const [profileId, children] of spawnedByProfile.entries()) {
      if (!scannerProfiles.find(p => p.id === profileId)) {
        orphanedSpawned.push(...children);
        spawnedByProfile.delete(profileId);
      }
    }

    return { manual, scannerProfiles, spawnedByProfile, orphanedSpawned };
  }, [bots]);

  const handleBacktestDateChange = (_evt, newValue) => {
    if (!Array.isArray(newValue)) return;
    let [s, e] = newValue;
    s = Math.max(rangeBounds.min, Math.min(s, rangeBounds.max));
    e = Math.max(rangeBounds.min, Math.min(e, rangeBounds.max));
    if (s > e) s = Math.max(rangeBounds.min, e - MS_DAY); // keep at least 1 day
    setBacktestDateRange([s, e]);
  };


  function parseBuySellHistory(raw) {
    try {
      if (typeof raw === 'object') return raw; // Already parsed
      return JSON.parse(raw);
    } catch (err) {
      console.warn("Failed to parse buy/sell history:", raw);
      return null;
    }
  }

  const startOfDayMs = (t) => {
    const d = new Date(t);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const endOfDayMs = (t) => {
    const d = new Date(t);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  };

  const toMsEpoch = (t) => {
    const n = Number(t);
    if (!Number.isFinite(n)) return undefined;
    return n < 1e12 ? n * 1000 : n; // seconds → ms
  };


  function buildSavePayload(bot, results) {
    const userId = Number(localStorage.getItem('userId'));
    return {
      userId,
      botId: bot?.id,
      bot_name: bot?.name || undefined,
      symbol: bot?.symbol,
      timeframe: (bot?.interval || '').toLowerCase(),
      start_time_ms: startOfDayMs(backtestDateRange[0]),
      end_time_ms: endOfDayMs(backtestDateRange[1]),
      strategy: { buy: bot?.logic?.buy, sell: bot?.logic?.sell },
      config: {
        initialCapital: Number(backtestParams.initialCapital) || 0,
        commission: Number(backtestParams.commission) || 0,
        slippage: Number(backtestParams.slippage) || 0,
      },
      metrics: results?.metrics || {},
      equity_curve: results?.equityCurve || [],
      trades: results?.trades || [],
      backtest_config: {
        symbol: bot?.symbol,
        timeframe: (bot?.interval || '').toLowerCase(),
        start_time_ms: startOfDayMs(backtestDateRange[0]),
        end_time_ms: endOfDayMs(backtestDateRange[1]),
        initialCapital: Number(backtestParams.initialCapital) || 0,
        commission: Number(backtestParams.commission) || 0,
        slippage: Number(backtestParams.slippage) || 0,
        strategy: { buy: bot?.logic?.buy, sell: bot?.logic?.sell }
      }
    };
  }
  // NEW: save handler
  async function handleSaveBacktest() {
    try {
      if (!selectedBotForBacktest || !backtestResults) return;
      const payload = buildSavePayload(selectedBotForBacktest, backtestResults);
      await backtestService.saveResult(payload);
      // refresh recent list after saving
      fetchRecentBacktests();
    } catch (err) {
      console.error('Save backtest failed:', err);
      alert('Failed to save backtest. Check logs.');
    }
  }


  // NEW: recent loader
  async function fetchRecentBacktests() {
    try {
      const userId = Number(localStorage.getItem('userId'));
      if (!Number.isFinite(userId)) return;
      const items = await backtestService.getResults(10);
      setRecentBacktests(items || []);
    } catch (err) {
      console.error('Failed to load recent backtests:', err);
    }
  }

  useEffect(() => {
    if (activeTab !== 'backtest') return;
    (async () => {
      const userId = Number(localStorage.getItem('userId'));
      if (!Number.isFinite(userId)) return;

      try {
        const { active, jobId, status, progress } = await backtestService.getActive(userId);
        if (!active || !jobId) return;

        // Seed UI with what we know
        setLiveBacktest(prev => ({
          jobId,
          status: status || 'running',
          progress: Number(progress) || 0,
          equityCurve: prev?.equityCurve ?? [],
          trades: prev?.trades ?? [],
          startedAt: prev?.startedAt ?? Date.now(),
          lastEventTs: Date.now(),
        }));

        setBacktestProgress(Number(progress) || 0);  // 👈 seed the actual progress bar

        // Rejoin the room + attach handlers
        attachBacktestListeners(jobId);

        // Poll as fallback (same poll you already use)
        const pollId = setInterval(async () => {
          try {
            const { status: s, detail } = await backtestService.getStatus(jobId);
            if (s === 'completed' || s === 'canceled' || s === 'failed') {
              clearInterval(pollId);
              detachBacktestListeners(jobId);
              setIsBacktesting(false);
              if (s === 'completed' && detail) {
                setBacktestResults({
                  metrics: detail.metrics || {},
                  trades: detail.trades || [],
                  equityCurve: detail.equity || []
                });
                setBacktestDialogOpen(true);
              }
              setLiveBacktest(prev => prev ? { ...prev, status: s } : prev);
            } else {
              setLiveBacktest(prev => prev ? { ...prev, status: s || prev.status } : prev);
            }
          } catch { }
        }, 5000);

        // Show the running section
        setIsBacktesting(true);
      } catch (e) {
        console.warn('active backtest lookup failed', e);
      }
    })();
  }, [activeTab]);


  useEffect(() => {
    if (activeTab === 'backtest') {
      fetchRecentBacktests();
    }
  }, [activeTab]);


  // 🔄 Replace your existing attachBacktestListeners with this version
  function attachBacktestListeners(jobId) {
    const channel = `backtest:events:${jobId}`;

    // 1) JOIN the room so the server will deliver roomed emits
    socket.emit('joinBacktest', jobId);

    // 2) Optional: raw logger for visibility during dev
    const rawLogger = (...args) => console.log('[RAW BACKTEST]', channel, ...args);
    socket.on(channel, rawLogger);

    // 3) Main handler (normalizes shapes + updates UI state)
    const handler = async (event) => {
      // Ensure container exists
      setLiveBacktest((prev) => {
        if (!prev || prev.jobId !== jobId) {
          return {
            jobId,
            progress: 0,
            equityCurve: [],
            trades: [],
            startedAt: Date.now(),
            lastEventTs: event.ts,
          };
        }
        return prev;
      });

      switch (event.type) {
        case 'start': {
          setBacktestProgress(0);
          setLiveBacktest(prev => prev ? { ...prev, status: 'running', lastEventTs: event.ts } : prev);

          break;
        }

        case 'progress': {
          const pctRaw = event.progress ?? event.percent;
          const pct = Math.max(0, Math.min(100, Number(pctRaw) || 0));
          setBacktestProgress(pct);
          setLiveBacktest(prev =>
            prev ? { ...prev, status: 'running', progress: pct, lastEventTs: event.ts } : prev
          );
          break;
        }

        case 'equity': {
          // Normalize to [t, e] tuples
          if (Array.isArray(event.points)) {
            const normalized = event.points
              .map((p) => (Array.isArray(p)
                ? [toMs(p[0]), Number(p[1])]
                : [toMs(p.t ?? p.time ?? p.ts), Number(p.v ?? p.equity)]
              ))
              .filter(([t, e]) => Number.isFinite(t) && Number.isFinite(e));

            setLiveBacktest((prev) =>
              prev ? { ...prev, equityCurve: [...prev.equityCurve, ...normalized], lastEventTs: event.ts } : prev
            );
          } else {
            const t = toMs(event.time ?? event.ts);
            const e = Number(event.equity);
            if (Number.isFinite(t) && Number.isFinite(e)) {
              setLiveBacktest((prev) =>
                prev ? { ...prev, equityCurve: [...prev.equityCurve, [t, e]], lastEventTs: event.ts } : prev
              );
            }
          }
          break;
        }

        case 'trade': {
          if (event.botData && Array.isArray(event.botData.history)) {
            const parsed = event.botData.history.map((h) => {
              let payload = h.buy_sell_history;
              try { payload = JSON.parse(payload); } catch { }
              return {
                time: toMs(h.date),
                side: payload.side,
                price: payload.price,
                qty: payload.quantity,
                pnl: Number.isFinite(payload.proceeds) && Number.isFinite(payload.cost)
                  ? (Number(payload.proceeds) - Number(payload.cost))
                  : undefined,
              };
            });
            setLiveBacktest((prev) =>
              prev ? { ...prev, trades: [...prev.trades, ...parsed], lastEventTs: event.ts } : prev
            );
          } else if (event.trade) {
            setLiveBacktest((prev) =>
              prev ? {
                ...prev,
                trades: [...prev.trades, {
                  ...event.trade,
                  time: toMs(event.trade.time ?? event.trade.ts ?? event.ts)
                }],
                lastEventTs: event.ts
              } : prev);
          }
          break;
        }

        case 'status': {
          // server might emit { type:'status', status:'canceled'|'running'|'queued'... }
          if (event.status === 'canceled') {
            setBacktestProgress((p) => (p < 100 ? p : 100));
            setIsBacktesting(false);
            setLiveBacktest(prev => prev ? { ...prev, status: 'canceled', lastEventTs: event.ts } : prev);
            socket.emit('leaveBacktest', jobId);
            detachBacktestListeners(jobId);
          }
          break;
        }

        case 'canceled': { // if you emit a dedicated event
          setIsBacktesting(false);
          setLiveBacktest(prev => prev ? { ...prev, status: 'canceled', lastEventTs: event.ts } : prev);
          socket.emit('leaveBacktest', jobId);
          detachBacktestListeners(jobId);
          break;
        }


        case 'trades': {
          // batch append; server sends { type:'trades', trades:[...] }
          const list = Array.isArray(event.trades) ? event.trades : [];
          if (list.length) {
            setLiveBacktest(prev =>
              prev
                ? { ...prev, trades: [...prev.trades, ...list.map(tr => ({ ...tr, time: toMs(tr.time ?? tr.t ?? tr.timestamp ?? event.ts) }))], lastEventTs: event.ts }
                : prev
            );
          }
          break;
        }

        case 'complete': {
          setBacktestProgress(100);
          setIsBacktesting(false);

          // prefer server’s final payload
          const normTime = (t) => toMs(t ?? event.ts);
          const tradesFromEvent = Array.isArray(event?.trades)
            ? event.trades.map(t => ({ ...t, time: normTime(t.time ?? t.t ?? t.timestamp) }))
            : [];
          const equityFromEvent = Array.isArray(event?.equity) ? event.equity : [];

          // take a synchronous snapshot of current live stream
          const current = liveBacktestRef.current;

          const tradesFromStream = Array.isArray(current?.trades) ? current.trades : [];
          const equityFromStream = Array.isArray(current?.equityCurve) ? current.equityCurve : [];

          const tradesFinal = tradesFromEvent.length ? tradesFromEvent : tradesFromStream;
          const equityFinal = equityFromEvent.length ? equityFromEvent : equityFromStream;

          const mergedMetrics = { ...(event?.metrics || {}) };
          mergedMetrics.totalTrades = Number.isFinite(mergedMetrics.totalTrades)
            ? mergedMetrics.totalTrades
            : tradesFinal.length;

          // update UI state
          setLiveBacktest(prev =>
            prev ? { ...prev, status: 'completed', progress: 100, lastEventTs: event.ts } : prev
          );
          setBacktestResults({ metrics: mergedMetrics, trades: tradesFinal, equityCurve: equityFinal });

          // persist (fire-and-forget)
          try {
            const payload = buildSavePayload(
              selectedBotForBacktest,
              { metrics: mergedMetrics, trades: tradesFinal, equityCurve: equityFinal }
            );
            backtestService.saveResult(payload)
              .then(() => fetchRecentBacktests())
              .catch(err => console.error('[backtest] save FAILED', err));
          } catch (err) {
            console.error('[backtest] save exception', err);
          }

          setBacktestDialogOpen(true);
          socket.emit('leaveBacktest', jobId);
          detachBacktestListeners(jobId);
          break;
        }


        case 'error': {
          console.error('[backtest:error]', event);
          setIsBacktesting(false);
          socket.emit('leaveBacktest', jobId);
          detachBacktestListeners(jobId);
          setLiveBacktest(prev => prev ? { ...prev, status: 'error', lastEventTs: event.ts } : prev);

          break;
        }

        default:
          break;
      }
    };

    socket.on(channel, handler);

    // Store BOTH handlers so we can off() both later
    backtestListenersRef.current.set(jobId, { channel, handlers: [handler, rawLogger] });
  }

  function formatProfitLikeChipInput(trade) {
    const profitVal = trade?.profit ?? trade?.pnl ?? null; // string like "+3.2%" or number

    if (profitVal == null) {
      return { text: '—', isLoss: false };
    }

    if (typeof profitVal === 'string') {
      const trimmed = profitVal.trim();
      const isLoss = trimmed.startsWith('-') || Number(trimmed.replace(/[%,$]/g, '')) < 0;
      return { text: trimmed, isLoss };
    }

    // numeric pnl
    const n = Number(profitVal);
    if (!Number.isFinite(n)) return { text: '—', isLoss: false };
    const sign = n >= 0 ? '+' : '-';
    return { text: `${sign}$${Math.abs(n).toFixed(2)}`, isLoss: n < 0 };
  }


  function detachBacktestListeners(jobId) {
    const rec = backtestListenersRef.current.get(jobId);
    if (!rec) return;
    for (const h of rec.handlers || []) {
      socket.off(rec.channel, h);
    }
    backtestListenersRef.current.delete(jobId);
  }



  const extractIndicatorsFromLogic = (logicStr) => {
    const regex = /(tickRSI|tickVWAP|tickPrice|tickDelta|tickVolume|tickTradeVolume|SMA|EMA|RSI|MACD|Volume)\s*\(?\s*(\d+)?\s*,?\s*["']?(\w+)?["']?\s*\)?/g;

    const indicators = new Set();
    let match;
    while ((match = regex.exec(logicStr)) !== null) {
      const name = match[1];
      const period = parseInt(match[2]) || 0;
      const source = match[3] || 'tick';

      indicators.add(JSON.stringify({ name, period, source }));
    }

    return Array.from(indicators).map(s => JSON.parse(s));
  };

// Direct-submit path for the duplicate-bot retry. Skips all the form
  // validation in handleSave because the payload was already built and
  // validated on the first attempt — we just need to re-send it with
  // confirm_duplicate=true to bypass the server's soft-warning check.
  const submitBotPayloadDirect = async (payload, stopped, selectedExchange) => {
    try {
      const resp = await botApiV2.createBot(payload);
      const raw = resp.bot ?? resp;

      let tiSafe = {};
      try { tiSafe = JSON.parse(raw.trade_info || '{}'); } catch (_) { }

      const webhookSecret = (tiSafe.webhook?.secret || '').trim();

      const formattedBot = {
        id: raw.id,
        name: raw.name,
        symbol: tiSafe.symbol || '(from webhook)',
        interval: tiSafe.timeframe || (tiSafe.signalSource === 'tradingview' ? 'Webhook' : '—'),
        exchangeId: raw.exchange_id ?? raw.exchangeId ?? newBot.exchangeId ?? null,
        exchange: raw.exchange?.exchange || selectedExchange?.exchange || '',
        exchangeName: raw.exchange?.name || raw.exchange?.exchange || '—',
        trade_info: raw.trade_info,
        status: raw.stop_trade_ASSET ? 'paused' : 'active',
        stop_trade_ASSET: raw.stop_trade_ASSET,
        logic: {
          buy: raw.buy_logic,
          sell: raw.sell_logic,
          shortOpen: tiSafe.futures?.shortOpenLogic || '',
          shortClose: tiSafe.futures?.shortCloseLogic || '',
          profitStop: tiSafe.profitStop,
          lossStop: tiSafe.lossStop,
          maxLoss: tiSafe.maxDailyLoss,
          maxTrades: tiSafe.maxTradesPerDay,
          trailingStop: tiSafe.trailingStop,
          investment: tiSafe.totalInvestment,
        },
        stats: raw.statistics ? normalizeBotStats(raw.statistics) : undefined,
        statusMessage: raw.status || '',
        errorMessage: raw.error_message || '',
        signalSource: tiSafe.signalSource || 'logic',
        webhookUrl: tiSafe.signalSource === 'tradingview'
          ? buildTradingViewWebhookUrl(raw.id, webhookSecret)
          : '',
      };

      const message = stopped
        ? `Bot "${formattedBot.name}" created (paused). Activate it when ready.`
        : resp?.message || 'Bot created and activated!';
      showToast('success', message);

      setBots(prev => [...prev, formattedBot]);
      resetForm();
      setIsCreating(false);
      setActiveTab('bots');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to create bot.';
      showToast('error', msg);
      console.error('Error saving bot (duplicate retry):', err);
    }
  };

  const handleSave = async (stopped = false) => {    const email = getEmail();
    if (!email) {
      alert('No email found (localStorage.email). Please log in again.');
      return;
    }

    // ── Basic required fields ──────────────────────────────────────────────────
    if (!newBot.name || !newBot.exchangeId) {
      alert('Name and exchange are required.');
      return;
    }

if (newBot.signalSource !== 'tradingview' && !newBot.symbol) {
      alert('Symbol is required for logic-based bots.');
      return;
    }

    // Funding asset is always required (balance source for bot capital & P&L)
    if (!newBot.funding_asset || !String(newBot.funding_asset).trim()) {
      alert('Funding asset is required.');
      return;
    }

    // ── Position size validation (spot vs futures) ────────────────────────────
    const isFutures = newBot.marketType === 'futures';
    // hasSize check
    const hasSize = isFutures
      ? (newBot.futureSizeMode === 'fixed_notional'
        ? Number(newBot.fixedNotional) > 0
        : Number(newBot.marginPct) > 0)
      : Number(newBot.tradeAmount) > 0;  // ← was positionValue

    // saveDisabled check  
    

    if (!hasSize) {
      alert('Position Size must be greater than 0.');
      return;
    }

    // ── BETA CAP — bot notional limited to $1000 USDT ─────────────────────────
    const BETA_BOT_CAP_USDT = 1000;
    let notionalUsdt = 0;

    if (newBot.marketType === 'futures') {
      const leverage = Number(newBot.leverage) || 1;
      if (newBot.futureSizeMode === 'fixed_notional') {
        notionalUsdt = Number(newBot.fixedNotional) || 0;
      } else {
        // margin_pct: can't compute exact notional without account balance.
        // Block this mode during beta — force users to fixed_notional.
        alert(
          'Beta limit: percentage-based margin sizing is disabled. ' +
          'Please switch to "Fixed Notional" sizing and set a value up to $1000.'
        );
        return;
      }
    } else {
      // Spot
      const sizeValue = Number(newBot.tradeAmount || newBot.positionValue) || 0;
      const sizeMode = newBot.sizeMode || 'quote';
      if (sizeMode === 'quote') {
        notionalUsdt = sizeValue;
      } else {
        // base mode — convert via spot price snapshot if you have one
        // For client-side only, we'll let the server enforce. Estimate optimistically.
        notionalUsdt = 0; // server validates the precise notional
      }
    }

    if (notionalUsdt > BETA_BOT_CAP_USDT) {
      alert(
        `Beta limit: bot position size cannot exceed $${BETA_BOT_CAP_USDT} USDT notional. ` +
        `Current: $${notionalUsdt.toFixed(0)}. ` +
        (newBot.marketType === 'futures'
          ? 'Reduce notional or leverage.'
          : 'Reduce position size.')
      );
      return;
    }

    // ── Logic validation (non-webhook only) ───────────────────────────────────

    // ── Logic validation (non-webhook only) ───────────────────────────────────
    if (newBot.signalSource !== 'tradingview') {
      const isHedge = isFutures && newBot.positionSide === 'hedge';
      const isStockHedgeMode = isStockEquity && newBot.allowShorts;
      const hasLogic = (isHedge || isStockHedgeMode)
        ? (buyConditions.length || sellConditions.length ||
          shortOpenConditions.length || shortCloseConditions.length)
        : (buyConditions.length || sellConditions.length);

      if (!hasLogic) {
        alert('Please add at least one condition.');
        return;
      }
    }

    // ── Futures validation ────────────────────────────────────────────────────
    if (isFutures) {
      const futuresErrors = validateFuturesConfig(newBot, userExchanges);
      if (futuresErrors.length > 0) {
        alert(futuresErrors.join('\n'));
        return;
      }
    }

    // ── Resolve assets ────────────────────────────────────────────────────────
    const userId = parseInt(localStorage.getItem('userId'), 10);
    const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
    const isHedge = isFutures && newBot.positionSide === 'hedge';

    const { base: BASE, quote: QUOTE } = getPairMeta({
      symbol: newBot.symbol,
      exchangeId: newBot.exchangeId,
      userExchanges,
      symbolsByExchange,
    });

    // ── Spot sizing — normalize field names ──────────────────────────
    const spotValue = newBot.positionValue || newBot.tradeAmount || 0;
    const spotMode = newBot.sizeMode || newBot.tradeMode || 'quote';

    // ── Order sizing (spot only — futures uses marginPct/fixedNotional) ────────
    const sizing = isFutures
      ? {
        mode: newBot.futureSizeMode || 'margin_pct',
        value: newBot.futureSizeMode === 'fixed_notional'
          ? Number(newBot.fixedNotional) || 0
          : Number(newBot.marginPct) || 0,
        assetType: newBot.futureSizeMode || 'margin_pct',
        assetSymbol: QUOTE,
        base_asset: newBot.baseAsset || BASE,
        base_precision: newBot.basePrecision ?? null,
        quote_asset: newBot.quoteAsset || QUOTE,
        quote_precision: newBot.quotePrecision ?? null,
        margin_asset: newBot.marginAsset ?? null,
      }
      : (() => {
        // ✅ PositionSizeSection always uses tradeAmount in BASE asset
        const s = buildOrderSizing(
          newBot.symbol,
          newBot.sizeMode || 'base', // ← respects toggle
          newBot.tradeAmount || 0,
          BASE, QUOTE
        );
        s.base_asset = newBot.baseAsset || BASE;
        s.base_precision = newBot.basePrecision ?? null;
        s.quote_asset = newBot.quoteAsset || QUOTE;
        s.quote_precision = newBot.quotePrecision ?? null;
        return s;
      })();

    // ── Build logic strings ───────────────────────────────────────────────────
    const buyLogicFinal = newBot.signalSource === 'tradingview'
      ? 'WEBHOOK_SIGNAL'
      : buildLogicFromConditions(buyConditions);

    const sellLogicFinal = newBot.signalSource === 'tradingview'
      ? 'WEBHOOK_SIGNAL'
      : buildLogicFromConditions(sellConditions);

    const shortOpenLogicFinal = isHedge
      ? buildLogicFromConditions(shortOpenConditions)
      : '';

    const shortCloseLogicFinal = isHedge
      ? buildLogicFromConditions(shortCloseConditions)
      : '';

    // ── Assemble tradeInfo ────────────────────────────────────────────────────
    const tradeInfo = {
      timeframe: newBot.signalSource === 'tradingview' ? '' : newBot.interval,
      symbol: newBot.symbol || '',
      exchange: selectedExchange?.exchange || '',

      // Spot investment (kept for backwards compat)
      totalInvestment: !isFutures && sizing.mode === 'quote' ? sizing.value : '',

      // Risk management
      profitStop: profitStop + '%',
      lossStop: lossStop + '%',
      maxDailyLoss: newBot.maxLoss,
      maxTradesPerDay: newBot.maxTrades,
      trailingStop: newBot.trailingStop,

      // Sizing
      mode: sizing.mode,
      value: Number(sizing.value) || 0,
      asset_symbol: sizing.assetSymbol,
      orderSizing: sizing,

      // Indicators
      indicators: newBot.signalSource === 'tradingview'
        ? []
        : extractIndicatorsFromConditions(
          buyConditions,
          sellConditions,
     ...((isHedge || (isStockEquity && newBot.allowShorts))
       ? [shortOpenConditions, shortCloseConditions]
       : []),
        ),

      // Signal source
      signalSource: newBot.signalSource,
      webhook: {
        enabled: newBot.signalSource === 'tradingview',
        provider: 'tradingview',
        secret: (newBot.webhookSecret || '').trim(),
        actionField: newBot.webhookActionField || 'action',
        buyValue: newBot.webhookBuyValue || 'BUY',
        sellValue: newBot.webhookSellValue || 'SELL',
      },

      // Market type
      marketType: newBot.marketType,

      // Futures config
      ...(isFutures && {
        futures: {
          type: newBot.futuresType,                    // 'usdt_m' | 'coin_m'
          leverage: Number(newBot.leverage) || 1,
          marginMode: newBot.marginMode,               // 'cross' | 'isolated'
          positionSide: newBot.positionSide,           // 'one_way' | 'hedge'
          reduceOnly: newBot.reduceOnly || false,

          // Position sizing
          sizeMode: newBot.futureSizeMode || 'margin_pct',
          marginPct: Number(newBot.marginPct) || 0,
          fixedNotional: Number(newBot.fixedNotional) || 0,
          maxConcurrentTrades: 1,

          // Hedge mode logic
          ...(isHedge && {
            shortOpenLogic: shortOpenLogicFinal,
            shortCloseLogic: shortCloseLogicFinal,
          }),
        },
      }),

      // Order execution
       orderExecution: {
        mode: newBot.orderExecution || 'market',
        limitOffsetPct: Number(newBot.limitOffsetPct) || 0.05,
        limitTimeoutSec: Number(newBot.limitTimeoutSec) || 10,
        postOnly: newBot.postOnly || false,
      },

// Phase B — asset class + stock-specific extras
      asset_class: assetClass,

      // Funding asset — balance source for bot capital & P&L.
      // Same field name & shape as scanner profiles for backend consistency.
      funding_asset: newBot.funding_asset || defaultFundingAssetForClass(assetClass),

      ...(isStockEquity && {
        alpaca: {
          extended_hours: !!newBot.extendedHours,
        },
        // Stock hedge: short logic uses the same condition arrays as futures hedge.
        // We treat allowShorts as the stock equivalent of positionSide=hedge.
        ...(newBot.allowShorts && {
          short_open_logic: buildLogicFromConditions(shortOpenConditions),
          short_close_logic: buildLogicFromConditions(shortCloseConditions),
        }),
      }),
    };

    // ── API payload ───────────────────────────────────────────────────────────
    const isStockHedge = isStockEquity && newBot.allowShorts;
    const stockShortOpen = isStockHedge ? buildLogicFromConditions(shortOpenConditions) : '';
    const stockShortClose = isStockHedge ? buildLogicFromConditions(shortCloseConditions) : '';

    const v2Payload = {
      userId,
      name: newBot.name,
      exchange_id: newBot.exchangeId,
      buy_logic: buyLogicFinal,
      sell_logic: sellLogicFinal,
      ...((isHedge || isStockHedge) && {
        short_open_logic: isHedge ? shortOpenLogicFinal : stockShortOpen,
        short_close_logic: isHedge ? shortCloseLogicFinal : stockShortClose,
      }),
trade_info: JSON.stringify(tradeInfo),
      asset_class: assetClass,   // ← mirror trade_info.asset_class to the column
      funding_asset: newBot.funding_asset || defaultFundingAssetForClass(assetClass),  // mirror to top-level (matches asset_class pattern)
      INIT_PRICE_ASSET: 100.0,
      stop_trade_ASSET: stopped,
      status: stopped ? 'paused' : '',
      error_message: '',
    };

    // ── Submit ────────────────────────────────────────────────────────────────

    const isRetry = v2Payload._isDuplicateRetry === true;
    if (isRetry) delete v2Payload._isDuplicateRetry;  // strip our local-only flag

    try {
      const resp = await botApiV2.createBot(v2Payload);
      const raw = resp.bot ?? resp;

      let tiSafe = {};
      try { tiSafe = JSON.parse(raw.trade_info || '{}'); } catch (_) { }

      const webhookSecret = (tiSafe.webhook?.secret || '').trim();

      const formattedBot = {
        id: raw.id,
        name: raw.name,
        symbol: tiSafe.symbol || '(from webhook)',
        interval: tiSafe.timeframe || (tiSafe.signalSource === 'tradingview' ? 'Webhook' : '—'),
        exchangeId: raw.exchange_id ?? raw.exchangeId ?? newBot.exchangeId ?? null,
        exchange: raw.exchange?.exchange || selectedExchange?.exchange || '',
        exchangeName: raw.exchange?.name || raw.exchange?.exchange || '—',
        trade_info: raw.trade_info,
        status: raw.stop_trade_ASSET ? 'paused' : 'active',
        stop_trade_ASSET: raw.stop_trade_ASSET,
        logic: {
          buy: raw.buy_logic,
          sell: raw.sell_logic,
          shortOpen: tiSafe.futures?.shortOpenLogic || '',
          shortClose: tiSafe.futures?.shortCloseLogic || '',
          profitStop: tiSafe.profitStop,
          lossStop: tiSafe.lossStop,
          maxLoss: tiSafe.maxDailyLoss,
          maxTrades: tiSafe.maxTradesPerDay,
          trailingStop: tiSafe.trailingStop,
          investment: tiSafe.totalInvestment,
        },
        stats: raw.statistics ? normalizeBotStats(raw.statistics) : undefined,
        statusMessage: raw.status || '',
        errorMessage: raw.error_message || '',
        signalSource: tiSafe.signalSource || 'logic',
        webhookUrl: tiSafe.signalSource === 'tradingview'
          ? buildTradingViewWebhookUrl(raw.id, webhookSecret)
          : '',
      };

      const message = stopped
        ? `Bot "${formattedBot.name}" created (paused). Activate it when ready.`
        : resp?.message || 'Bot created and activated!';
      showToast('success', message);

      setBots(prev => [...prev, formattedBot]);
      resetForm();
      setIsCreating(false);
      setActiveTab('bots');
    } catch (err) {
      // Soft-warning duplicate-bot path. Server returned 409 with a structured
      // payload describing which existing bot conflicts. We surface this as a
      // dialog so the user can confirm and resubmit with confirm_duplicate=true.
      // On the retry, the server skips the dedup check and creates the bot.
      if (
        !isRetry &&
        err?.response?.status === 409 &&
        err?.response?.data?.code === 'DUPLICATE_BOT'
      ) {
        setDuplicateBotConflict({
          conflict: err.response.data.conflict,
          pendingPayload: { ...v2Payload, confirm_duplicate: true, _isDuplicateRetry: true },
          stopped,   // capture so the retry preserves "Save (Paused)" vs "Save & Activate"
        });
        return;  // wait for the user's choice
      }

      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to create bot.';
      showToast('error', msg);
      console.error('Error saving bot:', err);
    }
  };


  const handleNext = () => {
    // Skip Trading Logic step (step 1) for TradingView bots
    if (activeStep === 0 && newBot.signalSource === 'tradingview') {
      setActiveStep(2); // Jump to Risk Management
    } else {
      setActiveStep(s => s + 1);
    }
  };
  async function ensureFuturesSymbols(exchangeId, exchangeName, marketType) {
    const cacheKey = `${exchangeName}:${marketType}`;
    if (!cacheKey || futuresSymbolsByExchange[cacheKey]) return;

    try {
      setSymbolsLoading(true);
      setSymbolsError(null);

      const list = await symbolService.getFutures(exchangeName, marketType);

      if (list.length === 0) {
        setSymbolsError(`No ${marketType} symbols found for ${exchangeName}`);
      }

      const normalized = list.map(item => ({
        symbol: item.symbol || item.class_name || '',
        base_asset: item.base_asset ?? null,
        quote_asset: item.quote_asset ?? null,
        margin_asset: item.margin_asset ?? null,
        pair: item.pair ?? null,
        contract_type: item.contract_type ?? null,
        status: item.status ?? null,
        price_precision: item.price_precision ?? null,
        quantity_precision: item.quantity_precision ?? null,
        market_type: item.market_type ?? marketType,
      }));

      setFuturesSymbolsByExchange(prev => ({ ...prev, [cacheKey]: normalized }));
    } catch (e) {
      console.error('futures symbols fetch failed', e);
      const msg = e?.response?.data?.error || e?.message || 'Failed to load futures symbols';
      setSymbolsError(msg);
      showToast('error', msg);
    } finally {
      setSymbolsLoading(false);
    }
  }

  const handleBack = () => {
    // If on Risk Management and using TradingView, go back to Basic Config
    if (activeStep === 2 && newBot.signalSource === 'tradingview') {
      setActiveStep(0);
    } else {
      setActiveStep(s => s - 1);
    }
  };

  const handleCreate = () => {
    setIsCreating(true);
    setActiveStep(0);
    setActiveTab('create');
  };
  const handleCancelCreate = () => {
    setIsCreating(false);
    resetForm();
  };




  const resetForm = () => {
    setNewBot({
      name: '',
      exchangeId: null,
      interval: '1H',
      symbol: 'BTC/USDT',
      sizeMode: 'quote',
      positionValue: '',
      totalAmount: '',
      maxLoss: '5',
      maxTrades: '10',
      trailingStop: '2',
      signalSource: 'logic',
      webhookSecret: '',
      webhookActionField: 'action',
      webhookBuyValue: 'BUY',
      webhookSellValue: 'SELL',
      // ✅ NEW: Reset futures fields
      marketType: 'spot',
      futuresType: 'usdt_m',
      leverage: 1,
      marginMode: 'cross',
      positionSide: 'one_way',
      shortOpenLogic: '',
      shortCloseLogic: '',
      reduceOnly: false,

      // ✅ NEW: Reset order execution fields
      orderExecution: 'market',
      limitOffsetPct: 0.05,
      limitTimeoutSec: 10,
      postOnly: false,
      futureSizeMode: 'margin_pct',
      marginPct: '',
      fixedNotional: '',
      maxConcurrentTrades: 1,
           marginAsset: undefined,
      baseAsset: undefined,
      quoteAsset: undefined,
      basePrecision: null,
      quotePrecision: null,
      contractType: null,

      // Phase B
// Phase B
      extendedHours: false,
      allowShorts: false,

      // Funding asset reset to crypto default; auto-snaps to USD on Alpaca via effect
      funding_asset: 'USDT',
    });
    setShortOpenConditions([]);
    setShortCloseConditions([]);
    setBuyConditions([]);
    setSellConditions([]);
    setProfitStop('20');
    setLossStop('10');
    setActiveStep(0);
  };

  // Quick Bot: prefill the wizard with a ready-made preset (RSI Reversal on the
  // first exchange) + safe risk defaults and jump to the review step, so a
  // first-timer just confirms and activates. Reuses the normal handleSave path.
  const handleQuickBot = () => {
    if (!userExchanges?.length) {
      setToast({ open: true, severity: 'info', message: 'First create an exchange — opening the demo setup for you…' });
      routerHistory.push('/profile/portfolio?onboard=demo');
      return;
    }
    const ex = userExchanges[0];
    const exchangeName = ex.exchange || ex.name;
    const tpl = SPOT_TEMPLATES[0]; // RSI Reversal — well-known starter strategy
    resetForm();
    setNewBot((prev) => ({
      ...prev,
      name: `Quick Bot — ${tpl.name}`,
      exchangeId: ex.id,
      symbol: 'BTC/USDT',
      interval: '1H',
      signalSource: 'logic',
      orderExecution: 'market',
      sizeMode: 'quote',
      positionValue: '100',
      funding_asset: 'USDT',
    }));
    if (ex.id) ensureSymbols(ex.id, exchangeName);
    setBuyConditions(templateToConditions(tpl.buy));
    setSellConditions(templateToConditions(tpl.sell));
    setProfitStop('20');
    setLossStop('10');
    setIsCreating(true);
    setActiveTab('create');
    setActiveStep(2); // jump to the review/risk step — confirm & activate
  };

  // Welcome-modal / checklist deep-link: ?quickbot=1 launches Quick Bot once.
  // Wait for the exchange list so we don't wrongly redirect a user who has one.
  useEffect(() => {
    if (!exchangesLoaded) return;
    const params = new URLSearchParams(rfLocation.search || '');
    if (params.get('quickbot') === '1' && !quickBotRanRef.current) {
      quickBotRanRef.current = true;
      handleQuickBot();
      routerHistory.replace('/profile/traderform');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangesLoaded, rfLocation.search]);

  const toggleBotStatus = async (botId) => {
    const botToUpdate = bots.find(b => b.id === botId);
    if (!botToUpdate) return;

    const currentlyPaused = botToUpdate.stop_trade_ASSET === true;
    const newStopStatus = !currentlyPaused;

    try {
      await tradingBotService.update(botId, {
        stop_trade_ASSET: newStopStatus,
        error_message: '',
        status: '',
        // ✅ Clear guard fields on resume
        guard_tag: '',
        guard_reason: '',
        guard_at: '',
        guard_side: '',
        guard_price: '',
      });

      setBots(prevBots =>
        prevBots.map(bot =>
          bot.id === botId
            ? {
              ...bot,
              stop_trade_ASSET: newStopStatus,
              status: newStopStatus ? 'paused' : 'active',
              // ✅ Clear guard state locally on resume
              ...(newStopStatus ? {} : {
                guardTag: '',
                guardReason: '',
                guardAt: '',
                guardSide: '',
                guardPrice: '',
                errorMessage: '',
              })
            }
            : bot
        )
      );
    } catch (error) {
      console.error('Failed to toggle bot status:', error);
    }
  };

  const deleteBot = async (id) => {
    try {
      await tradingBotService.delete(id);
      setBots(bots.filter(bot => bot.id !== id));
    } catch (err) {
      console.error('Failed to delete bot:', err);
    }
  };

  // Lazy-load one page of a bot's history (newest-first) and merge it into
  // liveBotHistories. reset=true starts from offset 0 (on expand); otherwise it
  // pages older entries. Deduped + ordered by mergeHistoryEntries, so overlap
  // with socket-fed live entries is harmless.
  const loadBotHistoryPage = async (botId, { reset = false } = {}) => {
    const cur = historyPaging[botId] || { offset: 0, total: null, loading: false, done: false };
    if (cur.loading) return;
    if (!reset && cur.done) return;
    const offset = reset ? 0 : cur.offset;

    setHistoryPaging(prev => ({ ...prev, [botId]: { ...(prev[botId] || {}), offset, loading: true, done: false } }));
    try {
      const res = await tradingBotService.getBotHistory(botId, { limit: HISTORY_PAGE_SIZE, offset });
      const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
      const total = Number.isFinite(res?.total) ? res.total : null;
      const mapped = mapHistoryRows(items);

      setLiveBotHistories(prev => ({ ...prev, [botId]: mergeHistoryEntries(prev[botId] || [], mapped) }));

      const nextOffset = offset + items.length;
      const done = (total != null && nextOffset >= total) || items.length < HISTORY_PAGE_SIZE;
      setHistoryPaging(prev => ({ ...prev, [botId]: { offset: nextOffset, total, loading: false, done } }));
    } catch (err) {
      console.error(`[history] load page failed for bot ${botId}:`, err?.message || err);
      setHistoryPaging(prev => ({ ...prev, [botId]: { ...(prev[botId] || { offset, total: null, done: false }), loading: false } }));
    }
  };

  const toggleExpandBot = (id) => {
    const willExpand = expandedBot !== id;
    setExpandedBot(willExpand ? id : null);
    // On first expand of a bot, fetch its first history page. The socket already
    // seeded the most recent entries; this fills in a full first page and enables
    // "Load more" for older history.
    if (willExpand && !historyPaging[id]) {
      loadBotHistoryPage(id, { reset: true });
    }
  };

  const getBotTransactions = (botId) => {
    return mockTransactions.filter(tx => tx.botId === botId);
  };

  // ─── Bot card renderer (used in both Manual and Scanner tabs) ─────────────
  function renderBotCard(bot) {
    return (
      <Card key={bot.id} elevation={0} sx={{
        bgcolor: AT.card,
        borderRadius: '14px',
        border: `1px solid ${expandedBot === bot.id ? 'rgba(77,143,199,.35)' : AT.hairline}`,
        boxShadow: '0 1px 2px rgba(0,0,0,.22)',
        transition: 'border-color .2s',
        '&:hover': { borderColor: 'rgba(77,143,199,.35)' }
      }}>
        <CardContent sx={{ p: '16px !important' }}>
          {/* ✅ Status Alert Banner */}
          {(() => {
            const statusDisplay = getTradingStatusDisplay(bot);
            if (!statusDisplay) return null;
            return (
              <Alert severity={statusDisplay.color} icon={statusDisplay.icon} sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>{statusDisplay.label}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>{statusDisplay.description}</Typography>
                    {(statusDisplay.guardAt || statusDisplay.guardPrice) && (
                      <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap">
                        {statusDisplay.guardAt && <Typography variant="caption" color="text.secondary"><strong>Since:</strong> {new Date(statusDisplay.guardAt).toLocaleString()}</Typography>}
                        {statusDisplay.guardPrice && <Typography variant="caption" color="text.secondary"><strong>At Price:</strong> ${Number(statusDisplay.guardPrice).toLocaleString()}</Typography>}
                        {statusDisplay.guardSide && <Typography variant="caption" color="text.secondary"><strong>Blocked:</strong> {statusDisplay.guardSide}</Typography>}
                      </Stack>
                    )}
                  </Box>
                </Box>
              </Alert>
            );
          })()}

          {/* Bot Header Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => toggleExpandBot(bot.id)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton size="small">
                {expandedBot === bot.id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>{bot.name}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ExchangeBadge type={bot.exchange} label={bot.exchangeName || bot.exchange} />
                  <FuturesBadge bot={bot} />
                  <OrderTypeBadge bot={bot} />
                  {/* Phase A: stock-equity badges (market hours, PDT, halts) */}
                  <StockStatusBadgeRow
                    bot={bot}
                    accountStatus={getAccountStatusForBot(bot, alpacaStatus)}
                  />

                  {/* Spawned chip */}
                  {bot.spawnedFromProfileId && bot.spawnedFromMeta && (
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="caption" fontWeight={600}>Spawned by {bot.spawnedFromMeta.profile_name}</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>Triggered at ${Number(bot.spawnedFromMeta.trigger_event?.triggered_price || 0).toLocaleString()}</Typography>
                          {bot.spawnedFromMeta.trigger_event?.strength != null && <Typography variant="caption" sx={{ display: 'block' }}>Strength: {Number(bot.spawnedFromMeta.trigger_event.strength).toFixed(2)}</Typography>}
                          {bot.spawnedFromMeta.auto_terminate_at && <Typography variant="caption" sx={{ display: 'block' }}>Auto-terminates: {new Date(bot.spawnedFromMeta.auto_terminate_at).toLocaleString()}</Typography>}
                        </Box>
                      }
                    >
                      <Chip size="small" icon={<ShowChart sx={{ fontSize: 12 }} />} label="Spawned" variant="outlined" sx={{ height: 22, bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#4d8fc7', borderColor: '#4d8fc7', '& .MuiChip-icon': { color: '#4d8fc7' } }} />
                    </Tooltip>
                  )}

                  {bot.signalSource === 'tradingview' && (
                    <Tooltip title="Receives signals from TradingView webhooks">
                      <Chip size="small" icon={<img src="https://www.tradingview.com/static/images/favicon.ico" alt="TV" style={{ width: 14, height: 14 }} />} label="Webhook" variant="outlined" sx={{ height: 22, '& .MuiChip-icon': { ml: 0.5 } }} />
                    </Tooltip>
                  )}

                  <Typography variant="body2" color="text.secondary">{bot.symbol} • {bot.interval}</Typography>
                  {bot.currentPrice && (
                    <Typography variant="body2" color="text.secondary">
                      • {Number(bot.currentPrice).toLocaleString()} {getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange }).quote}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(() => {
                const sevHex = { success: AT.positive, error: AT.negative, warning: AT.amber, info: AT.accent, default: AT.muted };
                const sd = getTradingStatusDisplay(bot);
                const label = sd ? sd.label : (bot.status === 'active' ? 'Active' : (bot.status || 'Unknown'));
                const c = sevHex[sd ? sd.color : (bot.status === 'active' ? 'success' : 'default')] || AT.muted;
                const pill = (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', fontWeight: 600, color: c, background: `${c}1f`, border: `1px solid ${c}59`, px: '12px', py: '6px', borderRadius: '8px', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c, flex: 'none' }} />{label}
                  </Box>
                );
                return sd
                  ? <Tooltip title={<Box><Typography variant="body2" fontWeight={600}>{sd.label}</Typography><Typography variant="caption">{sd.description}</Typography></Box>}>{pill}</Tooltip>
                  : pill;
              })()}

              {bot.signalSource !== 'tradingview' && (() => {
                const check = gateBacktest(bot);
                return (
                  <Tooltip title={check.supported ? 'Run Backtest' : check.message}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); runBacktest(bot); }}
                        color="info"
                        disabled={!check.supported}
                      >
                        <BarChart fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              })()}
              <Tooltip title={bot.status === 'active' ? 'Pause Bot' : 'Resume Bot'}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleBotStatus(bot.id); }} color={bot.status === 'active' ? 'warning' : 'success'}>
                  {bot.status === 'active' ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Collapse in={expandedBot === bot.id}>
            <Box sx={{ mt: 3, pl: 2, pr: 1 }}>
              {(() => {
                const ti = (() => { try { return JSON.parse(bot.trade_info || '{}'); } catch { return {}; } })();
                const isFuturesBot = ti.marketType === 'futures';
                const isStockBot = (ti.asset_class || bot.asset_class) === 'stock_equity';
                const isHedgeBot = (isFuturesBot && ti.futures?.positionSide === 'hedge') ||
                                   (isStockBot && (ti.short_open_logic || ti.futures?.shortOpenLogic));
                const { quote: QUOTE } = getPairMeta({
                  symbol: bot.symbol,
                  exchangeId: bot.exchangeId,
                  userExchanges,
                  symbolsByExchange,
                });

                return (
                  <>
                    {/* Phase A: Pending order progress bar (partial fills) */}
                    <PendingOrderProgress bot={bot} quote={QUOTE} />

                    {/* SECTION 1: Strategy & Risk */}
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, height: '100%', bgcolor: '#0f1218' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#070a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Functions sx={{ color: '#4d8fc7', fontSize: 18 }} />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>Trading Strategy</Typography>
                          </Box>
                          <Stack spacing={2}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                <TrendingUp sx={{ color: '#46b88a', fontSize: 16 }} />
                                <Typography variant="caption" sx={{ color: '#9aa6bd', fontWeight: 500 }}>{isFuturesBot ? 'OPEN LONG' : 'BUY CONDITION'}</Typography>
                              </Box>
                              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e8edf7', wordBreak: 'break-word' }}>{bot.logic?.buy || 'No conditions set'}</Typography>
                              </Paper>
                            </Box>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                <TrendingDown sx={{ color: '#d97b72', fontSize: 16 }} />
                                <Typography variant="caption" sx={{ color: '#9aa6bd', fontWeight: 500 }}>{isFuturesBot ? 'CLOSE LONG' : 'SELL CONDITION'}</Typography>
                              </Box>
                              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e8edf7', wordBreak: 'break-word' }}>{bot.logic?.sell || 'No conditions set'}</Typography>
                              </Paper>
                            </Box>
                            {isHedgeBot && (
                              <>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                    <TrendingDown sx={{ color: '#d9a441', fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ color: '#9aa6bd', fontWeight: 500 }}>OPEN SHORT</Typography>
                                  </Box>
                                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e8edf7', wordBreak: 'break-word' }}>{bot.logic?.shortOpen || 'No conditions set'}</Typography>
                                  </Paper>
                                </Box>
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                    <TrendingUp sx={{ color: '#4d8fc7', fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ color: '#9aa6bd', fontWeight: 500 }}>CLOSE SHORT</Typography>
                                  </Box>
                                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e8edf7', wordBreak: 'break-word' }}>{bot.logic?.shortClose || 'No conditions set'}</Typography>
                                  </Paper>
                                </Box>
                              </>
                            )}
                          </Stack>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, height: '100%', bgcolor: '#0f1218' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                            <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#070a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <WarningAmber sx={{ color: '#d9a441', fontSize: 18 }} />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>Risk Management</Typography>
                          </Box>
                          <Grid container spacing={1.5}>
                            {(() => {
                              const tiInner = (() => { try { return bot.trade_info ? JSON.parse(bot.trade_info) : {}; } catch { return {}; } })();
                              const { base: BASE, quote: QUOTE } = getPairMeta({ symbol: tiInner.symbol || bot.symbol, exchangeId: bot.exchange_id || bot.exchangeId || newBot.exchangeId, userExchanges, symbolsByExchange });
                              const os = tiInner.orderSizing || { mode: tiInner.mode || 'quote', value: tiInner.value ?? tiInner.totalInvestment, assetSymbol: tiInner.asset_symbol };
                              const unit = os.assetSymbol || (os.mode === 'base' ? BASE : QUOTE) || '—';
                              const rawVal = Number(os.value);
                              const safeVal = Number.isFinite(rawVal) ? rawVal : 0;

                              const riskItems = [
                                { icon: <AttachMoney sx={{ fontSize: 20 }} />, label: 'Position Size', value: os.mode === 'quote' ? `${safeVal.toLocaleString()} ${unit}` : `${safeVal.toLocaleString(undefined, { maximumFractionDigits: 8 })} ${unit}` },
                                { icon: <TrendingUp sx={{ fontSize: 20 }} />, label: 'Take Profit', value: bot.logic?.profitStop || '—' },
                                { icon: <TrendingDown sx={{ fontSize: 20 }} />, label: 'Stop Loss', value: bot.logic?.lossStop || '—' },
                                { icon: <Timeline sx={{ fontSize: 20 }} />, label: 'Trailing Stop', value: bot.logic?.trailingStop ? `${bot.logic.trailingStop}%` : '—' },
                                { icon: <BarChart sx={{ fontSize: 20 }} />, label: 'Max Daily Loss', value: bot.logic?.maxLoss ? `${bot.logic.maxLoss}%` : '—' },
                                { icon: <History sx={{ fontSize: 20 }} />, label: 'Max Trades/Day', value: bot.logic?.maxTrades || '—' },
                                ...(tiInner.marketType === 'futures' ? [
                                  { icon: <TrendingUp sx={{ fontSize: 20 }} />, label: 'Leverage', value: `${tiInner.futures?.leverage || 1}x` },
                                  { icon: <AccountBalance sx={{ fontSize: 20 }} />, label: 'Margin Mode', value: (tiInner.futures?.marginMode || 'cross').charAt(0).toUpperCase() + (tiInner.futures?.marginMode || 'cross').slice(1) },
                                  { icon: <ShowChart sx={{ fontSize: 20 }} />, label: 'Position Mode', value: tiInner.futures?.positionSide === 'hedge' ? 'Hedge' : 'One-Way' },
                                ] : []),
                                ...(tiInner.orderExecution?.mode && tiInner.orderExecution.mode !== 'market' ? [
                                  { icon: <AttachMoney sx={{ fontSize: 20 }} />, label: 'Order Type', value: ORDER_EXECUTION_MODES[tiInner.orderExecution.mode]?.label || tiInner.orderExecution.mode },
                                ] : [])
                              ];

                              return riskItems.map((item, idx) => (
                                <Grid item xs={6} sm={4} key={idx}>
                                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#070a0f', border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', minHeight: 85, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', '&:hover': { borderColor: 'rgba(255,255,255,0.1)' } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                      <Box sx={{ color: '#9aa6bd' }}>{item.icon}</Box>
                                      <Typography variant="caption" sx={{ color: '#9aa6bd', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', fontWeight: 500 }}>{item.label}</Typography>
                                    </Box>
                                    <Typography variant="h6" sx={{ color: '#f2f6ff', fontFamily: '"SF Mono", Monaco, Consolas, monospace', fontSize: '1rem', fontWeight: 600 }}>{item.value}</Typography>
                                  </Box>
                                </Grid>
                              ));
                            })()}
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid>

                    <FuturesInfoPanel bot={bot} futuresData={futuresInfo[bot.id]} />

                    {/* SECTION 1b: Total Value chart — derive a value from whatever
                        the bot has so it shows for all bots, not just synced ones. */}
                    {(() => {
                      const stat = bot.stats || {};
                      let ti = {}; try { ti = JSON.parse(bot.trade_info || '{}'); } catch (_) {}
                      const pick = (...vals) => { for (const v of vals) { const n = Number(v); if (Number.isFinite(n) && n > 0) return n; } return undefined; };
                      const cfg = pick(ti.positionValue, ti.totalAmount, ti.tradeAmount, bot.amount, bot.totalAmount);
                      const posVal = (Number.isFinite(Number(stat.PositionQty)) && Number.isFinite(Number(stat.MarkPrice))) ? Number(stat.PositionQty) * Number(stat.MarkPrice) : 0;
                      const cashEq = Number.isFinite(Number(stat.CashQuote)) ? Number(stat.CashQuote) + posVal : undefined;
                      const realizedEq = (Number.isFinite(Number(stat.InitialCapital)) && Number.isFinite(Number(stat.RealizedPnL))) ? Number(stat.InitialCapital) + Number(stat.RealizedPnL) : undefined;
                      const equity = pick(stat.Equity, cashEq, realizedEq, cfg);
                      if (equity == null) return null;
                      const initial = pick(stat.InitialCapital, cfg, equity);
                      return <BotEquityChart initial={initial} equity={equity} />;
                    })()}

                    {/* SECTION 2: Live Statistics */}
                    {bot.stats && (
                      <Paper elevation={0} sx={{ mt: 3, p: 2.5, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, bgcolor: '#0f1218' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#070a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <AccountBalance sx={{ color: '#4d8fc7', fontSize: 18 }} />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>Live Statistics</Typography>
                          </Box>
                          {bot.stats.UpdatedAt && (
                            <Chip size="small" variant="outlined" label={`Updated: ${new Date(bot.stats.UpdatedAt).toLocaleString()}`} sx={{ height: 24, fontSize: '0.7rem', color: '#9aa6bd', borderColor: 'rgba(255,255,255,0.1)' }} />
                          )}
                        </Box>

                        {(() => {
                          const baseStats = bot.stats || {};
                          // Real server stats are present iff UpdatedAt is set (an
                          // empty/never-synced statistics object normalizes to all-undefined).
                          // Prefer them; only recompute from history when absent — so a
                          // paginated (partial) history can't skew the totals.
                          const hasServerStats = !!(bot.stats && bot.stats.UpdatedAt);
                          const computed = hasServerStats ? null : computeStatsFromHistory(liveBotHistories[bot.id]);
                          const s = computed ? { ...baseStats, ...computed } : baseStats;
                          const isProfit = (s.RealizedPnL || 0) >= 0;
                          const totalReturnPct = isNum(s.Equity) && isNum(s.InitialCapital) && Number(s.InitialCapital) !== 0
                            ? ((Number(s.Equity) - Number(s.InitialCapital)) / Number(s.InitialCapital)) * 100
                            : null;
                          const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                          const fmtAsset = (v) => {
                            if (!isNum(v)) return '—';
                            const n = Number(v);
                            const digits = Math.abs(n) < 1 ? 4 : 2;
                            const sign = n < 0 ? '-' : '';
                            return `${sign}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${QUOTE || ''}`;
                          };
                          const statGroups = [
                            {
                              title: 'Capital', icon: <AccountBalance sx={{ fontSize: 16 }} />, items: [
                                { label: 'Initial', value: fmtAsset(s.InitialCapital) },
                                { label: 'Equity', value: fmtAsset(s.Equity), highlight: true },
                                { label: 'Cash', value: fmtAsset(s.CashQuote) },
                              ]
                            },
                            {
                              title: 'Position', icon: <ShowChart sx={{ fontSize: 16 }} />, items: [
                                { label: 'Quantity', value: isNum(s.PositionQty) ? Number(s.PositionQty).toLocaleString(undefined, { maximumFractionDigits: 8 }) : '—' },
                                { label: 'Entry Price', value: fmtAsset(s.EntryPrice) },
                                { label: 'Mark Price', value: fmtAsset(s.MarkPrice) },
                              ]
                            },
                            {
                              title: 'Profit & Loss', icon: <AttachMoney sx={{ fontSize: 16 }} />, isProfit, items: [
                                { label: 'Realized P&L', value: fmtAsset(s.RealizedPnL), highlight: true },
                                { label: 'Total Return', value: isNum(totalReturnPct) ? fmtPct(totalReturnPct) : '—' },
                                { label: 'Max Drawdown', value: isNum(s.MaxDrawdownPct) ? fmtPct(s.MaxDrawdownPct) : '—' },
                              ]
                            },
                            {
                              title: 'Performance', icon: <BarChart sx={{ fontSize: 16 }} />, items: [
                                { label: 'Trades', value: isNum(s.Trades) ? Number(s.Trades).toLocaleString() : '0' },
                                { label: 'Win Rate', value: isNum(s.WinRatePct) ? fmtPct(s.WinRatePct) : '—' },
                                { label: 'Profit Factor', value: fmtRatio(s.ProfitFactor) },
                              ]
                            },
                          ];

                          return (
                            <Grid container spacing={2}>
                              {statGroups.map((group, gIdx) => (
                                <Grid item xs={6} md={3} key={gIdx}>
                                  <Box sx={{ p: 2, bgcolor: '#070a0f', borderRadius: 2, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', height: '100%', '&:hover': { borderColor: 'rgba(255,255,255,0.1)' } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                                      <Box sx={{ color: '#9aa6bd' }}>{group.icon}</Box>
                                      <Typography variant="caption" fontWeight={600} sx={{ color: '#9aa6bd', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>{group.title}</Typography>
                                    </Box>
                                    <Stack spacing={1}>
                                      {group.items.map((item, iIdx) => (
                                        <Box key={iIdx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                          <Typography variant="caption" sx={{ color: '#9aa6bd' }}>{item.label}</Typography>
                                          <Typography variant="body2" fontWeight={item.highlight ? 700 : 500} sx={{ color: item.highlight && group.title === 'Profit & Loss' ? (group.isProfit ? '#46b88a' : '#d97b72') : '#f2f6ff', fontFamily: 'monospace' }}>
                                            {item.value}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          );
                        })()}

                        {/* Win/Loss breakdown */}
                        {(() => {
                          const baseStats = bot.stats || {};
                          // Real server stats are present iff UpdatedAt is set (an
                          // empty/never-synced statistics object normalizes to all-undefined).
                          // Prefer them; only recompute from history when absent — so a
                          // paginated (partial) history can't skew the totals.
                          const hasServerStats = !!(bot.stats && bot.stats.UpdatedAt);
                          const computed = hasServerStats ? null : computeStatsFromHistory(liveBotHistories[bot.id]);
                          const s = computed ? { ...baseStats, ...computed } : baseStats;
                          if (s.Wins === undefined && s.Losses === undefined) return null;
                          const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                          const fmtAsset = (v) => {
                            if (!isNum(v)) return '—';
                            const n = Number(v);
                            const digits = Math.abs(n) < 1 ? 4 : 2;
                            const sign = n < 0 ? '-' : '';
                            return `${sign}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${QUOTE || ''}`;
                          };
                          return (
                            <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                              <Chip icon={<CheckCircle sx={{ fontSize: 16, color: '#46b88a !important' }} />} label={`${s.Wins || 0} Wins`} size="small" sx={{ bgcolor: '#052e16', color: '#46b88a', borderColor: '#065f46', border: '1px solid' }} />
                              <Chip icon={<Cancel sx={{ fontSize: 16, color: '#d97b72 !important' }} />} label={`${s.Losses || 0} Losses`} size="small" sx={{ bgcolor: '#2a0a0a', color: '#d97b72', borderColor: '#991b1b', border: '1px solid' }} />
                              {s.GrossProfit !== undefined && <Chip label={`Gross Profit: ${fmtAsset(s.GrossProfit)}`} size="small" sx={{ bgcolor: '#065f46', color: '#46b88a' }} />}
                              {s.GrossLoss !== undefined && <Chip label={`Gross Loss: ${fmtAsset(s.GrossLoss)}`} size="small" sx={{ bgcolor: '#991b1b', color: '#d97b72' }} />}
                            </Box>
                          );
                        })()}

                        {/* Phase A: Hedge position panel — renders only when shortPositionQty > 0 */}
                        <HedgePositionPanel
                          stats={bot.stats}
                          quote={QUOTE}
                          markPrice={bot.currentPrice || bot.stats?.MarkPrice}
                        />

                        {/* Phase A: Buying Power chip for stock_equity bots */}
                        {isStockBot && (() => {
                          const acct = getAccountStatusForBot(bot, alpacaStatus);
                          if (!acct) return null;
                          return (
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                              <BuyingPowerChip
                                buyingPower={acct.buyingPower}
                                cash={acct.cash}
                                equity={acct.equity}
                              />
                            </Box>
                          );
                        })()}
                      </Paper>
                    )}

                    {/* SECTION 3: Webhook Display (TradingView only) */}
                    {bot.signalSource === 'tradingview' && bot.webhookUrl && (
                      <Paper elevation={0} sx={{ mt: 3, p: 2.5, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, bgcolor: '#0f1218' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: '#070a0f', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src="https://www.tradingview.com/static/images/favicon.ico" alt="TradingView" style={{ width: 18, height: 18 }} />
                          </Box>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>TradingView Webhook</Typography>
                          <Chip size="small" label="External Signals" color="info" variant="outlined" sx={{ ml: 'auto' }} />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ color: '#9aa6bd', mb: 0.5, display: 'block' }}>WEBHOOK URL</Typography>
                          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#4d8fc7', flex: 1, wordBreak: 'break-all' }}>{bot.webhookUrl}</Typography>
                            <Tooltip title="Copy webhook URL">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(bot.webhookUrl); showToast('success', 'Webhook URL copied!'); }} sx={{ color: '#9aa6bd' }}>
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Paper>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#9aa6bd', mb: 0.5, display: 'block' }}>TRADINGVIEW ALERT MESSAGE</Typography>
                          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#070a0f', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 1.5, position: 'relative' }}>
                            <IconButton size="small" onClick={(e) => {
                              e.stopPropagation();
                              const tiLocal = (() => { try { return JSON.parse(bot.trade_info || '{}'); } catch { return {}; } })();
                              const actionField = tiLocal.webhook?.actionField || 'action';
                              const template = JSON.stringify({ [actionField]: '{{strategy.order.action}}', symbol: '{{ticker}}', price: '{{close}}' }, null, 2);
                              navigator.clipboard.writeText(template);
                              showToast('success', 'Alert template copied!');
                            }} sx={{ position: 'absolute', top: 4, right: 4, color: '#9aa6bd' }}>
                              <ContentCopy fontSize="small" />
                            </IconButton>
                            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#a5d6a7', m: 0, whiteSpace: 'pre-wrap' }}>
                              {(() => {
                                const tiLocal = (() => { try { return JSON.parse(bot.trade_info || '{}'); } catch { return {}; } })();
                                const actionField = tiLocal.webhook?.actionField || 'action';
                                return `{
  "${actionField}": "{{strategy.order.action}}",
  "symbol": "{{ticker}}",
  "price": "{{close}}"
}`;
                              })()}
                            </Typography>
                          </Paper>
                        </Box>
                      </Paper>
                    )}

                    {/* SECTION 4: Trade History (with indicators) */}
                    <Paper elevation={0} sx={{ mt: 3, border: '1px solid', borderColor: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', bgcolor: '#0f1218' }}>
                      <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#070a0f', borderBottom: '1px solid', borderColor: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <History sx={{ color: '#9aa6bd', fontSize: 20 }} />
                          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }}>Trade History</Typography>
                        </Box>
                        {liveBotHistories[bot.id]?.length > 0 && (
                          <Chip size="small" variant="outlined" label={`${liveBotHistories[bot.id].length} entries`} sx={{ height: 24, color: '#9aa6bd', borderColor: 'rgba(255,255,255,0.1)' }} />
                        )}
                      </Box>

                      {liveBotHistories[bot.id] && liveBotHistories[bot.id].length > 0 ? (
                        <TableContainer sx={{ maxHeight: 400 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Time</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Side</TableCell>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Symbol</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Price</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Quantity</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Value</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Fee</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 600, bgcolor: '#070a0f', color: '#e8edf7', borderColor: 'rgba(255,255,255,0.06)' }}>Type</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {liveBotHistories[bot.id].slice().reverse().map((entry, idx) => {
                                const parsed = parseBuySellHistory(entry.text);
                                if (!parsed) return null;

                                // Reconciliation
                                if (parsed.type === 'reconciliation') {
                                  const isCritical = parsed.critical;
                                  const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                                  return (
                                    <TableRow key={idx} sx={{ bgcolor: isCritical ? 'rgba(251, 191, 36, 0.08)' : 'rgba(56, 189, 248, 0.06)', borderLeft: '3px solid', borderColor: isCritical ? '#d9a441' : '#4d8fc7' }}>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#9aa6bd' }}>{entry.timestamp}</Typography></TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label="SYNC" size="small" sx={{ fontWeight: 600, minWidth: 70, bgcolor: isCritical ? '#78350f' : '#0c4a6e', color: isCritical ? '#d9a441' : '#4d8fc7', border: '1px solid', borderColor: isCritical ? '#d9a441' : '#4d8fc7' }} />
                                      </TableCell>
                                      <TableCell colSpan={5} sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Typography variant="body2" sx={{ color: '#e8edf7', fontWeight: 500 }}>{parsed.buy_sell_history}</Typography>
                                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                                          {parsed.notional && <Typography variant="caption" sx={{ color: '#9aa6bd' }}>Notional: {parsed.notional} {QUOTE || ''}</Typography>}
                                          {parsed.diff_pct && <Typography variant="caption" sx={{ color: '#9aa6bd' }}>Drift: {parsed.diff_pct}%</Typography>}
                                          {parsed.source && <Typography variant="caption" sx={{ color: '#9aa6bd' }}>Source: {parsed.source === 'binance_sync' ? 'Binance' : parsed.source}</Typography>}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="center" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label={isCritical ? 'Critical' : 'Auto-Fixed'} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: isCritical ? '#78350f' : '#0f1218', color: isCritical ? '#d9a441' : '#4d8fc7', border: '1px solid', borderColor: isCritical ? '#d9a441' : '#4d8fc7' }} />
                                      </TableCell>
                                    </TableRow>
                                  );
                                }

                                // Order lifecycle (pending/cancelled/expired)
                                if (parsed.type === 'order_pending' || parsed.type === 'order_cancelled' || parsed.type === 'order_expired') {
                                  const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                                  const config = {
                                    order_pending: { label: '⏳ PENDING', bgColor: 'rgba(251, 191, 36, 0.06)', borderColor: '#d9a441', chipBg: '#78350f', chipColor: '#d9a441', statusLabel: 'Awaiting Fill' },
                                    order_cancelled: { label: '✕ CANCELLED', bgColor: 'rgba(156, 163, 175, 0.06)', borderColor: '#6f7c96', chipBg: 'rgba(255,255,255,0.1)', chipColor: '#9aa6bd', statusLabel: 'Cancelled' },
                                    order_expired: { label: '⏱ EXPIRED', bgColor: 'rgba(156, 163, 175, 0.06)', borderColor: '#6f7c96', chipBg: 'rgba(255,255,255,0.1)', chipColor: '#9aa6bd', statusLabel: 'Expired' },
                                  };
                                  const c = config[parsed.type];
                                  const sideUpper = String(parsed.side || '').toUpperCase();
                                  const isBuy = sideUpper.includes('BUY') || sideUpper.includes('LONG');
                                  return (
                                    <TableRow key={idx} sx={{ bgcolor: c.bgColor, borderLeft: '3px solid', borderLeftColor: c.borderColor }}>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#9aa6bd' }}>{entry.timestamp}</Typography></TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <Chip label={parsed.side || '—'} size="small" sx={{ fontWeight: 600, minWidth: 50, fontSize: '0.65rem', bgcolor: isBuy ? '#46b88a' : '#d97b72', color: '#ffffff' }} />
                                          <Chip label={c.label} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: c.chipBg, color: c.chipColor, border: '1px solid', borderColor: c.borderColor }} />
                                        </Stack>
                                      </TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ color: '#e8edf7' }}>{parsed.symbol || bot.symbol}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#f2f6ff' }}>{parsed.price ? `${Number(parsed.price).toLocaleString()} ${QUOTE || ''}` : '—'}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#e8edf7' }}>{parsed.quantity ? Number(parsed.quantity).toFixed(6) : '—'}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#9aa6bd', fontFamily: 'monospace' }}>{parsed.order_id ? `#${String(parsed.order_id).slice(-6)}` : '—'}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#6f7c96', fontStyle: 'italic' }}>{parsed.reason || c.statusLabel}</Typography></TableCell>
                                      <TableCell align="center" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label="Limit" size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#0f1218', color: '#4d8fc7', border: '1px solid', borderColor: '#3f6fb0' }} />
                                      </TableCell>
                                    </TableRow>
                                  );
                                }

                                // ─── Regular trade row ─────────────────────────────
                                const value = parsed.side === 'BUY' ? parseFloat(parsed.cost || 0) : parseFloat(parsed.proceeds || 0);
                                const { quote: QUOTE } = getPairMeta({ symbol: bot.symbol, exchangeId: bot.exchangeId, userExchanges, symbolsByExchange });
                                const hasIndicators = parsed.indicators && typeof parsed.indicators === 'object' && Object.keys(parsed.indicators).length > 0;

                                return (
                                  <React.Fragment key={idx}>
                                    <TableRow sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }, bgcolor: idx % 2 === 0 ? '#0f1218' : '#070a0f', '& td': hasIndicators ? { borderBottom: 'none' } : {} }}>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="caption" sx={{ color: '#9aa6bd' }}>{entry.timestamp}</Typography></TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label={parsed.side} size="small" sx={{ fontWeight: 600, minWidth: 50, bgcolor: parsed.side === 'BUY' ? '#46b88a' : '#d97b72', color: '#ffffff' }} />
                                      </TableCell>
                                      <TableCell sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" fontWeight={500} sx={{ color: '#e8edf7' }}>{parsed.symbol}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#f2f6ff' }}>{parseFloat(parsed.price).toLocaleString()} {QUOTE || ''}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#e8edf7' }}>{parseFloat(parsed.quantity).toFixed(6)}</Typography></TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', color: parsed.side === 'BUY' ? '#d97b72' : '#46b88a' }}>
                                          {parsed.side === 'BUY' ? '-' : '+'}{value.toFixed(2)} {QUOTE || ''}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}><Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#9aa6bd', fontSize: '0.75rem' }}>{parsed.fee ? `${Number(parsed.fee).toFixed(4)} ${QUOTE || ''}` : '—'}</Typography></TableCell>
                                      <TableCell align="center" sx={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                                        <Chip label={parsed.simulated ? 'Paper' : 'Live'} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: parsed.simulated ? '#0f1218' : '#052e16', color: parsed.simulated ? '#4d8fc7' : '#46b88a', border: '1px solid', borderColor: parsed.simulated ? '#4d8fc7' : '#46b88a' }} />
                                      </TableCell>
                                    </TableRow>

                                    {hasIndicators && (
                                      <TableRow sx={{ bgcolor: idx % 2 === 0 ? '#0f1218' : '#070a0f' }}>
                                        <TableCell colSpan={8} sx={{ borderColor: 'rgba(255,255,255,0.06)', py: 1, px: 2 }}>
                                          <Box sx={{ px: 1.5, py: 0.75, bgcolor: '#070a0f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                                              <Functions sx={{ color: '#4d8fc7', fontSize: 13 }} />
                                              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: '#9aa6bd', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                                                Indicators at signal
                                              </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                              {Object.entries(parsed.indicators).map(([key, val]) => {
                                                const m = key.match(/^([A-Za-z_][A-Za-z0-9_]*)_(\d+)_(?:tick|\w+)$/);
                                                const name = m?.[1] || key;
                                                const period = m?.[2];
                                                const displayName = period && period !== '0' ? `${name}(${period})` : name;
                                                const isNull = val == null || !Number.isFinite(Number(val));
                                                return (
                                                  <Typography key={key} sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#e8edf7' }}>
                                                    <span style={{ color: '#4d8fc7' }}>{displayName}</span>
                                                    <span style={{ color: '#6f7c96' }}> = </span>
                                                    <span style={{ color: isNull ? '#6f7c96' : '#46b88a' }}>
                                                      {isNull ? 'NaN' : Number(val).toFixed(4)}
                                                    </span>
                                                  </Typography>
                                                );
                                              })}
                                            </Box>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                          <History sx={{ fontSize: 48, color: 'rgba(255,255,255,0.1)', mb: 1 }} />
                          <Typography variant="body2" sx={{ color: '#9aa6bd' }}>
                            {historyPaging[bot.id]?.loading ? 'Loading trade history…' : 'No trade history yet'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6f7c96' }}>Trades will appear here once the bot executes orders</Typography>
                        </Box>
                      )}

                      {/* Paginated "Load more" — pages older history on demand */}
                      {liveBotHistories[bot.id]?.length > 0 && !historyPaging[bot.id]?.done && (
                        <Box sx={{ p: 1.5, textAlign: 'center', borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.06)' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={historyPaging[bot.id]?.loading}
                            onClick={() => loadBotHistoryPage(bot.id)}
                            sx={{ color: '#9aa6bd', borderColor: 'rgba(255,255,255,0.1)', textTransform: 'none' }}
                          >
                            {historyPaging[bot.id]?.loading ? 'Loading…' : 'Load older trades'}
                          </Button>
                          {Number.isFinite(historyPaging[bot.id]?.total) && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#6f7c96' }}>
                              Showing {liveBotHistories[bot.id].length} of {historyPaging[bot.id].total}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>

                    {/* SECTION 5: Action Buttons */}
                    {/* SECTION 5: Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3, mb: 1 }}>
                      {bot.signalSource !== 'tradingview' && (() => {
                        const check = gateBacktest(bot);
                        return (
                          <Tooltip title={check.supported ? '' : check.message} arrow>
                            <span>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<BarChart />}
                                disabled={!check.supported}
                                onClick={(e) => { e.stopPropagation(); runBacktest(bot); }}
                                sx={{
                                  borderRadius: 1.5,
                                  borderColor: 'rgba(255,255,255,0.1)',
                                  color: '#e8edf7',
                                  '&:hover': { borderColor: 'rgba(255,255,255,0.18)', bgcolor: 'rgba(255, 255, 255, 0.05)' },
                                  '&.Mui-disabled': { color: '#6f7c96', borderColor: 'rgba(255,255,255,0.06)' },
                                }}
                              >
                                Backtest
                              </Button>
                            </span>
                          </Tooltip>
                        );
                      })()}

                      <Button variant="outlined" size="small" startIcon={<Edit />} sx={{
                        borderRadius: 1.5, borderColor: 'rgba(255,255,255,0.1)', color: '#e8edf7',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.18)', bgcolor: 'rgba(255, 255, 255, 0.05)' }
                      }}>
                        Edit
                      </Button>
                      <Button variant="outlined" size="small" startIcon={<Delete />}
                        onClick={() => setConfirmDeleteBotId(bot.id)}
                        sx={{
                          borderRadius: 1.5, borderColor: 'rgba(255,255,255,0.1)', color: '#d97b72',
                          '&:hover': { borderColor: '#d97b72', bgcolor: 'rgba(220, 38, 38, 0.1)' }
                        }}>
                        Delete
                      </Button>
                    </Box>
                  </>
                );
              })()}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  }

  // ─── Scanner profile group (collapsible header + child bots) ──────────────
  function renderScannerProfileGroup(profile, children) {
    const isExpanded = expandedScannerProfile === profile.id;
    const cfg = profile.scannerConfig || {};
    const isPaused = cfg.paused === true;
    const triggerExpr = cfg.trigger?.expression || cfg.trigger?._preset?.kind || '—';
    const universe = cfg.universe?.type || '—';

    return (
      <Paper
        key={`profile-${profile.id}`}
        variant="outlined"
        sx={{
          borderColor: 'rgba(255,255,255,0.1)',
          borderWidth: 1,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#0f1218',
        }}
      >
        <Box
          onClick={() => setExpandedScannerProfile(isExpanded ? null : profile.id)}
          sx={{
            p: 2,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: '#070a0f',
            borderBottom: isExpanded ? '1px solid rgba(255,255,255,0.06)' : 'none',
            '&:hover': { bgcolor: '#0f1218' },
          }}
        >
          <IconButton size="small">
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>

          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: isPaused ? 'rgba(255,255,255,0.06)' : 'rgba(37, 99, 235, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShowChart sx={{ color: isPaused ? '#9aa6bd' : '#4d8fc7', fontSize: 22 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#f2f6ff' }} noWrap>
                {profile.name}
              </Typography>
              <Chip
                size="small"
                label="Scanner Profile"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#1e3a8a',
                  color: '#9cc3e4',
                  border: '1px solid #4d8fc7',
                }}
              />
              {isPaused && (
                <Chip
                  size="small"
                  label="Paused"
                  icon={<Pause sx={{ fontSize: 12 }} />}
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    bgcolor: '#451a03',
                    color: '#d9a441',
                  }}
                />
              )}
            </Box>
            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: '#9aa6bd' }}>
                <strong>Universe:</strong> {universe}
                {cfg.universe?.limit && ` (limit ${cfg.universe.limit})`}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9aa6bd' }}>
                <strong>Trigger:</strong>{' '}
                {triggerExpr.length > 50 ? triggerExpr.slice(0, 50) + '…' : triggerExpr}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#f2f6ff', lineHeight: 1 }}>
                {children.length}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9aa6bd' }}>
                {children.length === 1 ? 'spawned bot' : 'spawned bots'}
              </Typography>
            </Box>
            {cfg.max_concurrent_spawned && (
              <Typography variant="caption" sx={{ color: '#6f7c96' }}>
                / {cfg.max_concurrent_spawned} max
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
            <Tooltip title="Open in Scanner">
              <IconButton
                size="small"
                onClick={() => window.location.href = `/scanner?profile=${profile.id}`}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ p: 2, bgcolor: '#0f1218' }}>
            {children.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" sx={{ color: '#9aa6bd' }}>
                  No bots spawned yet
                </Typography>
                <Typography variant="caption" sx={{ color: '#6f7c96' }}>
                  Bots will appear here when the trigger fires.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {children.map(bot => renderBotCard(bot))}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Paper>
    );
  }



  /*
    function parseBuySellHistory(raw) {
    try {
      // Clean quotes and backslashes
      const decoded = raw.replace(/\\+/g, '').replace(/"+/g, '"');
      
      const symbolMatch = decoded.match(/Symbol (\w+)/i);
      const sideMatch = decoded.match(/\b(BUY|SELL)\b/);
      const totalPriceMatch = decoded.match(/TOTAL PRICE ([\d.]+)/i);
      const avgPriceMatch = decoded.match(/AVERAGE PRICE ([\d.]+)/i);
      const timeMatch = decoded.match(/AT TIME (.+?) FOR/);
      const ruleMatch = decoded.match(/INFO "(.*)"/);
  
      return {
        symbol: symbolMatch?.[1] || 'N/A',
        side: sideMatch?.[1] || 'N/A',
        totalPrice: totalPriceMatch?.[1] || '0',
        avgPrice: avgPriceMatch?.[1] || '0',
        time: timeMatch?.[1] || 'Unknown Time',
        rule: ruleMatch?.[1] || '',
      };
    } catch (err) {
      console.warn("Failed to parse buy/sell history:", raw);
      return null;
    }
  }
  */

  // ... (keep all other existing functions like handleNext, handleBack, etc.)

  return (
    <ThemeProvider theme={AT_THEME}>
    <Box sx={{ minHeight: '100vh', bgcolor: AT.bg, color: AT.text, fontFamily: AT_SANS, '@keyframes clp': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } } }}>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
      <Toolbar button={<Logout />} />

      <Box sx={{ maxWidth: '1280px', mx: 'auto', px: { xs: 2.5, md: '40px' }, py: 4.5 }}>
        {/* Page header — eyebrow + engine status + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '12px', letterSpacing: '.2em', color: AT.accent }}>AUTOTRADER</Box>
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: AT_MONO, fontSize: '10px', letterSpacing: '.12em', color: AT.muted }}>
            <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: AT.positive, animation: 'clp 2.6s ease-in-out infinite' }} />ENGINE ONLINE
          </Box>
        </Box>
        <Typography component="h1" sx={{ fontFamily: AT_SANS, fontSize: '32px', fontWeight: 700, letterSpacing: '-.02em', color: AT.bright, m: '0 0 6px' }}>
          Trading Bot Manager
        </Typography>
        <Typography sx={{ fontSize: '15px', color: AT.body, m: '0 0 26px' }}>
          Create, monitor and tune automated strategies running across your exchanges.
        </Typography>

        {/* Top tabs — icon + label with accent underline (handoff §3) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', borderBottom: `1px solid rgba(255,255,255,.07)`, mb: 3, flexWrap: 'wrap' }}>
          {[
            { key: 'bots', label: 'My Bots', icon: <Settings sx={{ fontSize: 17 }} /> },
            { key: 'create', label: 'Create Bot', icon: <AddCircleOutline sx={{ fontSize: 17 }} />, onClick: handleCreate },
            { key: 'backtest', label: 'Backtesting', icon: <History sx={{ fontSize: 17 }} /> },
            { key: 'advisor', label: 'AI Advisor', icon: <ShowChart sx={{ fontSize: 17 }} /> },
          ].map((t) => {
            const on = activeTab === t.key;
            return (
              <Box key={t.key} onClick={() => { setActiveTab(t.key); if (t.onClick) t.onClick(); }}
                sx={{ display: 'flex', alignItems: 'center', gap: '9px', px: '18px', py: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                  color: on ? '#fff' : AT.body, borderBottom: `2px solid ${on ? AT.accent : 'transparent'}`, mb: '-1px',
                  transition: 'color .15s', '& svg': { color: on ? AT.accent : AT.muted }, '&:hover': { color: on ? '#fff' : AT.text } }}>
                {t.icon} {t.label}
              </Box>
            );
          })}
        </Box>

        {activeTab === 'create' && isCreating && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 332px' }, gap: '16px', alignItems: 'start', mb: 4 }}>
                <Box sx={{ minWidth: 0, bgcolor: AT.card, border: `1px solid ${AT.hairline}`, borderRadius: '14px', boxShadow: '0 1px 2px rgba(0,0,0,.22)', p: { xs: '20px', md: '24px' } }}>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label, index) => {
                  const isSkipped = index === 1 && newBot.signalSource === 'tradingview';
                  return (
                    <Step key={label} completed={isSkipped ? false : undefined}>
                      <StepLabel
                        optional={isSkipped ? (
                          <Typography variant="caption" color="text.secondary">
                            (Using TradingView)
                          </Typography>
                        ) : null}
                        sx={isSkipped ? { opacity: 0.5 } : {}}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>

              {/* (Strategy summary now lives in the persistent right rail — §8a) */}

              {activeStep === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Bot Name"
                      value={newBot.name}
                      onChange={e => setNewBot({ ...newBot, name: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ExchangeSelect
                      exchanges={userExchanges}
                      value={exchangeValue}               // <— stable reference
                      disabled={!userExchanges?.length}
                      onChange={(ex) => {
                        setNewBot(prev => ({ ...prev, exchangeId: ex?.id ?? null }));
                        if (ex?.id) {
                          const exchangeName = ex.exchange || ex.name;
                          ensureSymbols(ex.id, exchangeName);
                        }
                      }}
                    />
{/* Phase B — show asset class derived from exchange */}
                    {newBot.exchangeId && (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssetClassChip assetClass={assetClass} />
                      </Box>
                    )}
                  </Grid>

                  {/* Funding Asset — balance source for bot capital & P&L.
                      Crypto: USDT/USDC/FDUSD/BUSD/BTC/ETH. Stocks: USD only (locked). */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={!newBot.exchangeId || isStockEquity}>
                      <InputLabel>Funding Asset</InputLabel>
                      <Select
                        value={newBot.funding_asset || defaultFundingAssetForClass(assetClass)}
                        label="Funding Asset"
                        onChange={(e) => setNewBot(prev => ({ ...prev, funding_asset: e.target.value }))}
                      >
                        {getFundingAssetsForClass(assetClass).map(a => (
                          <MenuItem key={a} value={a}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              <Typography variant="body2" fontWeight={500}>{a}</Typography>
                              {/* TODO: live balance once GET /api/exchange/:id/balances ships */}
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                      {isStockEquity
                        ? 'Alpaca accounts are USD-funded. Bot capital and P&L are measured in USD.'
                        : !newBot.exchangeId
                          ? 'Select an exchange first.'
                          : `Bot capital, P&L, and equity measured in ${newBot.funding_asset || 'USDT'}. If the pair quotes in a different asset, the bot will auto-convert at startup.`}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>


                    <TextField
                      label="Search symbol"
                      placeholder="e.g. BTC/USDT or btcusdt"
                      value={symbolSearch}
                      onChange={(e) => setSymbolSearch(e.target.value)}
                      fullWidth
                      sx={{ mb: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                        endAdornment: symbolSearch ? (
                          <IconButton
                            size="small"
                            onClick={() => setSymbolSearch('')}
                            aria-label="Clear search"
                          >
                            <Cancel fontSize="small" />
                          </IconButton>
                        ) : null
                      }}
                    />

                     <Autocomplete
                      loading={symbolsLoading}
                      options={filteredSymbolOptions}
                      getOptionLabel={(opt) => {
                        if (opt.base_asset && opt.quote_asset) {
                          return `${opt.base_asset}/${opt.quote_asset}`;
                        }
                        return compactToPretty(opt.symbol);
                      }}
                      value={
                        filteredSymbolOptions.find(o => o.symbol === newBot.symbol) || null
                      }
                      // Phase B — render shortability badge inline for stock_equity
                      renderOption={(props, option) => {
                        const label = option.base_asset && option.quote_asset
                          ? `${option.base_asset}/${option.quote_asset}`
                          : compactToPretty(option.symbol);
                        if (!isStockEquity) {
                          return <li {...props}>{label}</li>;
                        }
                        const sym = String(option.symbol || '').toUpperCase();
                        const status = shortability.get(sym);
                        return (
                          <li {...props} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <span>{label}</span>
                            <ShortabilityBadge status={status} compact />
                          </li>
                        );
                      }}
                      // Phase B — when the dropdown is open, batch-fetch shortability for visible symbols
                      onOpen={() => {
                        if (!isStockEquity) return;
                        const syms = (filteredSymbolOptions || []).slice(0, 50).map(o => String(o.symbol || '').toUpperCase()).filter(Boolean);
                        if (syms.length) shortability.lookup(syms);
                      }}
                      onChange={(_e, val) => {
                        if (!val) {
                          setNewBot(prev => ({
                            ...prev,
                            symbol: '',
                            baseAsset: undefined,
                            basePrecision: undefined,
                            quoteAsset: undefined,
                            quotePrecision: undefined,
                            marginAsset: undefined,
                          }));
                          return;
                        }

                        const symbol = (val.symbol || val.class_name || '').toUpperCase();
                        let baseAsset = val.base_asset;
                        let quoteAsset = val.quote_asset;

                        // For futures COIN-M, margin asset is the base asset (e.g. BTC)
                        // For futures USDT-M, margin asset is USDT
                        const marginAsset = val.margin_asset ?? quoteAsset ?? null;

                        if (baseAsset && !quoteAsset) {
                          quoteAsset = symbol.replace(new RegExp(`^${baseAsset}`, 'i'), '') || symbol.slice(-4);
                        }
                        if (!baseAsset || !quoteAsset) {
                          const [fb, fq] = splitPair(symbol);
                          baseAsset = baseAsset || fb;
                          quoteAsset = quoteAsset || fq;
                        }

                        setNewBot(prev => ({
                          ...prev,
                          symbol,
                          baseAsset,
                          basePrecision: val.base_precision ?? val.quantity_precision ?? val.price_precision ?? null,
                          quoteAsset,
                          quotePrecision: val.quote_precision ?? 2,
                          marginAsset,
                          // carry through futures metadata so Order Size In label is accurate
                          ...(prev.marketType === 'futures' && {
                            contractType: val.contract_type ?? null,
                          }),
                        }));
                      }}

                      noOptionsText={
                        newBot.exchangeId
                          ? (symbolsLoading ? 'Loading symbols…' : 'No matching symbols')
                          : 'Select an exchange first'
                      }
                      ListboxProps={{ style: { maxHeight: 320 } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Trading Pair"
                          helperText={
                            symbolsError
                              ? symbolsError
                              : newBot.exchangeId
                                ? (() => {
                                  const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
                                  const exchangeName = selectedExchange?.exchange || selectedExchange?.name || newBot.exchangeId;

                                  if (newBot.marketType === 'futures') {
                                    const marketType = newBot.futuresType === 'coin_m' ? 'coinm_futures' : 'usdm_futures';
                                    const cacheKey = `${exchangeName}:${marketType}`;
                                    const count = (futuresSymbolsByExchange[cacheKey] || []).length;
                                    return `Loaded ${count} ${marketType === 'coinm_futures' ? 'COIN-M' : 'USDT-M'} futures symbols`;
                                  }

                                  const cacheKey = exchangeName;
                                  return `Loaded ${(symbolsByExchange[cacheKey] || []).length} symbols`;
                                })()
                                : 'Select an exchange to load symbols'
                          }
                          fullWidth
                        />
                      )}
                    />
<SymbolFeeDisplay
                      symbol={newBot.symbol}
                      exchangeId={newBot.exchangeId}
                      userExchanges={userExchanges}
                      symbolsByExchange={newBot.marketType === 'futures' ? futuresSymbolsByExchange : symbolsByExchange}
                      orderExecutionMode={newBot.orderExecution}
                    />

                    {/* Funding-asset vs pair-quote mismatch warning.
                        Stock_equity is always USD-funded so no warning ever applies.
                        For crypto, surface the conversion cost up front. */}
                    {!isStockEquity && newBot.symbol && newBot.funding_asset && (() => {
                      const { quote } = getPairMeta({
                        symbol: newBot.symbol,
                        exchangeId: newBot.exchangeId,
                        userExchanges,
                        symbolsByExchange: newBot.marketType === 'futures' ? futuresSymbolsByExchange : symbolsByExchange,
                      });
                      const mm = describeFundingMismatchForPair(newBot.funding_asset, quote);
                      if (!mm) return null;
                      return (
                        <Alert severity={mm.severity} icon={<WarningAmber fontSize="small" />} sx={{ mt: 1.5 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{mm.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{mm.body}</Typography>
                        </Alert>
                      );
                    })()}
                  </Grid>


                  {/* ✅ Time Interval - ONLY FOR LOGIC-BASED BOTS */}
                  {newBot.signalSource !== 'tradingview' && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Time Interval</InputLabel>
                        <Select
                          value={newBot.interval}
                          onChange={e => setNewBot({ ...newBot, interval: e.target.value })}
                          label="Time Interval"
                        >
                          {['tick', '1m', '5m', '15m', '30m', '1H', '4H', '8H', '12H', '1D'].map(i => (
                            <MenuItem key={i} value={i}>{i}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}


                  {/* Signal Source Section - Enhanced TradingView Integration */}
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        borderColor: newBot.signalSource === 'tradingview' ? 'primary.main' : 'divider',
                        transition: 'border-color 0.2s',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <ShowChart color="primary" />
                        <Typography variant="subtitle1" fontWeight={600}>
                          Signal Source
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        {/* Signal Mode Toggle */}
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Paper
                              variant="outlined"
                              onClick={() => setNewBot(prev => ({ ...prev, signalSource: 'logic' }))}
                              sx={{
                                flex: 1,
                                p: 2,
                                cursor: 'pointer',
                                borderColor: newBot.signalSource === 'logic' ? 'primary.main' : 'divider',
                                borderWidth: newBot.signalSource === 'logic' ? 2 : 1,
                                bgcolor: newBot.signalSource === 'logic' ? 'action.selected' : 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: 'primary.light' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Functions color={newBot.signalSource === 'logic' ? 'primary' : 'action'} />
                                <Typography variant="subtitle2" fontWeight={600}>
                                  Internal Logic
                                </Typography>
                                {newBot.signalSource === 'logic' && (
                                  <Chip size="small" label="Selected" color="primary" sx={{ ml: 'auto' }} />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Use Buy/Sell rules defined in Step 2. Bot evaluates conditions automatically.
                              </Typography>
                            </Paper>

                            <Paper
                              variant="outlined"
                              onClick={() => setNewBot(prev => ({
                                ...prev,
                                signalSource: 'tradingview',
                                webhookSecret: prev.webhookSecret || Array.from(crypto.getRandomValues(new Uint8Array(16))).map(b => b.toString(16).padStart(2, '0')).join(''),
                              }))}
                              sx={{
                                flex: 1,
                                p: 2,
                                cursor: 'pointer',
                                borderColor: newBot.signalSource === 'tradingview' ? 'primary.main' : 'divider',
                                borderWidth: newBot.signalSource === 'tradingview' ? 2 : 1,
                                bgcolor: newBot.signalSource === 'tradingview' ? 'action.selected' : 'background.paper',
                                transition: 'all 0.2s',
                                '&:hover': { borderColor: 'primary.light' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <img
                                  src="https://www.tradingview.com/static/images/favicon.ico"
                                  alt="TradingView"
                                  style={{ width: 20, height: 20 }}
                                />
                                <Typography variant="subtitle2" fontWeight={600}>
                                  TradingView Webhook
                                </Typography>
                                {newBot.signalSource === 'tradingview' && (
                                  <Chip size="small" label="Selected" color="primary" sx={{ ml: 'auto' }} />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Receive BUY/SELL signals from TradingView alerts via webhook.
                              </Typography>
                            </Paper>
                          </Box>
                        </Grid>

                        {/* TradingView Configuration */}
                        {newBot.signalSource === 'tradingview' && (
                          <>
                            <Grid item xs={12}>
                              <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                  <strong>How it works:</strong> Create an alert in TradingView, set the webhook URL below,
                                  and configure the alert message to send JSON with your action field.
                                </Typography>
                              </Alert>
                            </Grid>

                            {/* Webhook URL Preview */}
                            <Grid item xs={12}>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  bgcolor: 'grey.900',
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    WEBHOOK URL (copy after creating bot)
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label="Template"
                                    variant="outlined"
                                    color="warning"
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    color: 'primary.light',
                                    wordBreak: 'break-all',
                                    p: 1,
                                    bgcolor: 'grey.800',
                                    borderRadius: 1,
                                  }}
                                >
                                  {buildTradingViewWebhookUrl("BOT_ID", newBot.webhookSecret)}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  ⚠️ BOT_ID will be replaced with the actual bot ID after creation
                                </Typography>
                              </Paper>
                            </Grid>

                            {/* Secret & Field Configuration */}
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Webhook Secret"
                                value={newBot.webhookSecret || ''}
                                onChange={(e) => setNewBot(prev => ({ ...prev, webhookSecret: e.target.value }))}
                                fullWidth
                                placeholder="e.g., my-secret-key-123"
                                helperText="Optional. Must match the 'secret' in your TradingView URL."
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Visibility fontSize="small" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Action Field Name"
                                value={newBot.webhookActionField || 'action'}
                                onChange={(e) => setNewBot(prev => ({ ...prev, webhookActionField: e.target.value }))}
                                fullWidth
                                helperText="JSON field name that contains BUY/SELL value"
                              />
                            </Grid>

                            <Grid item xs={6} sm={3}>
                              <TextField
                                label="BUY Signal Value"
                                value={newBot.webhookBuyValue || 'BUY'}
                                onChange={(e) => setNewBot(prev => ({ ...prev, webhookBuyValue: e.target.value }))}
                                fullWidth
                                size="small"
                              />
                            </Grid>

                            <Grid item xs={6} sm={3}>
                              <TextField
                                label="SELL Signal Value"
                                value={newBot.webhookSellValue || 'SELL'}
                                onChange={(e) => setNewBot(prev => ({ ...prev, webhookSellValue: e.target.value }))}
                                fullWidth
                                size="small"
                              />
                            </Grid>

                            {/* TradingView Alert Message Template */}
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                                TradingView Alert Message Template
                              </Typography>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 2,
                                  bgcolor: 'grey.900',
                                  borderRadius: 1,
                                  position: 'relative',
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const template = JSON.stringify({
                                      [newBot.webhookActionField || 'action']: '{{strategy.order.action}}',
                                      symbol: '{{ticker}}',
                                      price: '{{close}}',
                                      time: '{{time}}',
                                    }, null, 2);
                                    navigator.clipboard.writeText(template);
                                    showToast('success', 'Alert template copied to clipboard!');
                                  }}
                                  sx={{ position: 'absolute', top: 8, right: 8 }}
                                >
                                  <Tooltip title="Copy to clipboard">
                                    <ContentCopy fontSize="small" />
                                  </Tooltip>
                                </IconButton>
                                <Typography
                                  component="pre"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    color: '#a5d6a7',
                                    m: 0,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {`{
  "${newBot.webhookActionField || 'action'}": "{{strategy.order.action}}",
  "symbol": "{{ticker}}",
  "price": "{{close}}",
  "time": "{{time}}"
}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                                  Copy this into TradingView's alert message field. The <code>{'{{strategy.order.action}}'}</code> will be
                                  replaced with "buy" or "sell" automatically.
                                </Typography>
                              </Paper>
                            </Grid>

                            {/* Setup Instructions */}
                            <Grid item xs={12}>
                              <Accordion
                                sx={{
                                  bgcolor: 'background.default',
                                  '&:before': { display: 'none' },
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                  <Typography variant="subtitle2">
                                    📖 TradingView Setup Instructions
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Stack spacing={1.5}>
                                    <Typography variant="body2">
                                      <strong>1.</strong> Create your bot here first to get the actual webhook URL
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>2.</strong> In TradingView, create a strategy or indicator with alerts
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>3.</strong> Click "Create Alert" on your chart
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>4.</strong> In the alert settings:
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                      <li>
                                        <Typography variant="body2">
                                          Check "Webhook URL" and paste your bot's webhook URL
                                        </Typography>
                                      </li>
                                      <li>
                                        <Typography variant="body2">
                                          Set "Message" to the JSON template above
                                        </Typography>
                                      </li>
                                    </Box>
                                    <Typography variant="body2">
                                      <strong>5.</strong> Save the alert - signals will now trigger your bot!
                                    </Typography>
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                      <Typography variant="caption">
                                        TradingView webhook alerts require a paid TradingView plan (Pro, Pro+, or Premium).
                                      </Typography>
                                    </Alert>
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>



                  {/* ✅ NEW: Insert FuturesConfig here }
                  <FuturesConfigSection
                    newBot={newBot}
                    setNewBot={setNewBot}
                    userExchanges={userExchanges}
                  />
                  */}


                 {/* ✅ NEW: Insert OrderExecution here */}
                  <OrderExecutionSection
                    newBot={newBot}
                    setNewBot={setNewBot}
                  />

                  {/* Phase B — Extended hours toggle (self-gates to stock_equity) */}
                  <Grid item xs={12}>
                    <ExtendedHoursToggle
                      assetClass={assetClass}
                      value={newBot.extendedHours}
                      orderExecutionMode={newBot.orderExecution}
                      onChange={(v) => setNewBot(prev => ({ ...prev, extendedHours: v }))}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <PositionSizeSection
                      newBot={newBot}
                      setNewBot={setNewBot}
                      marketType={newBot.marketType}
                      leverage={newBot.leverage}
                      baseAsset={newBot.baseAsset}
                      quoteAsset={newBot.quoteAsset}
                    />
                  </Grid>
                </Grid>
              )}


               {activeStep === 1 && (
                <Box sx={{ minHeight: 500 }}>  {/* Add minHeight */}
                  {/* Phase B — Allow Shorts toggle for stock_equity (mirrors futures hedge mode) */}
                  {isStockEquity && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        onClick={() => setNewBot(prev => ({ ...prev, allowShorts: !prev.allowShorts }))}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                      >
                        <Chip
                          label={newBot.allowShorts ? 'ON' : 'OFF'}
                          size="small"
                          color={newBot.allowShorts ? 'info' : 'default'}
                          variant={newBot.allowShorts ? 'filled' : 'outlined'}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={500}>Allow Shorts</Typography>
                          <Typography variant="caption" color="text.secondary">
                              Enable short-side conditions. The symbol must be easy-to-borrow (ETB) and your Alpaca account must be a margin account with shorting enabled.

                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  )}

                  <StrategyBuilder
                    buyConditions={buyConditions}
                    sellConditions={sellConditions}
                    onBuyConditionsChange={setBuyConditions}
                    onSellConditionsChange={setSellConditions}

                    // ✅ Hedge mode short conditions
                    shortOpenConditions={shortOpenConditions}
                    shortCloseConditions={shortCloseConditions}
                    onShortOpenConditionsChange={setShortOpenConditions}
                    onShortCloseConditionsChange={setShortCloseConditions}

                    // ✅ Market context
                    marketType={newBot.marketType}
                    positionMode={
                      isStockEquity
                        ? (newBot.allowShorts ? 'hedge' : 'one_way')
                        : newBot.positionSide
                    }
                    assetClass={assetClass}

                    isTickMode={isTickInterval}
                    profitStop={profitStop}
                    lossStop={lossStop}
                    onProfitStopChange={setProfitStop}
                    onLossStopChange={setLossStop}
                  />
                  {/* Remove the Grid container wrapper - StrategyBuilder has its own layout */}
                </Box>
              )}


              {activeStep === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Max Daily Loss (%)"
                      value={newBot.maxLoss}
                      onChange={e => setNewBot({ ...newBot, maxLoss: e.target.value })}
                      fullWidth
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Max Trades Per Day"
                      value={newBot.maxTrades}
                      onChange={e => setNewBot({ ...newBot, maxTrades: e.target.value })}
                      fullWidth
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Trailing Stop (%)"
                      value={newBot.trailingStop}
                      onChange={e => setNewBot({ ...newBot, trailingStop: e.target.value })}
                      fullWidth
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, mt: 2, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Strategy Summary
                      </Typography>
                      <Stack spacing={1}>
                         <Typography variant="body2"><strong>Name:</strong> {newBot.name || 'Untitled'}</Typography>
                        <Typography variant="body2"><strong>Pair:</strong> {newBot.symbol || '(from webhook)'}</Typography>
                        <Typography variant="body2">
                          <strong>Market:</strong>{' '}
                          {isStockEquity
                            ? `Stock Equity${newBot.extendedHours ? ' (Extended Hours)' : ''}${newBot.allowShorts ? ' • Long+Short' : ''}`
                            : (newBot.marketType === 'futures' ? `Futures ${newBot.leverage}x` : 'Spot')}
                        </Typography>
                        <Typography variant="body2"><strong>Signal:</strong> {newBot.signalSource === 'tradingview' ? 'TradingView Webhook' : 'Internal Logic'}</Typography>

                        {newBot.signalSource !== 'tradingview' && (
                          <>
                            <Typography variant="body2">
                              <strong>{newBot.marketType === 'futures' && newBot.positionSide === 'hedge' ? 'Open Long:' : 'Buy Logic:'}</strong>{' '}
                              {buildLogicFromConditions(buyConditions) || 'None'}
                            </Typography>
                            <Typography variant="body2">
                              <strong>{newBot.marketType === 'futures' && newBot.positionSide === 'hedge' ? 'Close Long:' : 'Sell Logic:'}</strong>{' '}
                              {buildLogicFromConditions(sellConditions) || 'None'}
                            </Typography>
                            {((newBot.marketType === 'futures' && newBot.positionSide === 'hedge') ||
                              (isStockEquity && newBot.allowShorts)) && (
                              <>
                                <Typography variant="body2"><strong>Open Short:</strong> {buildLogicFromConditions(shortOpenConditions) || 'None'}</Typography>
                                <Typography variant="body2"><strong>Close Short:</strong> {buildLogicFromConditions(shortCloseConditions) || 'None'}</Typography>
                              </>
                            )}
                          </>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* Webhook Configuration Section - Only for TradingView bots */}



              {/* Phase B — PDT warning for stock_equity, only on the final step */}
              {activeStep === steps.length - 1 && (
                <ShortEligibilityWarning
                  assetClass={assetClass}
                  allowShorts={newBot.allowShorts}
                  accountStatus={accountStatusForSelected}
                />
              )}


              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  onClick={activeStep === 0 ? handleCancelCreate : handleBack}
                  variant="outlined"
                >
                  {activeStep === 0 ? 'Cancel' : 'Back'}
                </Button>
                <Box>
                  {activeStep < steps.length - 1 && (
                    <Button
                      onClick={handleNext}
                      variant="contained"
                      disableElevation
                      sx={{ ml: 1, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'linear-gradient(180deg,#436686,#395a78)' } }}
                    >
                      Next
                    </Button>
                  )}
                  {activeStep === steps.length - 1 && (
                    <>
                      <Button
                        onClick={() => handleSave(true)}
                        variant="outlined"
                        sx={{ ml: 1, borderColor: 'grey.600', color: 'grey.300', textTransform: 'none', '&:hover': { borderColor: 'grey.500', bgcolor: 'rgba(255,255,255,0.05)' } }}
                      >
                        Save (Paused)
                      </Button>
                      {botReadiness.ready ? (
                        <Button
                          onClick={() => handleSave(false)}
                          variant="contained"
                          disableElevation
                          sx={{ ml: 1, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'linear-gradient(180deg,#436686,#395a78)' } }}
                        >
                          Save &amp; Activate
                        </Button>
                      ) : (
                        <Tooltip title={`Next: ${botReadiness.firstMissing?.label || 'finish the remaining steps'}`} arrow>
                          <Button
                            onClick={() => botReadiness.firstMissing && setActiveStep(botReadiness.firstMissing.step)}
                            variant="outlined"
                            sx={{ ml: 1, textTransform: 'none', color: AT.muted, borderColor: 'rgba(255,255,255,.14)' }}
                          >
                            Finish setup to activate
                          </Button>
                        </Tooltip>
                      )}
                    </>
                  )}
                </Box>
              </Box>
                </Box>
                <Box sx={{ position: { md: 'sticky' }, top: 16, alignSelf: 'flex-start' }}>
                  <StrategyRail
                    readiness={botReadiness}
                    newBot={newBot}
                    buyConditions={buyConditions}
                    sellConditions={sellConditions}
                    profitStop={profitStop}
                    lossStop={lossStop}
                    exchangeLabel={(() => { const e = userExchanges.find((x) => x.id === newBot.exchangeId); return e ? (e.name || e.exchange) : ''; })()}
                    onJump={(step) => setActiveStep(step)}
                  />
                </Box>
              </Box>
        )}

        {activeTab === 'bots' && (
          <Box>
            {!isCreating && (
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  disableElevation
                  data-tour="create-bot"
                  startIcon={<Bolt />}
                  onClick={handleQuickBot}
                  sx={{ color: '#06121f', bgcolor: AT.accent, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#5a9bd0' } }}
                >
                  Quick Bot
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  onClick={handleCreate}
                  sx={{ textTransform: 'none' }}
                >
                  Create New Bot
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<History />}
                  onClick={() => setActiveTab('backtest')}
                  sx={{ textTransform: 'none' }}
                >
                  Backtesting
                </Button>
              </Stack>
            )}

            {bots.length === 0 ? (
              <Paper elevation={0} sx={{
                p: 4,
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 2
              }}>
                <Typography variant="h6" gutterBottom>No trading bots created yet</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Launch a ready-made strategy with Quick Bot, or build your own from scratch
                </Typography>
                <Stack direction="row" spacing={1.5} justifyContent="center">
                  <Button
                    variant="contained"
                    disableElevation
                    data-tour="create-bot"
                    startIcon={<Bolt />}
                    onClick={handleQuickBot}
                    sx={{ color: '#06121f', bgcolor: AT.accent, fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#5a9bd0' } }}
                  >
                    Quick Bot
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutline />}
                    onClick={handleCreate}
                    sx={{ textTransform: 'none' }}
                  >
                    Build manually
                  </Button>
                </Stack>
              </Paper>

            ) : (
              <>
                {/* Manual / Scanner sub-tabs (handoff §5) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '26px', borderBottom: `1px solid rgba(255,255,255,.07)`, mb: 2.5, flexWrap: 'wrap' }}>
                  {(() => {
                    const onManual = botListTab === 'manual';
                    const onScanner = botListTab === 'scanner';
                    const scN = groupedBots.scannerProfiles.length;
                    return (
                      <>
                        <Box onClick={() => setBotListTab('manual')} sx={{ display: 'flex', alignItems: 'center', gap: '9px', px: '2px', pb: '12px', cursor: 'pointer', fontSize: '14.5px', fontWeight: 600, color: onManual ? '#fff' : AT.body, borderBottom: `2px solid ${onManual ? AT.accent : 'transparent'}`, mb: '-1px' }}>
                          Manual Bots
                          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '12px', fontWeight: 600, color: '#cdd6e8', background: 'rgba(255,255,255,.08)', px: '9px', py: '2px', borderRadius: '999px' }}>{groupedBots.manual.length}</Box>
                        </Box>
                        <Box onClick={() => setBotListTab('scanner')} sx={{ display: 'flex', alignItems: 'center', gap: '9px', px: '2px', pb: '12px', cursor: 'pointer', fontSize: '14.5px', fontWeight: 600, color: onScanner ? '#fff' : AT.body, borderBottom: `2px solid ${onScanner ? AT.accent : 'transparent'}`, mb: '-1px' }}>
                          Scanner Bots
                          <Box component="span" sx={{ fontFamily: AT_MONO, fontSize: '11.5px', fontWeight: 600, color: '#9cc3e4', background: 'rgba(77,143,199,.14)', border: '1px solid rgba(77,143,199,.3)', px: '9px', py: '2px', borderRadius: '999px' }}>{scN} {scN === 1 ? 'Profile' : 'Profiles'}</Box>
                        </Box>
                      </>
                    );
                  })()}
                </Box>

                {/* Manual Bots tab */}
                {botListTab === 'manual' && (
                  <Stack spacing={2}>
                    {groupedBots.manual.length === 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{ p: 4, textAlign: 'center', borderColor: 'rgba(255,255,255,0.06)', bgcolor: '#0f1218' }}
                      >
                        <Settings sx={{ fontSize: 48, color: 'rgba(255,255,255,0.1)', mb: 1 }} />
                        <Typography variant="body1" sx={{ color: '#e8edf7' }}>
                          No manual bots yet
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9aa6bd' }}>
                          Manually-created bots will appear here. Bots spawned by scanners are in the Scanner Bots tab.
                        </Typography>
                      </Paper>
                    ) : (
                      groupedBots.manual.map(bot => renderBotCard(bot))
                    )}
                  </Stack>
                )}

                {/* Scanner Bots tab */}
                {botListTab === 'scanner' && (
                  <Stack spacing={2}>
                    {groupedBots.scannerProfiles.length === 0 && groupedBots.orphanedSpawned.length === 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{ p: 4, textAlign: 'center', borderColor: 'rgba(255,255,255,0.06)', bgcolor: '#0f1218' }}
                      >
                        <ShowChart sx={{ fontSize: 48, color: 'rgba(255,255,255,0.1)', mb: 1 }} />
                        <Typography variant="body1" sx={{ color: '#e8edf7' }}>
                          No scanner profiles yet
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9aa6bd', display: 'block', mb: 2 }}>
                          Create a scanner profile to automatically spawn bots when conditions are met.
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.location.href = '/scanner'}
                          sx={{ borderColor: 'rgba(255,255,255,0.1)', color: '#e8edf7' }}
                        >
                          Open Scanner
                        </Button>
                      </Paper>
                    ) : (
                      <>
                        {groupedBots.scannerProfiles.map(profile =>
                          renderScannerProfileGroup(profile, groupedBots.spawnedByProfile.get(profile.id) || [])
                        )}

                        {groupedBots.orphanedSpawned.length > 0 && (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderColor: 'rgba(255,255,255,0.1)',
                              bgcolor: '#0f1218',
                              borderStyle: 'dashed',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                              <WarningAmber sx={{ color: '#d9a441', fontSize: 20 }} />
                              <Typography variant="subtitle2" sx={{ color: '#d9a441' }}>
                                Orphaned spawned bots ({groupedBots.orphanedSpawned.length})
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#9aa6bd', ml: 1 }}>
                                Parent scanner profile was deleted
                              </Typography>
                            </Box>
                            <Stack spacing={1.5}>
                              {groupedBots.orphanedSpawned.map(bot => renderBotCard(bot))}
                            </Stack>
                          </Paper>
                        )}
                      </>
                    )}
                  </Stack>
                )}
              </>
            )}
          </Box>
        )}

        {activeTab === 'backtest' && (
          <Box>
            <Paper elevation={0} sx={{ p: '20px 22px', mb: 2, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', bgcolor: AT.card }}>
              {(() => {
                const { quote: BT_QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
                const btDays = Math.max(1, Math.round((backtestDateRange[1] - backtestDateRange[0]) / MS_DAY));
                const btCaption = `${format(new Date(backtestDateRange[0]), 'MMM d, yyyy')} – ${format(new Date(backtestDateRange[1]), 'MMM d, yyyy')} · ${btDays} days`;
                const check = selectedBotForBacktest ? gateBacktest(selectedBotForBacktest) : { supported: true, unsupported: [] };
                const disabled = !selectedBotForBacktest || isBacktesting || !check.supported;
                const labelSx = { display: 'block', fontFamily: AT_MONO, fontSize: '10px', letterSpacing: '.1em', color: AT.muted2, mb: '7px' };
                const inputSx = { width: '100%', fontFamily: AT_MONO, fontSize: '15px', color: AT.bright, background: AT.inner, border: '1px solid rgba(255,255,255,.1)', borderRadius: '9px', px: '15px', py: '12px', outline: 'none', '&:focus': { borderColor: 'rgba(77,143,199,.5)' } };
                return (
                  <>
                    {/* compact config bar (§7a) */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '18px', flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Box component="label" sx={labelSx}>BACKTEST</Box>
                        <Box component="select"
                          value={selectedBotForBacktest?.id || ''}
                          onChange={(e) => setSelectedBotForBacktest(bots.find(b => String(b.id) === String(e.target.value)))}
                          sx={{ ...inputSx, fontFamily: AT_SANS, fontWeight: 600, cursor: 'pointer', appearance: 'none' }}>
                          <option value="" disabled>Select a bot…</option>
                          {bots.filter(b => b.signalSource !== 'tradingview').map(b => <option key={b.id} value={b.id}>{b.name} ({b.symbol})</option>)}
                        </Box>
                      </Box>
                      <Box>
                        <Box component="label" sx={labelSx}>PERIOD</Box>
                        <Box sx={{ display: 'flex', gap: '6px' }}>
                          {['1M', '3M', '6M', '1Y', 'YTD'].map((k) => {
                            const on = btRange === k;
                            return (
                              <Box key={k} onClick={() => {
                                setBtRange(k);
                                const end = endOfTodayMs();
                                const start = k === 'YTD' ? new Date(new Date(end).getFullYear(), 0, 1).getTime() : monthsAgoMs({ '1M': 1, '3M': 3, '6M': 6, '1Y': 12 }[k]);
                                setBacktestDateRange([Math.max(rangeBounds.min, start), Math.min(rangeBounds.max, end)]);
                              }} sx={{ fontFamily: AT_MONO, fontSize: '12px', fontWeight: 600, px: '13px', py: '9px', borderRadius: '8px', cursor: 'pointer', transition: 'all .15s ease', color: on ? AT.accentText : AT.body, background: on ? 'rgba(77,143,199,.12)' : AT.inner, border: `1px solid ${on ? 'rgba(77,143,199,.38)' : 'rgba(255,255,255,.1)'}` }}>{k}</Box>
                            );
                          })}
                        </Box>
                      </Box>
                      <Box sx={{ width: 150 }}>
                        <Box component="label" sx={labelSx}>CAPITAL ({BT_QUOTE || 'USD'})</Box>
                        <Box component="input" type="number" value={backtestParams.initialCapital}
                          onChange={(e) => setBacktestParams((p) => ({ ...p, initialCapital: Number(e.target.value) || 0 }))}
                          sx={inputSx} />
                      </Box>
                      <Tooltip title={!selectedBotForBacktest ? 'Select a bot first' : (check.supported ? '' : check.message)} arrow>
                        <Box component="span" sx={{ display: 'inline-block' }}>
                          <Box component="button" disabled={disabled}
                            onClick={() => selectedBotForBacktest && runBacktest(selectedBotForBacktest)}
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: '9px', fontFamily: AT_SANS, fontSize: '14px', fontWeight: 600, color: '#e7eef6', background: disabled ? 'rgba(255,255,255,.08)' : 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', px: '26px', py: '13px', borderRadius: '9px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, transition: 'transform .2s', '&:hover': disabled ? {} : { transform: 'translateY(-1px)' } }}>
                            <PlayArrow sx={{ fontSize: 17 }} /> {isBacktesting ? 'Running…' : 'Run backtest'}
                          </Box>
                        </Box>
                      </Tooltip>
                    </Box>

                    {/* range caption + Advanced toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mt: '14px', flexWrap: 'wrap' }}>
                      <Mono sx={{ fontSize: '11.5px', color: AT.muted }}>{btCaption}</Mono>
                      <Box onClick={() => setBtAdvOpen((o) => !o)} sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', fontWeight: 600, color: '#9cc3e4', cursor: 'pointer' }}>
                        <Settings sx={{ fontSize: 14 }} />{btAdvOpen ? 'Hide advanced' : 'Advanced'}
                      </Box>
                    </Box>

                    {/* advanced params (commission / slippage / lookback) */}
                    <Collapse in={btAdvOpen}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1.4fr' }, gap: '16px', mt: '16px', pt: '16px', borderTop: '1px solid rgba(255,255,255,.06)' }}>
                        <Box>
                          <Box component="label" sx={labelSx}>COMMISSION (%)</Box>
                          <Box component="input" type="number" value={backtestParams.commission}
                            onChange={(e) => setBacktestParams((p) => ({ ...p, commission: Number(e.target.value) || 0 }))}
                            sx={inputSx} />
                        </Box>
                        <Box>
                          <Box component="label" sx={labelSx}>SLIPPAGE (%)</Box>
                          <Box component="input" type="number" step="0.001" min="0" value={backtestParams.slippage}
                            onChange={(e) => setBacktestParams((p) => ({ ...p, slippage: Number(e.target.value) || 0 }))}
                            sx={inputSx} />
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '9px', fontFamily: AT_MONO, fontSize: '10px', letterSpacing: '.1em', color: AT.muted2 }}>
                            <span>LOOKBACK WINDOW</span><span style={{ color: '#cdd6e8' }}>{btDays} days</span>
                          </Box>
                          <Box component="input" type="range" min="7" max="365"
                            value={Math.min(365, Math.max(7, btDays))}
                            onChange={(e) => { const days = Number(e.target.value); const end = backtestDateRange[1]; setBtRange(''); setBacktestDateRange([Math.max(rangeBounds.min, end - days * MS_DAY), end]); }}
                            sx={{ width: '100%', accentColor: AT.accent, cursor: 'pointer', mt: '8px' }} />
                        </Box>
                      </Box>
                    </Collapse>

                    {selectedBotForBacktest && !check.supported && <UnsupportedAlert check={check} />}
                  </>
                );
              })()}
            </Paper>


            {/* §7b — Live Backtest Stream (idle / running / complete in place) */}
            {(() => {
              const ls = liveBacktest?.status;
              const running = isBacktesting || ls === 'queued' || ls === 'running';
              const stopped = ls === 'canceled';
              const complete = !!backtestResults && !running;
              const btStatus = running ? 'running' : complete ? 'complete' : stopped ? 'stopped' : 'idle';
              const stMap = { idle: { label: 'IDLE', c: AT.muted }, running: { label: 'RUNNING', c: AT.accent }, complete: { label: 'COMPLETE', c: AT.positive }, stopped: { label: 'STOPPED', c: AT.negative } };
              const st = stMap[btStatus];
              const { quote: QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
              return (
                <Paper elevation={0} sx={{ p: '24px', mt: 2, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', bgcolor: AT.card }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 2.25, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: '8px', background: 'rgba(77,143,199,.14)', border: '1px solid rgba(77,143,199,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BarChart sx={{ color: '#7fb2dc', fontSize: 16 }} /></Box>
                      <Typography sx={{ fontSize: '15px', fontWeight: 700, color: AT.bright }}>Live Backtest Stream</Typography>
                    </Box>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: AT_MONO, fontSize: '11px', letterSpacing: '.06em', fontWeight: 600, color: st.c, background: `${st.c}1f`, border: `1px solid ${st.c}59`, px: '12px', py: '5px', borderRadius: '7px' }}>
                      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: st.c }} />{st.label}
                    </Box>
                  </Box>

                  {btStatus === 'idle' || btStatus === 'stopped' ? (
                    <Box sx={{ textAlign: 'center', color: AT.muted, fontSize: '13.5px', p: '30px 20px', border: '1px dashed rgba(255,255,255,.1)', borderRadius: '10px' }}>
                      Configure parameters above and run a backtest to stream live progress, equity and trades.
                    </Box>
                  ) : running ? (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '9px', fontSize: '12.5px', color: AT.body }}>
                        <span>Progress</span><Mono sx={{ color: '#cdd6e8' }}>{ls === 'queued' ? '—' : `${Math.round(backtestProgress)}%`}</Mono>
                      </Box>
                      <Box sx={{ height: 8, borderRadius: '5px', background: 'rgba(255,255,255,.06)', overflow: 'hidden' }}>
                        <Box sx={{ width: ls === 'queued' ? '12%' : `${Math.round(backtestProgress)}%`, height: '100%', borderRadius: '5px', background: 'linear-gradient(90deg,#3f6fb0,#4d8fc7)', transition: 'width .3s ease' }} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.75, mt: 1.75, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '13px', color: '#8593ad' }}>{ls === 'queued' ? 'Waiting for an available worker…' : 'Replaying strategy over historical bars…'}</Typography>
                        <Box component="button" onClick={() => setConfirmCancelOpen(true)} disabled={isCanceling}
                          sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: AT.negative, background: 'rgba(217,123,114,.08)', border: '1px solid rgba(217,123,114,.35)', px: '16px', py: '9px', borderRadius: '8px', cursor: isCanceling ? 'default' : 'pointer' }}>
                          <Cancel sx={{ fontSize: 14 }} /> {isCanceling ? 'Stopping…' : 'Stop'}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    (() => {
                      const m = backtestResults.metrics || {};
                      const ret = isNum(m.returnPct) ? Number(m.returnPct) : (parseFloat(String(m.totalReturn || '').replace('%', '')) || 0);
                      const benchPct = isNum(m.benchmark?.returnPct) ? Number(m.benchmark.returnPct) : 0;
                      const alpha = ret - benchPct;
                      const up = alpha >= 0;
                      const vC = up ? AT.positive : AT.negative;
                      const cap = Number(backtestParams.initialCapital) || 0;
                      const finalEq = isNum(m.finalEquity) ? Number(m.finalEquity) : cap * (1 + ret / 100);
                      const maxDD = isNum(m.maxDrawdown) ? Number(m.maxDrawdown) : (isNum(m.maxDrawdownPct) ? Number(m.maxDrawdownPct) : null);
                      const verdictLabel = up ? (Number(m.sharpeRatio) >= 1 ? 'STRONG' : 'MODERATE') : 'WEAK';
                      const recText = up ? 'Beat buy & hold over this period — a candidate to deploy.' : 'Trailed buy & hold over this period — refine before deploying.';
                      const kpis = [
                        { k: 'FINAL EQUITY', v: atShortUsd(finalEq), c: AT.bright },
                        { k: 'SHARPE', v: fmtRatio(m.sharpeRatio), c: AT.bright },
                        { k: 'MAX DRAWDOWN', v: maxDD != null ? `${Number(maxDD).toFixed(2)}%` : '—', c: AT.negative },
                        { k: 'WIN RATE', v: isNum(m.winRate) ? `${Number(m.winRate).toFixed(1)}%` : '—', c: AT.bright },
                        { k: 'TRADES', v: isNum(m.totalTrades) ? String(m.totalTrades) : (Array.isArray(backtestResults.trades) ? String(backtestResults.trades.length) : '—'), c: AT.bright },
                        { k: 'ALPHA', v: `${alpha >= 0 ? '+' : ''}${alpha.toFixed(2)}%`, c: alpha >= 0 ? AT.positive : AT.negative },
                      ];
                      const eqVals = (Array.isArray(backtestResults.equityCurve) ? backtestResults.equityCurve : []).map((p) => Array.isArray(p) ? Number(p[1]) : Number(p?.v ?? p?.equity ?? p?.value)).filter(Number.isFinite);
                      const stratVals = eqVals.length >= 2 ? eqVals : [cap, finalEq];
                      const bStart = stratVals[0], bEnd = bStart * (1 + benchPct / 100);
                      const benchVals = stratVals.map((_, i) => bStart + (bEnd - bStart) * (i / (stratVals.length - 1 || 1)));
                      const lo = Math.min(...stratVals, ...benchVals), hi = Math.max(...stratVals, ...benchVals);
                      const sp = atPathScaled(stratVals, lo, hi, 720, 150);
                      const bp = atPathScaled(benchVals, lo, hi, 720, 150);
                      const trades = (Array.isArray(backtestResults.trades) ? backtestResults.trades : []).slice(-5).reverse();
                      return (
                        <Box>
                          {/* hero verdict card (§7b) */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '22px', mb: 2.25, p: '20px 22px', background: `${vC}1a`, border: '1px solid rgba(255,255,255,.08)', borderRadius: '12px', flexWrap: 'wrap' }}>
                            <Box sx={{ flex: 'none' }}>
                              <Mono sx={{ fontSize: '10px', letterSpacing: '.12em', color: AT.muted2, mb: '6px', display: 'block' }}>TOTAL RETURN</Mono>
                              <Mono sx={{ fontSize: '38px', fontWeight: 600, lineHeight: 1, color: ret >= 0 ? AT.positive : AT.negative }}>{ret >= 0 ? '+' : ''}{ret.toFixed(2)}%</Mono>
                              <Mono sx={{ fontSize: '11.5px', color: '#8593ad', mt: '7px', display: 'block' }}>{atShortUsd(cap)} → {atShortUsd(finalEq)}</Mono>
                            </Box>
                            <Box sx={{ width: '1px', alignSelf: 'stretch', background: 'rgba(255,255,255,.08)' }} />
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                              <Box component="span" sx={{ display: 'inline-flex', fontFamily: AT_MONO, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.08em', color: '#06121f', background: vC, px: '11px', py: '5px', borderRadius: '6px', mb: '10px' }}>{verdictLabel}</Box>
                              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#cdd6e8', lineHeight: 1.5 }}>{recText}</Typography>
                              <Typography sx={{ fontSize: '12.5px', color: '#8593ad', mt: '6px' }}>
                                {ret >= 0 ? '+' : ''}{ret.toFixed(2)}% vs {benchPct >= 0 ? '+' : ''}{benchPct.toFixed(2)}% buy &amp; hold — {alpha >= 0 ? '+' : ''}{alpha.toFixed(2)}% alpha at {fmtRatio(m.sharpeRatio)} Sharpe.
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px', flex: 'none' }}>
                              <Box component="button" onClick={() => setActiveTab('bots')} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontFamily: AT_SANS, fontSize: '13.5px', fontWeight: 600, color: '#e7eef6', background: 'linear-gradient(180deg,#3d5c7b,#33506b)', border: 'none', px: '22px', py: '12px', borderRadius: '9px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'transform .2s', '&:hover': { transform: 'translateY(-1px)' } }}>
                                <Box component="svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></Box> Deploy as live bot
                              </Box>
                              <Box component="button" onClick={() => { setActiveBacktestTab('summary'); setBacktestDialogOpen(true); }} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px', fontFamily: AT_SANS, fontSize: '13px', fontWeight: 600, color: '#9cc3e4', background: 'transparent', border: '1px solid rgba(77,143,199,.3)', px: '22px', py: '11px', borderRadius: '9px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                View full report
                              </Box>
                            </Box>
                          </Box>

                          {/* equity curve */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.25, flexWrap: 'wrap' }}>
                            <Mono sx={{ fontSize: '11px', letterSpacing: '.08em', color: AT.muted2 }}>EQUITY CURVE</Mono>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: '#8593ad' }}><Box component="span" sx={{ width: 14, height: '2px', bgcolor: AT.accent }} />Strategy</Box>
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11.5px', color: '#8593ad' }}><Box component="span" sx={{ width: 14, borderTop: `2px dashed ${AT.muted}` }} />Buy &amp; hold</Box>
                          </Box>
                          <Box sx={{ color: AT.accent, mb: 2.5 }}>
                            <svg viewBox="0 0 720 150" preserveAspectRatio="none" style={{ width: '100%', height: 218, display: 'block' }}>
                              <defs><linearGradient id="bt-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="currentColor" stopOpacity="0.2" /><stop offset="1" stopColor="currentColor" stopOpacity="0" /></linearGradient></defs>
                              <g stroke="rgba(255,255,255,.05)" strokeWidth="1"><line x1="0" y1="8" x2="720" y2="8" /><line x1="0" y1="75" x2="720" y2="75" /><line x1="0" y1="142" x2="720" y2="142" /></g>
                              <path d={sp.area} fill="url(#bt-area)" />
                              <path d={bp.line} fill="none" stroke={AT.muted} strokeWidth="1.5" strokeDasharray="5 5" strokeLinecap="round" strokeLinejoin="round" />
                              <path d={sp.line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <circle cx={sp.lastX} cy={sp.lastY} r="3.5" fill="currentColor" />
                            </svg>
                          </Box>

                          {/* metrics */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(6,1fr)' }, gap: '10px', mb: 2.25 }}>
                            {kpis.map((kp) => (
                              <Box key={kp.k} sx={{ background: AT.inner, border: '1px solid rgba(255,255,255,.07)', borderRadius: '10px', p: '14px' }}>
                                <Mono sx={{ fontSize: '9.5px', letterSpacing: '.08em', color: AT.muted2, mb: '9px', display: 'block' }}>{kp.k}</Mono>
                                <Mono sx={{ fontSize: '17px', fontWeight: 600, color: kp.c }}>{kp.v}</Mono>
                              </Box>
                            ))}
                          </Box>

                          <Mono sx={{ fontSize: '11px', letterSpacing: '.08em', color: AT.muted2, mb: '6px', display: 'block' }}>RECENT TRADES</Mono>
                          <Box sx={{ overflowX: 'auto' }}>
                            <Box sx={{ minWidth: 520 }}>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 1fr 1fr 1fr', gap: '12px', p: '8px 4px', borderBottom: '1px solid rgba(255,255,255,.07)', fontFamily: AT_MONO, fontSize: '10.5px', letterSpacing: '.08em', color: AT.muted }}>
                                <span>TIME</span><span>SIDE</span><span style={{ textAlign: 'right' }}>PRICE</span><span style={{ textAlign: 'right' }}>QTY</span><span style={{ textAlign: 'right' }}>P&amp;L</span>
                              </Box>
                              {trades.length === 0 && <Typography sx={{ fontSize: '12.5px', color: AT.muted, py: 1.5 }}>No trades.</Typography>}
                              {trades.map((tr, i) => {
                                const side = String(tr.side || tr.type || '').toUpperCase();
                                const sc = side === 'BUY' ? AT.positive : side === 'SELL' ? AT.negative : AT.accent;
                                const pnl = Number(tr.pnl ?? tr.profit);
                                return (
                                  <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 1fr 1fr 1fr', gap: '12px', alignItems: 'center', p: '11px 4px', borderBottom: '1px solid rgba(255,255,255,.04)', fontFamily: AT_MONO, fontVariantNumeric: 'tabular-nums', fontSize: '12.5px' }}>
                                    <Box component="span" sx={{ color: AT.body }}>{tr.time || tr.timestamp || tr.date ? new Date(tr.time || tr.timestamp || tr.date).toLocaleString() : '—'}</Box>
                                    <Box component="span"><Box component="span" sx={{ display: 'inline-flex', fontSize: '11px', fontWeight: 700, color: sc, background: `${sc}1f`, px: '10px', py: '3px', borderRadius: '6px' }}>{side || '—'}</Box></Box>
                                    <Box component="span" sx={{ color: '#c9d2e3', textAlign: 'right' }}>{isNum(tr.price) ? Number(tr.price).toLocaleString() : '—'}</Box>
                                    <Box component="span" sx={{ color: AT.body, textAlign: 'right' }}>{isNum(tr.qty ?? tr.amount) ? Number(tr.qty ?? tr.amount).toLocaleString(undefined, { maximumFractionDigits: 6 }) : '—'}</Box>
                                    <Box component="span" sx={{ color: Number.isFinite(pnl) ? (pnl >= 0 ? AT.positive : AT.negative) : AT.body, fontWeight: 600, textAlign: 'right' }}>{Number.isFinite(pnl) ? `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)} ${QUOTE || ''}` : '—'}</Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })()
                  )}
                </Paper>
              );
            })()}


            {/* §7c — Recent Backtests */}
            <Paper elevation={0} sx={{ p: '24px', mt: 2, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', bgcolor: AT.card }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.25 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', background: 'rgba(77,143,199,.14)', border: '1px solid rgba(77,143,199,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><History sx={{ color: '#7fb2dc', fontSize: 16 }} /></Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: AT.bright }}>Recent Backtests</Typography>
                <Mono sx={{ fontSize: '12px', color: AT.muted }}>last 10</Mono>
              </Box>
              {recentBacktests.length === 0 ? (
                <Typography sx={{ fontSize: '13.5px', color: AT.muted, py: 1.5 }}>No saved backtests yet.</Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box sx={{ minWidth: 760 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1.4fr 1fr 0.9fr 0.9fr 0.9fr 0.9fr', gap: '12px', p: '0 4px 11px', borderBottom: '1px solid rgba(255,255,255,.07)', fontFamily: AT_MONO, fontSize: '10.5px', letterSpacing: '.08em', color: AT.muted }}>
                      <span>BOT</span><span>PERIOD</span><span style={{ textAlign: 'right' }}>RETURN</span><span style={{ textAlign: 'right' }}>SHARPE</span><span style={{ textAlign: 'right' }}>WIN</span><span style={{ textAlign: 'right' }}>MAX DD</span><span style={{ textAlign: 'right' }}>DATE</span>
                    </Box>
                    {recentBacktests.map((bt) => {
                      const m = bt.metrics || {};
                      const botName = bt.bot_name || botsById[bt.bot_id] || (bt.bot_id != null ? `Bot #${bt.bot_id}` : 'Unknown Bot');
                      const ret = isNum(m.returnPct) ? Number(m.returnPct) : parseFloat(String(m.totalReturn || '').replace('%', ''));
                      const retStr = Number.isFinite(ret) ? `${ret >= 0 ? '+' : ''}${ret.toFixed(2)}%` : (m.totalReturn ?? '—');
                      const retC = Number.isFinite(ret) ? (ret >= 0 ? AT.positive : AT.negative) : AT.body;
                      const dd = isNum(m.maxDrawdown) ? `${Number(m.maxDrawdown).toFixed(1)}%` : (isNum(m.maxDrawdownPct) ? `${Number(m.maxDrawdownPct).toFixed(1)}%` : '—');
                      const period = [bt.symbol, bt.timeframe].filter(Boolean).join(' · ') || '—';
                      return (
                        <Box key={bt.id} onClick={() => openSavedBacktest(bt)} sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1.4fr 1fr 0.9fr 0.9fr 0.9fr 0.9fr', gap: '12px', alignItems: 'center', p: '13px 4px', borderBottom: '1px solid rgba(255,255,255,.04)', cursor: 'pointer', transition: 'background .15s', '&:hover': { background: 'rgba(255,255,255,.03)' } }}>
                          <Box component="span" sx={{ fontSize: '13.5px', fontWeight: 600, color: AT.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{botName}</Box>
                          <Mono sx={{ fontSize: '12.5px', color: AT.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{period}</Mono>
                          <Mono sx={{ fontSize: '13px', fontWeight: 600, color: retC, textAlign: 'right' }}>{retStr}</Mono>
                          <Mono sx={{ fontSize: '13px', color: '#c9d2e3', textAlign: 'right' }}>{m.sharpeRatio != null ? fmtRatio(m.sharpeRatio) : '—'}</Mono>
                          <Mono sx={{ fontSize: '13px', color: '#c9d2e3', textAlign: 'right' }}>{isNum(m.winRate) ? `${Number(m.winRate).toFixed(0)}%` : '—'}</Mono>
                          <Mono sx={{ fontSize: '13px', color: '#c9d2e3', textAlign: 'right' }}>{dd}</Mono>
                          <Mono sx={{ fontSize: '12.5px', color: '#8593ad', textAlign: 'right' }}>{new Date(bt.created_at || bt.createdAt).toLocaleDateString()}</Mono>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>

            {bots.length === 0 && (
              <Paper elevation={0} sx={{
                p: 4,
                textAlign: 'center',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 2
              }}>
                <Typography variant="h6" gutterBottom>No trading bots available</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Create a trading bot first to run backtests
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setActiveTab('create')}
                  sx={{
                    bgcolor: 'grey.800',
                    '&:hover': { bgcolor: 'grey.700' }
                  }}
                >
                  Create New Bot
                </Button>
              </Paper>
            )}
          </Box>
        )}

        {activeTab === 'advisor' && (
          <AITradingAdvisor
            bots={bots}
            botHistories={liveBotHistories}
            futuresInfo={futuresInfo}
          />
        )}


        <Dialog
          open={confirmDeleteOpen}
          onClose={() => !deleting && setConfirmDeleteOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmber color="warning" />
            Confirm delete
          </DialogTitle>

          <DialogContent dividers>
            <Typography gutterBottom>
              Delete this saved backtest permanently?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {viewingSavedBacktestId}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={handleDeleteSavedBacktest}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(confirmDeleteBotId)}
          onClose={() => !isDeletingBot && setConfirmDeleteBotId(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>
              Please confirm to delete the bot.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteBotId(null)} disabled={isDeletingBot}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={async () => {
                if (!confirmDeleteBotId) return;
                try {
                  setIsDeletingBot(true);
                  await tradingBotService.delete(confirmDeleteBotId);
                  setBots(prev => prev.filter(b => b.id !== confirmDeleteBotId));
                  setConfirmDeleteBotId(null);
                } catch (err) {
                  console.error('Failed to delete bot:', err);
                } finally {
                  setIsDeletingBot(false);
                }
              }}
              disabled={isDeletingBot}
            >
              {isDeletingBot ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Soft-warning dialog: server returned 409 because the user already
            has an active bot on this exchange + symbol. Clicking "Create
            Anyway" resubmits with confirm_duplicate=true to bypass the check. */}
        <Dialog
          open={!!duplicateBotConflict}
          onClose={() => setDuplicateBotConflict(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmber color="warning" />
            Duplicate Bot Warning
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You already have a bot on{' '}
              <strong>{duplicateBotConflict?.conflict?.symbol}</strong> on this exchange
              {duplicateBotConflict?.conflict?.bot_name && (
                <> — <em>"{duplicateBotConflict.conflict.bot_name}"</em></>
              )}
              {' '}(bot #{duplicateBotConflict?.conflict?.bot_id}).
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Two bots on the same symbol will compete for fills and independently
              size positions. If their entry/exit logic differs, they can fight
              each other — one buying while the other sells.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Create another bot on this symbol anyway?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDuplicateBotConflict(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<AddCircleOutline />}
              onClick={async () => {
                const conflict = duplicateBotConflict;
                if (!conflict) return;
                setDuplicateBotConflict(null);
                const selectedExchange = userExchanges.find(ex => ex.id === newBot.exchangeId);
                await submitBotPayloadDirect(conflict.pendingPayload, conflict.stopped, selectedExchange);
              }}
            >
              Create Anyway
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={confirmCancelOpen}
          onClose={() => !isCanceling && setConfirmCancelOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmber color="warning" />
            Cancel backtest?
          </DialogTitle>

          <DialogContent dividers>
            <Typography gutterBottom>
              This will signal the running backtest to stop. Progress so far may not be saved.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Job: {liveBacktest?.jobId}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setConfirmCancelOpen(false)} disabled={isCanceling}>
              Keep Running
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Cancel />}
              onClick={cancelBacktest}
              disabled={isCanceling}
            >
              {isCanceling ? 'Stopping…' : 'Stop Backtest'}
            </Button>
          </DialogActions>
        </Dialog>


        {/* Backtest Results Dialog */}
        <Dialog
          open={backtestDialogOpen}
          onClose={closeBacktestDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              <BarChart sx={{ mr: 1 }} />
              Backtest Results: {selectedBotForBacktest?.name}
            </Box>
            <Typography variant="subtitle2" color="text.secondary">
              {format(backtestDateRange[0], 'MMM d, yyyy')} - {format(backtestDateRange[1], 'MMM d, yyyy')}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Tabs
              value={activeBacktestTab}
              onChange={(e, newValue) => setActiveBacktestTab(newValue)}
              sx={{ mb: 3 }}
            >
              <Tab label="Summary" value="summary" icon={<AccountBalance />} />
              <Tab label="Equity Curve" value="equity" icon={<Timeline />} />
              <Tab label="Trades" value="trades" icon={<History />} />
            </Tabs>

            {activeBacktestTab === 'summary' && backtestResults && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={isSavedView ? 6 : 12}>
                  <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>

                      <TableBody>
                        {(() => {
                          const m = backtestResults?.metrics || {};
                          const bench = m.benchmark || {};

                          // Prefer numeric % (returnPct). Fallback to any preformatted string you saved.
                          const totalReturn = isNum(m.returnPct) ? fmtPct(m.returnPct) : (m.totalReturn ?? '—');
                          const benchReturn = isNum(bench.returnPct) ? fmtPct(bench.returnPct) : '—';

                          return (
                            <>
                              <TableRow>
                                <TableCell><strong>Total Trades</strong></TableCell>
                                <TableCell align="right">
                                  {isNum(m.totalTrades) ? Number(m.totalTrades) : 0}
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell><strong>Profit / Loss</strong></TableCell>
                                <TableCell align="right">
                                  {(() => {
                                    const { quote: QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
                                    if (!isNum(m.pnl)) return '—';
                                    const n = Number(m.pnl);
                                    const sign = n < 0 ? '-' : '';
                                    return `${sign}${Math.abs(n).toFixed(2)} ${QUOTE || ''}`;
                                  })()}
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell><strong>Sharpe Ratio</strong></TableCell>
                                <TableCell align="right">
                                  {fmtRatio(m.sharpeRatio)}
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell><strong>Total Return</strong></TableCell>
                                <TableCell align="right">
                                  {totalReturn}
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell><strong>Benchmark Return</strong></TableCell>
                                <TableCell align="right">
                                  {benchReturn}
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell><strong>Win Rate</strong></TableCell>
                                <TableCell align="right">
                                  {fmtPct(m.winRate)}
                                </TableCell>
                              </TableRow>
                            </>
                          );
                        })()}
                      </TableBody>

                    </Table>
                  </TableContainer>
                </Grid>

                {/* NEW: Backtest & Strategy Config */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Backtest & Strategy Config</Typography>
                  {(() => {
                    // Prefer saved config first (detail/backtest row), then fall back to current UI state
                    const selected = selectedBotForBacktest || {};
                    const savedCfg = safeParseMaybe(backtestResults?.backtest_config)
                      || safeParseMaybe(backtestResults?.config)
                      || {};
                    const cfg = {
                      symbol: savedCfg.symbol ?? selected.symbol ?? '—',
                      timeframe: (savedCfg.timeframe ?? selected.interval ?? '—')?.toString(),
                      startMs: savedCfg.start_time_ms ?? backtestDateRange?.[0],
                      endMs: savedCfg.end_time_ms ?? backtestDateRange?.[1],
                      initialCapital: (Number(savedCfg.initialCapital ?? backtestParams.initialCapital) || 0),
                      commission: Number(savedCfg.commission ?? backtestParams.commission),
                      slippage: Number(savedCfg.slippage ?? backtestParams.slippage),
                      strategy: savedCfg.strategy ?? { buy: selected.logic?.buy, sell: selected.logic?.sell }
                    };
                    const pct = (v) => Number.isFinite(v) ? `${Number(v).toFixed(2)}%` : '—';
                    const { quote: QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
                    const money = (v) => Number.isFinite(v) ? `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${QUOTE || ''}` : '—';
                    const fmtDate = (ms) => (Number.isFinite(ms) ? new Date(ms).toLocaleString() : '—');

                    return (
                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell><strong>Symbol</strong></TableCell>
                              <TableCell align="right">{cfg.symbol}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Timeframe</strong></TableCell>
                              <TableCell align="right">{(cfg.timeframe || '').toUpperCase()}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Start</strong></TableCell>
                              <TableCell align="right">{fmtDate(cfg.startMs)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>End</strong></TableCell>
                              <TableCell align="right">{fmtDate(cfg.endMs)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Initial Capital</strong></TableCell>
                              <TableCell align="right">{money(cfg.initialCapital)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Commission</strong></TableCell>
                              <TableCell align="right">{pct(cfg.commission)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell><strong>Slippage</strong></TableCell>
                              <TableCell align="right">{pct(cfg.slippage)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Typography variant="caption" color="text.secondary">Buy Logic</Typography>
                                <Paper variant="outlined" sx={{ p: 1, mt: .5, fontFamily: 'monospace', bgcolor: 'background.default' }}>
                                  {cfg.strategy?.buy || '—'}
                                </Paper>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Sell Logic</Typography>
                                <Paper variant="outlined" sx={{ p: 1, mt: .5, fontFamily: 'monospace', bgcolor: 'background.default' }}>
                                  {cfg.strategy?.sell || '—'}
                                </Paper>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </Grid>

              </Grid>
            )}

            {activeBacktestTab === 'equity' && backtestResults && (
              <Box sx={{ height: 420 }}>
                <Typography variant="h6" gutterBottom>Equity Curve</Typography>
                <Paper variant="outlined" sx={{ p: 2, height: 380 }}>
                  {Array.isArray(backtestResults.equityCurve) && backtestResults.equityCurve.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={toChartData(backtestResults.equityCurve)} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="time"
                          type="number"
                          domain={['dataMin', 'dataMax']}
                          tickFormatter={(t) => new Date(t).toLocaleString()}
                          minTickGap={60}
                        />
                        <YAxis
                          dataKey="equity"
                          tickFormatter={(v) => {
                            const { quote: QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
                            return `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${QUOTE || ''}`;
                          }}
                          width={80}
                        />
                        <RechartsTooltip
                          labelFormatter={(t) => new Date(t).toLocaleString()}
                          formatter={(value) => {
                            const { quote: QUOTE } = getPairMeta({ symbol: selectedBotForBacktest?.symbol, exchangeId: selectedBotForBacktest?.exchangeId, userExchanges, symbolsByExchange });
                            return [`${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${QUOTE || ''}`, 'Equity'];
                          }}
                        />
                        <Line type="monotone" dataKey="equity" dot={false} strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography color="text.secondary">No equity data.</Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}

            {activeBacktestTab === 'trades' && backtestResults && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Trade History ({Array.isArray(backtestResults.trades) ? backtestResults.trades.length : 0} rows)
                </Typography>
                {(() => {
                  const displayTrades = normalizeAndPairTrades(backtestResults.trades);
                  return (

                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Side</TableCell>
                            <TableCell>Time</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">PnL</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayTrades.map((t, idx) => {
                            const hasExit = Number.isFinite(t.exitPrice) || Number.isFinite(t.exitTime);
                            const profitChip = (() => {
                              if (t.pnlPct != null) return { text: pctStr(t.pnlPct), isLoss: t.pnlPct < 0 };
                              if (t.pnl != null) return { text: moneyStr(t.pnl), isLoss: t.pnl < 0 };
                              return formatProfitLikeChipInput(t.raw);
                            })();


                            return (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Chip
                                    label={hasExit ? 'BUY → SELL' : (t.side || '—')}
                                    size="small"
                                    color={hasExit ? 'default' : (String(t.side).startsWith('SELL') ? 'error' : 'success')}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  {t.entryTime ? new Date(t.entryTime).toLocaleString() : '—'}
                                  {hasExit && (
                                    <>
                                      <br />
                                      <span style={{ opacity: .7 }}>→ {new Date(t.exitTime).toLocaleString()}</span>
                                    </>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {Number.isFinite(t.entryPrice)
                                    ? t.entryPrice.toLocaleString(undefined, { maximumFractionDigits: 8 })
                                    : '—'}
                                  {hasExit && (
                                    <>
                                      <br />
                                      <span style={{ opacity: .7 }}>
                                        → {Number(t.exitPrice).toLocaleString(undefined, { maximumFractionDigits: 8 })}
                                      </span>
                                    </>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {Number.isFinite(t.amount)
                                    ? t.amount.toLocaleString(undefined, { maximumFractionDigits: 8 })
                                    : '—'}
                                </TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={profitChip.text}
                                    size="small"
                                    color={profitChip.isLoss ? 'error' : 'success'}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                })()}
              </Box>
            )}

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBacktestDialogOpen(false)}>Close</Button>



            {viewingSavedBacktestId && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setConfirmDeleteOpen(true)}
                sx={{ ml: 1 }}
              >
                Delete
              </Button>
            )}

            {!isSavedView && (
              <Button
                variant="outlined"
                startIcon={<CheckCircle />}
                onClick={handleSaveBacktest}
                disabled={!backtestResults || !selectedBotForBacktest}
                sx={{ ml: 1 }}
              >
                Save Backtest
              </Button>
            )}

            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={() => {
                setBacktestDialogOpen(false);
                toggleBotStatus(selectedBotForBacktest.id);
              }}
            >
              Activate This Strategy
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
    </ThemeProvider>
  );
}